import { APP_CONFIG, buildCopyrightText } from "@/config/app-config";

export default {
  common: {
    appName: APP_CONFIG.brand.name,
    appSubtitle: APP_CONFIG.brand.subtitle,
    cancel: "取消",
    confirm: "确认",
    save: "保存",
    delete: "删除",
    edit: "编辑",
    view: "查看",
    viewAll: "查看全部",
    create: "新增",
    search: "搜索",
    filter: "筛选",
    refresh: "刷新",
    retry: "重试",
    back: "返回",
    submit: "提交",
    close: "关闭",
    details: "详情",
    done: "完成",
    copy: "复制",
    success: "操作成功",
    failed: "操作失败",
    required: "必填",
    optional: "选填",
    enabled: "启用",
    disabled: "禁用",
    switchLanguage: "切换语言",
    dialog: "弹窗",
    globalLoading: "加载中...",
    loading: "加载中...",
    saving: "保存中…",
    resourceLoadingDescription: "正在加载应用资源...",
    import: "导入",
    export: "导出",
    actions: "操作",
    noData: "暂无数据",
    yes: "是",
    no: "否",
  },
  toast: {
    viewportLabel: "通知",
  },
  auth: {
    brandAria: "返回登录页",
    terms: "服务条款",
    privacy: "隐私政策",
    copyright: buildCopyrightText(),
    loginFailed: "登录失败",
    mfaFailed: "MFA 验证失败",
    verificationFailed: "验证码错误，请重试",
    passkeyVerifying: "验证中...",
    passkeyLogin: "使用 Passkey 登录",
    or: "或",
    mfaPrompt: "账号 {email} 已开启 MFA，请输入 6 位验证码。",
    mfaCode: "一次性验证码",
    verifyAndSignIn: "验证并登录",
    previousStep: "返回上一步",
    tenantIdentifier: "租户标识",
    tenantIdPlaceholder: "租户 ID",
    email: "邮箱",
    password: "密码",
    signingIn: "登录中...",
    signIn: "登录",
    forgotPassword: "忘记密码",
    trialQuestion: "希望体验平台？",
    applyTrial: "申请试用",
    enterEmailFirst: "请先输入邮箱",
    passkeyUnsupportedOrigin:
      "当前访问地址不受支持，请使用 http://localhost:6277 登录",
    passkeyRpMismatch: "Passkey 域名不匹配，请使用 http://localhost:6277 登录",
    passkeyNotBound: "该邮箱尚未绑定 Passkey，请先在设置中注册或使用密码登录",
    passkeyFailed: "Passkey 登录失败，请重试或使用密码登录",
    mfaInvalid: "验证码错误，请检查后重试",
    mfaInvalidDetailed: "验证码错误，请检查身份验证器中的 6 位数字后重试",
    sessionExpired: "登录状态已过期，请重新登录。",
    sessionRevoked: "您的账号已在其他设备登录，当前会话已失效，请重新登录",
    ssoContinueWith: "使用 {provider} 登录",
    tenantRequired: "请先填写租户标识",
    ssoErrors: {
      SSO_LOGIN_FAILED: "企业 SSO 登录失败，请重试或联系管理员",
      SSO_USER_NOT_PROVISIONED: "当前账号尚未在平台开通，请联系租户管理员",
      SSO_MFA_SESSION_EXPIRED: "SSO 验证会话已过期，请重新发起登录",
      LOGIN_IP_NOT_ALLOWED: "当前 IP 不在允许登录范围内",
      PASSWORD_LOGIN_DISABLED: "该租户已禁用密码登录",
      ENTERPRISE_MFA_REQUIRED: "租户策略要求启用 MFA，请先在设置中配置",
      OIDC_STATE_EXPIRED: "SSO 状态已过期，请重新发起登录",
    },
    forgot: {
      title: "找回密码",
      description: "输入账号邮箱，我们会发送一个 30 分钟内有效的密码重置链接。",
      sent: "如果账号存在，重置邮件已经发送。请检查收件箱和垃圾邮件目录。",
      tenantLabel: "租户 ID",
      optional: "选填",
      tenantPlaceholder: "邮箱属于多个租户时请填写",
      sendResetEmail: "发送重置邮件",
      submitting: "提交中...",
      backToSignIn: "返回登录",
      submitFailed: "提交失败",
    },
    reset: {
      title: "重置密码",
      description: "请设置新的登录密码。链接和令牌将在 30 分钟后失效。",
      done: "密码已更新，所有旧登录会话已撤销。请使用新密码重新登录。",
      goToSignIn: "前往登录",
      token: "重置令牌",
      newPassword: "新密码",
      confirmPassword: "确认密码",
      updating: "更新中...",
      updatePassword: "更新密码",
      mismatch: "两次密码不一致",
      failed: "重置失败",
    },
    invitation: {
      title: "接受账号邀请",
      done: "账号已创建，可以使用邮箱和新密码登录。",
      token: "邀请令牌",
      name: "姓名",
      setPassword: "设置密码",
      joinTenant: "加入租户",
      acceptFailed: "接受邀请失败",
      validation: {
        tokenRequired: "请输入邀请令牌",
        passwordMin: "密码至少 8 位",
      },
    },
  },
  personalAuth: {
    registerTitle: "个人账号注册",
    registerDescription: "使用邮箱创建个人租户与默认工作区。",
    displayName: "显示名称",
    displayNamePlaceholder: "例如：张三",
    registerSubmit: "注册",
    registerSubmitting: "注册中…",
    registerFailed: "注册失败",
    emailAlreadyRegistered: "该邮箱已注册，请直接登录。",
    haveAccount: "已有账号？",
    googleRegister: "使用 Google 注册 / 登录",
    googleLogin: "使用 Google 登录",
    googleUnavailableTitle: "无法使用该 Google 账号登录",
    googleUnavailableDescription: "这个 Google 账号尚未开通。请使用已开通的账号登录，或返回首页后提交试用申请，由运营通过后再使用。",
    googleUnavailableGeneric: "Google 登录没有完成。请稍后再试。",
    backHome: "返回首页",
    verifyPendingTitle: "请完成邮箱验证",
    verifyPendingDescription: "请查收邮件并点击验证链接完成认证。",
    verifySentTo: "验证邮件已发送至：{email}",
    verifySuccessTitle: "邮箱验证成功",
    verifySuccessDescription: "请登录后继续使用。",
    verifySuccessNotice: "邮箱已验证成功，登录后即可开始使用。",
    verifyAlreadyTitle: "邮箱已验证",
    verifyAlreadyDescription: "该邮箱已完成验证。",
    verifyAlreadyNotice: "无需额外操作，请登录后继续使用。",
    verifyExpiredTitle: "验证链接无效",
    verifyExpiredDescription: "链接已过期或无效，请重新发送验证邮件。",
    verifyExpiredNotice: "请重新发送验证邮件，并从邮件中的链接再次完成验证。",
    resendVerification: "重发验证邮件",
    resendAccepted: "验证邮件已发送，请查收。",
    resendFailed: "验证邮件发送失败",
    emailNotVerified: "未验证",
    homeTitle: "个人首页",
    homeLoadFailed: "加载个人首页失败",
    workspaceType: "工作区类型",
    entityType: "主体类型",
    currency: "币种",
    fiscalYear: "财年开始日",
    googleLinkRequired: "该邮箱已有账号，请先登录后再绑定 Google。",
    welcomeTitle: "欢迎，{name}",
    welcomeDescription: "您的个人工作区已就绪，可以从仪表盘开始管理账务。",
    verifyPendingInline: "完成邮箱验证后可使用全部功能。",
    setupCompleteTitle: "账号准备情况",
    setupCompleteDescription: "个人事业主的初始设置已完成。",
    accountEmail: "邮箱",
    workspaceName: "工作区",
    entityTypeSoleProprietor: "个人事业主",
    fiscalYearValue: "{month}月{day}日起 · {currency}",
    nextStepsTitle: "下一步",
    nextStepsDescription: "建议先进入仪表盘了解概况，或前往设置完善个人资料。",
    goToDashboard: "进入仪表盘",
    openSettings: "打开设置",
    viewWorkspace: "查看工作区",
    uploadDocuments: "上传票据",
  },
  personalLineIntegration: {
    title: "LINE 绑定",
    description: "将 LINE 官方账号与个人账号绑定。",
    notLinked:
      "尚未绑定 LINE 账号。\n\n请添加 LINE 官方账号为好友，并在对话中发送「連携」。",
    loadFailed: "无法加载 LINE 绑定状态",
    actionFailed: "更新 LINE 绑定失败",
    platformTitle: "官方账号",
    platformAvailable: "可用",
    platformUnavailable: "不可用",
    addFriend: "添加好友",
    bindingsTitle: "已绑定工作区",
    defaultBadge: "默认接收工作区",
    imageIntakeOn: "图片接收开启",
    imageIntakeOff: "图片接收关闭",
    linkedAt: "绑定时间 {value}",
    noDefaultWarning:
      "尚未选择接收工作区。请在从 LINE 发送票据前选择默认工作区。",
    setDefaultButton: "设为默认接收",
    unlinkWorkspaceButton: "解除此工作区绑定",
    unlinkAllButton: "解除全部 LINE 绑定",
    unlinkAllConfirmTitle: "解除全部 LINE 绑定",
    unlinkAllConfirmDescription: "将解除当前账号下所有有效的 LINE 工作区绑定。",
    selectWorkspaceTitle: "请选择要绑定 LINE 的工作区",
    selectWorkspaceDescription: "选择此 LINE 账号要绑定到哪个工作区。",
    noBindableWorkspace: "没有可绑定 LINE 的工作区。",
    alreadyLinked: "已绑定",
    resultSuccessTitle: "LINE 绑定已完成",
    resultSuccessDescription: "请返回 LINE 应用。",
    resultFailedTitle: "无法完成 LINE 绑定",
    resultFailedDescription: "请从 LINE 对话重新发起绑定。",
    resultPendingTitle: "正在确认绑定",
    resultPendingDescription: "请稍后重试。",
  },
  workspaceLineIntegration: {
    title: "Workspace LINE 绑定",
    description: "为此工作区启用来自 LINE 的票据图片接收。",
    platformTitle: "官方账号",
    platformAvailable: "可用",
    platformUnavailable: "不可用",
    addFriend: "添加好友",
    integrationTitle: "工作区集成",
    statusActive: "已启用",
    statusDisabled: "已停用",
    statusNotEnabled: "未启用",
    imageIntakeOn: "图片接收开启",
    imageIntakeOff: "图片接收关闭",
    enableButton: "启用 LINE 集成",
    disableButton: "停用 LINE 集成",
    enableImageButton: "开启图片接收",
    disableImageButton: "关闭图片接收",
    myBindingTitle: "我的 LINE 绑定",
    bound: "已绑定到此工作区。",
    notBound: "尚未绑定到此工作区。",
    boundAccountFallback: "LINE 账户 {value}",
    boundAccountUnknown: "已绑定的 LINE 账户",
    lineAccountId: "账户标识：{value}",
    linkedAt: "绑定时间：{value}",
    defaultBadge: "默认接收工作区",
    setDefaultButton: "设为默认接收工作区",
    unlinkButton: "解除此工作区绑定",
    linkStepsTitle: "绑定步骤",
    linkStepAddFriend: "在下方添加 LINE 官方账号为好友。",
    linkStepSendCommand: "在 LINE 1:1 对话中发送「連携」。",
    linkStepOpenLink:
      "点击 Bot 回复的「連携する」按钮，登录本系统并选择此工作区。",
    linkStepConfirm:
      "在 LINE 中完成 Account Link 确认后，返回此页面查看绑定状态。",
    manageBindingsButton: "管理全部 LINE 绑定",
    accountBindingsTitle: "账号 LINE 绑定列表",
    accountBindingsDescription:
      "查看 LINE 账号与各 Workspace 的绑定状态，可在此设置默认接收 Workspace 或解除绑定。",
    currentWorkspaceBadge: "当前 Workspace",
    inviteTitle: "邀请提交者绑定",
    inviteDescription:
      "生成 6 位邀请码，让无需 Web 账号的 LINE 用户绑定到此工作区并提交票据。",
    generateInviteButton: "生成邀请码",
    inviteCodeLabel: "邀请码",
    inviteExpiresAt: "有效期至 {value}",
    inviteInstructions:
      "请提交者在 LINE 与 Bot 的 1:1 对话中发送上述 6 位数字（或「BIND 数字」）。",
    copyInviteCode: "复制邀请码",
    copiedInviteCode: "已复制",
    revokeInviteCode: "撤销邀请码",
    submittersTitle: "已绑定提交者",
    submittersEmpty: "尚无通过邀请码绑定的 LINE 提交者。",
    unlinkSubmitter: "解除绑定",
    loadFailed: "加载工作区 LINE 集成失败",
    actionFailed: "更新工作区 LINE 集成失败",
  },
  personalDocuments: {
    uploadTitle: "票据上传",
    uploadDescription: "上传 PDF 或图片格式的收据与发票。",
    listTitle: "票据列表",
    listDescription: "查看已上传票据及处理状态。",
    selectFileTitle: "选择文件",
    allowedFormats: "PDF、JPEG、PNG，最大 {maxMb} MB",
    dropzone: "点击选择文件或拖拽到此处",
    uploadButton: "上传",
    uploading: "上传中…",
    uploadSuccess: "上传完成。",
    uploadFailed: "上传失败",
    fileTooLarge: "文件超过 {maxMb} MB 限制",
    viewList: "查看列表",
    nextPhaseNotice: "OCR 与分录建议将在后续版本提供。",
    empty: "暂无已上传票据。",
    download: "下载",
    delete: "删除",
    loadFailed: "加载列表失败",
    downloadFailed: "下载失败",
    deleteFailed: "删除失败",
    workspaceRequired: "请先选择工作区再上传。",
    viewDetail: "详情",
    detailTitle: "票据详情",
    detailDescription: "查看 OCR 与分录候选",
    processingStatus: "处理状态",
    failureCode: "错误码",
    ocrResult: "OCR 结果",
    pageCount: "页数",
    detectedLanguages: "检测语言",
    candidateTitle: "分录候选",
    candidateDisclaimer: "以下为 AI 候选，尚未写入会计数据。",
    warnings: "警告",
    retryProcessing: "重试处理",
    retryFailed: "重试失败",
    openReview: "打开内容确认",
    review: {
      title: "内容确认",
      description: "请对照原件核对并修正 AI 候选分录。",
      aiDisclaimer: "以下为 AI 候选，请对照原件核对并修正。",
      previewTitle: "凭证预览",
      showOriginal: "显示凭证",
      formTitle: "录入内容",
      documentNumber: "凭证编号",
      memo: "摘要",
      linesTitle: "借方 / 贷方",
      debit: "借方",
      credit: "贷方",
      selectAccount: "选择会计科目",
      addLine: "添加行",
      clearAll: "清空全部候选",
      totals: "合计",
      unbalanced: "借贷不平衡",
      save: "保存",
      confirm: "确认内容",
      confirmTitle: "确认复核内容",
      confirmDescription: "将确认录入内容并创建会计草稿。",
      openDraft: "打开会计草稿",
      loadFailed: "加载复核内容失败",
      saveFailed: "保存失败",
      confirmFailed: "确认失败",
      saveState: {
        idle: "未保存",
        saving: "保存中",
        saved: "已保存",
        failed: "保存失败",
        conflict: "其他页面已更新内容。请加载最新内容后再次保存。",
      },
      steps: {
        document: "凭证",
        ocr: "识别结果",
        review: "内容确认",
        draft: "会计草稿",
        approval: "审批",
        posting: "过账",
      },
    },
    field: {
      documentType: "凭证类型",
      documentDate: "日期",
      counterparty: "交易对方",
      totalAmount: "合计金额",
      taxAmount: "税额",
      confidence: "置信度",
    },
    processingStage: {
      OCR_QUEUED: "等待 OCR",
      OCR_PROCESSING: "OCR 处理中",
      OCR_COMPLETED: "OCR 完成",
      AI_EXTRACTION_QUEUED: "等待候选生成",
      AI_EXTRACTING: "候选生成中",
      REVIEW_REQUIRED: "待人工复核",
      PROCESSING_FAILED: "处理失败",
    },
    status: {
      UPLOADED: "上传中",
      SECURITY_SCANNING: "上传中",
      STORED: "已保存",
      SECURITY_REJECTED: "失败",
      QUEUED_FOR_PARSING: "待处理",
      PARSING: "待处理",
      PARSED: "待处理",
      NEEDS_REVIEW: "待复核",
      OCR_QUEUED: "等待 OCR",
      OCR_PROCESSING: "OCR 处理中",
      OCR_COMPLETED: "OCR 完成",
      AI_EXTRACTION_QUEUED: "等待候选生成",
      AI_EXTRACTING: "候选生成中",
      PROCESSING_FAILED: "处理失败",
      PROCESSING_CANCELLED: "已取消",
      ARCHIVED: "已删除",
    },
  },
  legalPage: {
    effectiveDate: "生效日期：{date}",
  },
  trial: {
    title: "申请试用",
    description: "提交企业信息并完成邮箱验证，审核通过后将收到管理员账号邀请。",
    submittedTitle: "申请已提交",
    submittedDescription: "运营人员审核后会通过邮件通知您，请留意收件箱。",
    organizationName: "企业或组织名称",
    organizationPlaceholder: "请输入企业或组织名称",
    contactName: "联系人",
    contactPlaceholder: "请输入联系人姓名",
    workEmail: "工作邮箱",
    emailVerified: "已验证",
    resendAfter: "{seconds}s 后重发",
    sendingCode: "发送中",
    sendCode: "发送验证码",
    verificationCode: "邮箱验证码",
    codePlaceholder: "请输入 6 位验证码",
    verifying: "验证中",
    confirmEmail: "确认邮箱",
    phone: "联系电话",
    phonePlaceholder: "请输入联系电话",
    countryRegion: "国家或地区",
    countryPlaceholder: "例如：中国",
    expectedMonthlyUnits: "预计每月用量",
    useCase: "使用场景",
    useCasePlaceholder: "例如：每月处理供应商发票并提取金额、日期和公司信息",
    notes: "补充说明",
    notesPlaceholder: "可补充文档类型、语言或期望上线时间",
    agreementPrefix: "我已阅读并同意",
    agreementConnector: "和",
    agreementSuffix: "。",
    submitting: "提交中...",
    submit: "提交试用申请",
    invalidEmail: "请输入有效的邮箱地址",
    codeSent: "验证码已发送，请检查邮箱",
    codeSendFailed: "验证码发送失败",
    invalidCode: "请输入 6 位验证码",
    emailVerifiedToast: "邮箱验证成功",
    codeVerifyFailed: "验证码验证失败",
    submitFailed: "提交失败，请稍后重试",
    validation: {
      organizationName: "请填写企业或组织名称",
      organizationNameTooLong: "企业或组织名称不能超过 {max} 个字符",
      contactName: "请填写联系人姓名",
      contactNameTooLong: "联系人姓名不能超过 {max} 个字符",
      phone: "请输入有效的联系电话",
      phoneTooLong: "联系电话不能超过 {max} 个字符",
      countryRegion: "请填写国家或地区",
      countryRegionTooLong: "国家或地区不能超过 {max} 个字符",
      useCase: "请至少用 10 个字描述使用场景",
      useCaseTooLong: "使用场景不能超过 {max} 个字符",
      expectedMonthlyUnits: "预计用量必须大于 0",
      expectedMonthlyUnitsInteger: "预计用量必须是整数",
      expectedMonthlyUnitsTooLarge: "预计用量不能超过 {max}",
      notesTooLong: "补充说明不能超过 {max} 个字符",
      termsAccepted: "请阅读并同意服务条款和隐私政策",
      verificationToken: "请先完成邮箱验证",
    },
  },
  navigation: {
    operatorConsole: "运营控制台",
    tenantConsole: "返回租户端",
    operationManagement: "运营管理",
  },
  impersonation: {
    bannerTitle: "代运营中",
    bannerDescription: "你正在以租户管理员权限管理「{tenant}」",
    exitToOperator: "返回运营控制台",
    exitSuccess: "已退出代运营",
    exitFailed: "退出代运营失败",
  },
  userMenu: {
    accountSettings: "个人设定",
    feedback: "我的反馈",
    signOut: "退出",
  },
  sidebar: {
    sendFeedback: "我要反馈",
  },
  tenantShell: {
    menuSearchPlaceholder: "搜索菜单",
    menuNoResults: "没有匹配的菜单",
    collapseSidebar: "折叠侧边栏",
    expandSidebar: "展开侧边栏",
    breadcrumb: "当前位置",
    back: "后退",
    refresh: "刷新当前页面",
    home: "返回首页",
    fullscreen: "全屏显示",
    exitFullscreen: "退出全屏",
    openedPages: "已打开页面",
    closeTab: "关闭 {label}",
    closeCurrentTab: "关闭当前",
    closeOtherTabs: "关闭其他",
    closeAllTabs: "关闭所有",
  },
  placeholders: {
    clickToCopy: "点击复制替换词",
    custom: "自定义",
    example: "示例: {example}",
    copiedToken: "已复制 {token}",
    copyFailedManual: "复制失败，请手动复制",
  },
  dataTableCell: {
    copiedToClipboard: "已复制到剪贴板",
    copyFailed: "复制失败",
  },
  menu: {
    systemSettings: { label: "系统设定" },
    personalSettings: { label: "个人设定", subtitle: "个人账户与安全" },
    sections: {
      documents: "凭证",
      banking: "银行",
      arAp: "债权债务",
      accounting: "会计",
      reports: "报告与导出",
    },
    dashboard: {
      label: "仪表盘",
      subtitle: "工作区关键指标与待办",
    },
    workspace: {
      label: "工作区",
      subtitle: "会计数据空间与成员边界",
    },
    documentIntake: {
      label: "书类受信箱",
      subtitle: "收据与发票受理及 AI 解析确认",
    },
    documentSummary: {
      label: "伝票集計",
      subtitle: "处理完成伝票的财务汇总、筛选与导出",
    },
    reviewInbox: {
      label: "复核收件箱",
      subtitle: "等待处理的分录草稿复核",
    },
    accountingBooks: {
      label: "账簿设置",
      subtitle: "管理工作区会计账簿与会计期间",
    },
    chartOfAccounts: {
      label: "勘定科目",
      subtitle: "管理有效会计账簿下的科目",
    },
    ledger: {
      label: "总账",
      subtitle: "当前有效会计账簿的已过账分录总账",
    },
    financialReports: {
      label: "财务报表",
      subtitle: "试算表、资产负债表与损益投影",
    },
    accountingExports: {
      label: "会计数据导出",
      subtitle: "将已过账分录导出为通用 CSV",
    },
    bankAccounts: {
      label: "账户设定",
      subtitle: "银行账户登记与 GL 科目映射",
    },
    bankImport: {
      label: "银行流水导入",
      subtitle: "上传银行流水并查看导入批次",
    },
    bankMatching: {
      label: "银行流水匹配",
      subtitle: "将银行流水行与候选匹配并确认",
    },
    bankEvidence: {
      label: "银行明细×传票核对",
      subtitle: "将出金明细与已确认传票段核对关联",
    },
    bankReconciliation: {
      label: "期间对账",
      subtitle: "按期间核对银行明细与业务交易",
    },
    bankGlMappings: {
      label: "银行口座 会計設定",
      subtitle: "配置银行账户与 GL 科目映射",
    },
    arApReceivables: {
      label: "应收",
      subtitle: "应收义务（运营未决清单）",
    },
    arApPayables: {
      label: "应付",
      subtitle: "应付义务（运营未决清单）",
    },
    arApPayments: {
      label: "收付款",
      subtitle: "入金与出金",
    },
    openingArAp: {
      label: "期初应收应付",
      subtitle: "导入期初未结余额",
    },
    japaneseFinancialStatements: {
      label: "日本式财务报表",
      subtitle: "基于报表快照的日本式列示",
    },
    webhooks: {
      label: "Webhook",
      subtitle: "事件推送与集成",
    },
    team: {
      label: "权限管理",
      subtitle: "菜单权限与成员",
    },
    organizationHealth: {
      label: "企业管理",
      subtitle: "企业信息、事业所与部门",
    },
    employeeManagement: {
      label: "员工管理",
      subtitle: "员工、退职者与标签",
    },
    billing: {
      label: "套餐账单",
      subtitle: "套餐、额度与账单",
    },
    settings: {
      label: "设置",
      subtitle: "账户与安全",
    },
    personalLineIntegration: {
      label: "LINE 绑定",
      subtitle: "绑定 LINE 账号并管理接收工作区",
    },
    feedback: {
      label: "我的反馈",
      subtitle: "查看历史反馈与追问",
    },
    notifications: {
      label: "通知中心",
      subtitle: "站内提醒与业务动态",
    },
  },
  workspace: {
    switcher: { placeholder: "选择工作区" },
    accessSource: {
      DIRECT: "直接成员",
      FIRM_ENGAGEMENT: "事务所授权",
      FIRM_ASSIGNMENT: "事务所分配",
      firmEngagement: "事务所授权",
    },
    actions: {
      createPersonal: "创建个人工作区",
      createFirm: "创建工作区",
      lineIntegration: "LINE 集成",
    },
    line: {
      unavailable: "当前未开放 LINE 绑定",
    },
    form: {
      name: "工作区名称",
      personalPlaceholder: "例如：个人账本",
      firmPlaceholder: "例如：东京税理士事务所",
      defaults:
        "首版固定为 Asia/Tokyo、ja-JP、JPY，默认会计年度从 1 月 1 日开始。",
    },
    page: {
      title: "工作区",
      description:
        "Workspace 是票据、银行流水、会计草稿、审核和导出的实际操作边界。",
      noAccess: "无法访问当前工作区，请选择其他工作区或联系管理员。",
      emptyTitle: "暂无可访问工作区",
      emptyDescription: "你需要被加入至少一个 Workspace 后才能处理会计数据。",
    },
    table: {
      name: "名称",
      type: "类型",
      status: "状态",
      roles: "角色",
      actions: "操作",
    },
    type: {
      PERSONAL: "个人",
      FIRM_INTERNAL: "税理士事务所",
      CLIENT_COMPANY: "客户企业",
    },
    status: { ACTIVE: "启用", SUSPENDED: "暂停", ARCHIVED: "归档" },
    role: {
      PERSONAL_OWNER: "个人所有者",
      FIRM_OWNER: "事务所所有者",
      FIRM_ADMIN: "事务所管理员",
      ACCOUNTANT: "税理士担当",
      REVIEWER: "复核者",
      ASSISTANT: "助理",
      COMPANY_OWNER: "企业所有者",
      COMPANY_ADMIN: "企业管理员",
      COMPANY_MEMBER: "企业成员",
      COMPANY_VIEWER: "企业只读",
    },
  },
  chartOfAccounts: {
    title: "勘定科目",
    subtitle: "管理有效会计账簿下的科目。",
    emptyNoBook: {
      title: "尚无有效会计账簿",
      description: "请先在账簿设置中创建会计账簿，再管理科目。",
      openSettings: "打开账簿设置",
    },
    conflict: {
      title: "存在多本有效账簿",
      description: "请先在账簿设置中处理至仅剩一本有效账簿，再管理科目。",
      openSettings: "打开账簿设置",
    },
    actions: {
      createAccount: "新增科目",
      edit: "编辑",
      archive: "归档",
      initialize: "初始化",
    },
    initialize: {
      confirmDescription:
        "仅补充缺失的默认科目；已有科目的代码和名称不会被修改。",
    },
    fields: {
      code: "代码",
      name: "名称",
      accountType: "Type",
      normalBalance: "借贷方向",
      status: "状态",
    },
    errors: {
      loadFailed: "加载勘定科目失败。",
      bookAccessRequired:
        "需要账簿设置读取权限才能加载有效账簿，否则无法管理科目。",
      initializeFailed: "初始化勘定科目失败。",
    },
  },
  accountingBookSettings: {
    title: "账簿设置",
    subtitle: "管理工作区当前有效的会计账簿与会计期间。",
    tabs: {
      summary: "概要",
      accounts: "科目",
      periods: "会计期间",
    },
    empty: {
      title: "尚无有效会计账簿",
      description: "请先创建账簿，再使用会计草稿或总账。",
      create: "创建账簿",
    },
    conflict: {
      title: "存在多本有效账簿",
      description: "请归档至仅剩一本有效账簿后，再管理会计期间。",
      archive: "归档",
    },
    history: {
      title: "历史账簿",
    },
    actions: {
      edit: "编辑",
      archive: "归档",
      createAccount: "新增科目",
      initializePeriods: "初始化年度",
    },
    fields: {
      name: "名称",
      accountingBasis: "记账基础",
      fiscalYearStart: "会计年度起算",
      defaultTaxTreatment: "默认税区分",
      currency: "币种",
      status: "状态",
      fiscalYear: "会计年度",
    },
    errors: {
      loadFailed: "加载账簿失败。",
      createFailed: "创建账簿失败。",
      updateFailed: "更新账簿失败。",
      archiveFailed: "归档账簿失败。",
      accountCreateFailed: "创建科目失败。",
      accountUpdateFailed: "更新科目失败。",
      accountArchiveFailed: "归档科目失败。",
      periodInitFailed: "初始化会计期间失败。",
    },
  },
  documents: {
    transaction: {
      field: {},
      type: {
        EXPENSE: "费用",
        PURCHASE: "采购",
        SALE: "销售",
        INCOME: "收入",
        PAYMENT: "付款",
        RECEIPT: "收款",
        TRANSFER: "转账",
        REFUND: "退款",
        UNKNOWN: "未知",
      },
      status: {
        DRAFT: "草稿",
        REVIEW_REQUIRED: "待确认",
        CONFIRMED: "已确认",
        REJECTED: "已拒绝",
        SUPERSEDED: "已被新草稿替代",
        CANCELLED: "已取消",
      },
      validation: {},
      warning: {},
    },
    accounting: {
      posted: "已过账",
      book: {
        defaultName: "默认会计账簿",
        openSettings: "前往账簿设置",
      },
      classification: {
        status: {
          SUGGESTED: "已建议",
          REVIEW_REQUIRED: "待确认",
          CONFIRMED: "已确认",
          REJECTED: "已拒绝",
          SUPERSEDED: "已被替代",
          CANCELLED: "已取消",
        },
      },
      journal: {
        status: {
          DRAFT: "草稿",
          REVIEW_REQUIRED: "待确认",
          CONFIRMED: "草稿已确认",
          REJECTED: "已拒绝",
          SUPERSEDED: "已被替代",
          CANCELLED: "已取消",
        },
      },
      validation: {
        VALID: "借贷平衡",
        WARNING: "有警告",
        INVALID: "不可确认",
      },
      tax: {
        DISPLAYED_TAX_10: "票面 10%",
        DISPLAYED_REDUCED_TAX_8: "票面 8%",
        DISPLAYED_EXEMPT: "票面免税",
        DISPLAYED_MIXED: "票面混合",
        UNKNOWN: "税务建议未知",
      },
      period: {
        range: "{start} 至 {end}",
        status: {
          OPEN: "开放",
          SOFT_LOCKED: "软锁定",
          CLOSED: "已关闭",
        },
      },
      posting: {
        title: "过账",
        request: "申请过账",
        processingHint: "过账请求已入队，正在处理中。",
        status: {
          NOT_REQUESTED: "未申请",
          QUEUED: "排队中",
          PROCESSING: "处理中",
          SUCCEEDED: "已过账",
          RETRYABLE_FAILED: "失败可重试",
          PERMANENTLY_FAILED: "永久失败",
          CANCELLED: "已取消",
        },
      },
      postedJournal: {
        title: "Posted Journal",
      },
      reversal: {
        title: "冲销仕訳",
        create: "创建冲销申请",
        submit: "提交复核",
        reversalDate: "冲销日期",
        reasonCode: "原因代码",
        reasonText: "冲销理由",
        displayStatus: {
          none: "未冲销",
          pending: "冲销处理中",
          posted: "已冲销",
        },
        status: {},
      },
      amendment: {
        displayStatus: {},
        types: {},
        status: {},
      },
    },
    ledger: {
      title: "总账",
      backToDocuments: "返回票据",
      financialReports: "财务报表",
      accountFilter: "科目筛选",
      allAccounts: "全部科目",
      dateRange: "日期范围",
      journalNumber: "凭证号",
      search: "查询",
      empty: "暂无总账分录。",
      noPermission: "当前工作区无总账查看权限。",
      columns: {
        date: "日期",
        journal: "凭证",
        account: "科目",
        side: "借贷",
        amount: "金额",
      },
      journalType: {
        STANDARD: "标准",
        REVERSAL: "冲销",
        AMENDMENT: "更正",
      },
      reversesJournal: "冲销 {value}",
      reversedByJournal: "被 {value} 冲销",
      amendsJournal: "更正 {value}",
      amendedByJournal: "被 {value} 更正",
      periodClose: {
        period: "会计期间",
        summaryTitle: "期间摘要",
        title: "期间结账",
        ledgerEntryCount: "总账分录数",
        postedJournalCount: "已过账凭证数",
        totalDebit: "借方合计",
        totalCredit: "贷方合计",
        softLock: "软锁定期间",
        create: "创建结账申请",
        validate: "运行校验",
        submit: "提交复核",
        cancel: "取消申请",
        checksTitle: "结账校验项",
        snapshotTitle: "结账快照",
        snapshotGeneratedAt: "生成于 {value}",
        openingBalanceWarning:
          "当前期间尚未生成期初余额，请先完成上一已关账期间的结转后再进行期间结账。",
        summaryNone: "尚未创建结账申请。",
        summaryClosed: "该期间已结账。",
        summarySoftLockRequired: "请先软锁定期间后再创建结账申请。",
        status: {
          DRAFT: "草稿",
          VALIDATING: "校验中",
          VALIDATION_FAILED: "校验未通过",
          REVIEW_REQUIRED: "待复核",
          APPROVED: "已批准",
          CLOSING: "结账中",
          CLOSED: "已结账",
          REJECTED: "已拒绝",
          CANCELLED: "已取消",
          SUPERSEDED: "已被替代",
          FAILED: "失败",
        },
        checkStatus: {
          PASSED: "通过",
          WARNING: "警告",
          FAILED: "失败",
          NOT_APPLICABLE: "不适用",
        },
        snapshotColumns: {
          code: "科目代码",
          account: "科目名称",
          periodDebit: "本期借方",
          periodCredit: "本期贷方",
          closingDebit: "期末借方",
          closingCredit: "期末贷方",
        },
        checkMessages: {
          POSTING_IN_PROGRESS_PASSED: "无进行中的过账请求。",
          POSTING_IN_PROGRESS_FAILED: "存在进行中的过账请求。",
          POSTING_FAILED_PASSED: "无永久失败的过账请求。",
          POSTING_FAILED_FAILED: "存在永久失败的过账请求。",
          APPROVED_REVERSAL_NOT_POSTED_PASSED: "无待过账的冲销申请。",
          APPROVED_REVERSAL_NOT_POSTED_FAILED: "存在已批准但未过账的冲销申请。",
          APPROVED_AMENDMENT_NOT_POSTED_PASSED: "无待过账的更正申请。",
          APPROVED_AMENDMENT_NOT_POSTED_FAILED:
            "存在已批准但未过账的更正申请。",
          POSTED_JOURNAL_BALANCE_PASSED: "已过账凭证借贷平衡。",
          POSTED_JOURNAL_BALANCE_FAILED: "已过账凭证借贷不平衡。",
          LEDGER_LINE_COUNT_PASSED: "总账分录数与过账行数一致。",
          LEDGER_LINE_COUNT_FAILED: "总账分录数与过账行数不一致。",
          LEDGER_AMOUNT_MATCH_PASSED: "总账金额与过账行一致。",
          LEDGER_AMOUNT_MATCH_FAILED: "总账金额与过账行不一致。",
          ORPHAN_LEDGER_ENTRY_PASSED: "无孤立总账分录。",
          ORPHAN_LEDGER_ENTRY_FAILED: "存在孤立总账分录。",
          DUPLICATE_LEDGER_ENTRY_PASSED: "无重复总账分录。",
          DUPLICATE_LEDGER_ENTRY_FAILED: "存在重复总账分录。",
          PERIOD_JOURNAL_DATE_PASSED: "凭证日期均在期间范围内。",
          PERIOD_JOURNAL_DATE_FAILED: "存在期间范围外的凭证日期。",
          PERIOD_LEDGER_DATE_PASSED: "总账日期均在期间范围内。",
          PERIOD_LEDGER_DATE_FAILED: "存在期间范围外的总账日期。",
          JOURNAL_TYPE_RELATION_PASSED: "凭证类型关联正确。",
          JOURNAL_TYPE_RELATION_FAILED: "凭证类型关联异常。",
          TOTAL_DEBIT_CREDIT_PASSED: "总账借贷合计平衡。",
          TOTAL_DEBIT_CREDIT_FAILED: "总账借贷合计不平衡。",
          UNCONFIRMED_JOURNAL_DRAFTS_PASSED: "无未确认的分录草稿。",
          UNCONFIRMED_JOURNAL_DRAFTS_WARNING: "存在未确认的分录草稿。",
          UNCONFIRMED_JOURNAL_DRAFTS_NOT_APPLICABLE:
            "未确认分录草稿检查不适用。",
          OPEN_REVIEWS_PASSED: "无未结复核案件。",
          OPEN_REVIEWS_WARNING: "存在未结复核案件。",
          OPEN_REVIEWS_NOT_APPLICABLE: "复核案件检查不适用。",
          UNPOSTED_DOCUMENTS_PASSED: "无未过账票据。",
          UNPOSTED_DOCUMENTS_WARNING: "存在未过账票据。",
          UNPOSTED_DOCUMENTS_NOT_APPLICABLE: "未过账票据检查不适用。",
          OPENING_BALANCE_REQUIRED_PASSED: "当前期间已存在期初余额。",
          OPENING_BALANCE_REQUIRED_FAILED: "非首期期间必须先完成期初余额结转。",
          OPENING_BALANCE_CHECKSUM_PASSED: "期初余额校验和有效。",
          OPENING_BALANCE_CHECKSUM_FAILED: "期初余额校验和无效。",
        },
      },
      trialBalance: {
        title: "试算平衡",
        columns: {
          code: "科目代码",
          account: "科目名称",
          openingDebit: "期初借方",
          openingCredit: "期初贷方",
          periodDebit: "本期借方",
          periodCredit: "本期贷方",
          closingDebit: "期末借方",
          closingCredit: "期末贷方",
        },
      },
      carryForward: {
        title: "期间结转",
        crossYearTitle: "跨会计年度期初结转",
        create: "创建结转申请",
        validate: "运行校验",
        submit: "提交复核",
        cancel: "取消申请",
        checksTitle: "结转校验项",
        summaryNone: "尚未创建结转申请。",
        summaryCrossYearNone:
          "上一会计年度已决算后，可创建跨年度期初结转申请。",
        summaryCrossYearCompleted: "当前年度首期已生成跨年度期初余额。",
        summaryCompleted: "当前期间已生成期初余额。",
        summarySourceNotClosed: "上一期间必须先结账后才能结转。",
        summaryNotApplicable: "首期无需同年度结转。",
        crossYearDisclaimerRevenueExpense:
          "收入和费用科目不会结转到下一财政年度。",
        crossYearDisclaimerCurrentYearProfit:
          "本期纯利益账户必须在年结后为零。",
        crossYearDisclaimerRetainedEarnings:
          "利益剰余金已包含本年度净利润或亏损，将按年结后余额结转。",
        crossYearDisclaimerNoJournal: "本操作不会创建新的凭证或 Ledger Entry。",
        crossYearDisclaimerNoFinancialStatements:
          "本操作不会生成正式财务报表，也不代表税务申报已完成。",
        status: {
          DRAFT: "草稿",
          VALIDATING: "校验中",
          VALIDATION_FAILED: "校验未通过",
          REVIEW_REQUIRED: "待复核",
          APPROVED: "已批准",
          PROCESSING: "处理中",
          COMPLETED: "已完成",
          REJECTED: "已拒绝",
          CANCELLED: "已取消",
          SUPERSEDED: "已被替代",
          FAILED: "失败",
        },
        checkStatus: {
          PASSED: "通过",
          WARNING: "警告",
          FAILED: "失败",
          NOT_APPLICABLE: "不适用",
        },
        checkMessages: {
          SOURCE_PERIOD_CLOSED_PASSED: "源期间已关账。",
          SOURCE_PERIOD_CLOSED_FAILED: "源期间尚未关账。",
          TARGET_PERIOD_OPEN_PASSED: "目标期间为开放状态。",
          TARGET_PERIOD_OPEN_FAILED: "目标期间不是开放状态。",
          SAME_ACCOUNTING_BOOK_PASSED: "源期间与目标期间属于同一账簿。",
          SAME_ACCOUNTING_BOOK_FAILED: "源期间与目标期间不属于同一账簿。",
          SAME_FISCAL_YEAR_PASSED: "源期间与目标期间属于同一会计年度。",
          SAME_FISCAL_YEAR_FAILED: "源期间与目标期间不属于同一会计年度。",
          PERIOD_ADJACENT_PASSED: "源期间与目标期间相邻。",
          PERIOD_ADJACENT_FAILED: "源期间与目标期间不相邻。",
          SOURCE_SNAPSHOT_EXISTS_PASSED: "源期间结账快照存在。",
          SOURCE_SNAPSHOT_EXISTS_FAILED: "源期间结账快照不存在。",
          SOURCE_SNAPSHOT_CHECKSUM_PASSED: "源期间快照校验和有效。",
          SOURCE_SNAPSHOT_CHECKSUM_FAILED: "源期间快照校验和无效。",
          SOURCE_SNAPSHOT_TOTAL_BALANCE_PASSED: "源期间快照借贷合计平衡。",
          SOURCE_SNAPSHOT_TOTAL_BALANCE_FAILED: "源期间快照借贷合计不平衡。",
          SOURCE_ACCOUNT_BALANCE_VALID_PASSED: "源期间科目余额有效。",
          SOURCE_ACCOUNT_BALANCE_VALID_FAILED: "源期间科目余额无效。",
          TARGET_NO_OPENING_BALANCE_PASSED: "目标期间尚无期初余额。",
          TARGET_NO_OPENING_BALANCE_FAILED: "目标期间已存在期初余额。",
          TARGET_NO_POSTED_JOURNALS_PASSED: "目标期间尚无已过账凭证。",
          TARGET_NO_POSTED_JOURNALS_FAILED: "目标期间已有已过账凭证。",
          TARGET_NO_CLOSE_REQUEST_PASSED: "目标期间无进行中的结账申请。",
          TARGET_NO_CLOSE_REQUEST_FAILED: "目标期间存在进行中的结账申请。",
          BOOK_ACTIVE_PASSED: "账簿处于有效状态。",
          BOOK_ACTIVE_FAILED: "账簿未处于有效状态。",
          REQUEST_VERSION_CURRENT_PASSED: "结转申请版本为最新。",
          REQUEST_VERSION_CURRENT_FAILED: "结转申请版本已过期。",
          ARCHIVED_ACCOUNTS_INCLUDED_PASSED: "源快照中无已归档科目。",
          ARCHIVED_ACCOUNTS_INCLUDED_WARNING: "源快照包含已归档科目。",
          ABNORMAL_ACCOUNT_BALANCE_PASSED: "未检测到异常科目余额。",
          ABNORMAL_ACCOUNT_BALANCE_WARNING: "检测到异常科目余额。",
          ZERO_BALANCE_ACCOUNTS_PASSED: "源快照中无零余额科目。",
          ZERO_BALANCE_ACCOUNTS_WARNING: "源快照包含零余额科目。",
        },
      },
      yearEndClose: {
        fiscalYear: "会计年度",
        title: "年度决算",
        create: "创建决算申请",
        validate: "运行校验",
        generate: "生成结转行",
        submit: "提交复核",
        cancel: "取消申请",
        retry: "重试过账",
        checksTitle: "决算校验项",
        snapshotTitle: "年度决算快照",
        snapshotGeneratedAt: "生成于 {value}",
        totalRevenue: "收入合计",
        totalExpense: "费用合计",
        profitBeforeTransfer: "结转前利润",
        summaryNone: "尚未创建年度决算申请。",
        summaryCompleted: "该会计年度已完成决算。",
        summaryNoPeriods: "当前账簿尚无该会计年度期间。",
        summaryPeriodsNotClosed:
          "尚有 {count} 个期间未关账，全部关账后方可决算。",
        summaryPolicyNotConfigured:
          "请先在账簿中配置当期纯利益与利益剰余金科目。",
        disclaimerNoNextFyOpening:
          "本阶段不自动生成次年度期初余额或跨年度结转。",
        disclaimerNoFinancialStatements: "本阶段不输出财务报表。",
        disclaimerNoReopen: "年度决算完成后不可重新打开或冲销决算分录。",
        progress: {
          validating: "正在运行决算校验…",
          approved: "复核已批准，即将开始过账…",
          postingQueued: "决算过账已入队，请稍候…",
          partiallyPosted: "损益结转已完成，正在处理利益剰余金振替…",
          completed: "年度决算已完成。",
        },
        status: {
          DRAFT: "草稿",
          VALIDATING: "校验中",
          VALIDATION_FAILED: "校验未通过",
          REVIEW_REQUIRED: "待复核",
          APPROVED: "已批准",
          POSTING_QUEUED: "过账排队中",
          PARTIALLY_POSTED: "部分已过账",
          COMPLETED: "已完成",
          REJECTED: "已拒绝",
          CANCELLED: "已取消",
          SUPERSEDED: "已被替代",
          FAILED: "失败",
        },
        checkStatus: {
          PASSED: "通过",
          WARNING: "警告",
          FAILED: "失败",
          NOT_APPLICABLE: "不适用",
        },
        lineGroups: {
          PROFIT_AND_LOSS_CLOSING: "损益结转",
          RETAINED_EARNINGS_TRANSFER: "利益剰余金振替",
        },
        lineColumns: {
          code: "科目代码",
          account: "科目名称",
          side: "借贷",
          amount: "金额",
        },
        snapshotColumns: {
          code: "科目代码",
          account: "科目名称",
          preCloseDebit: "决算前借方",
          preCloseCredit: "决算前贷方",
          postCloseDebit: "决算后借方",
          postCloseCredit: "决算后贷方",
          treatment: "决算处理",
        },
        treatment: {
          CARRIED_FORWARD: "结转",
          CLOSED_TO_PROFIT: "结转至当期纯利益",
          CURRENT_YEAR_PROFIT: "当期纯利益",
          RETAINED_EARNINGS: "利益剰余金",
          ZERO_BALANCE: "零余额",
        },
        checkMessages: {
          FINAL_PERIOD_EXISTS_PASSED: "最后一期期间存在。",
          FINAL_PERIOD_EXISTS_FAILED: "最后一期期间不存在。",
          FINAL_PERIOD_CLOSED_PASSED: "最后一期已关账。",
          FINAL_PERIOD_CLOSED_FAILED: "最后一期尚未关账。",
          ALL_PERIODS_CLOSED_PASSED: "全部期间已关账。",
          ALL_PERIODS_CLOSED_FAILED: "尚有期间未关账。",
          PERIOD_SEQUENCE_COMPLETE_PASSED: "期间序列完整。",
          PERIOD_SEQUENCE_COMPLETE_FAILED: "期间序列不完整。",
          FINAL_LEDGER_SNAPSHOT_EXISTS_PASSED: "最后一期结账快照存在。",
          FINAL_LEDGER_SNAPSHOT_EXISTS_FAILED: "最后一期结账快照不存在。",
          FINAL_LEDGER_SNAPSHOT_CHECKSUM_PASSED: "结账快照校验和有效。",
          FINAL_LEDGER_SNAPSHOT_CHECKSUM_FAILED: "结账快照校验和无效。",
          TEMPORARY_ACCOUNT_BALANCES_VALID_PASSED: "临时科目余额有效。",
          TEMPORARY_ACCOUNT_BALANCES_VALID_FAILED: "临时科目余额无效。",
          OPENING_BALANCE_CHAIN_COMPLETE_PASSED: "期初余额链条完整。",
          OPENING_BALANCE_CHAIN_COMPLETE_FAILED: "期初余额链条不完整。",
          CARRY_FORWARD_CHAIN_COMPLETE_PASSED: "期间结转链条完整。",
          CARRY_FORWARD_CHAIN_COMPLETE_FAILED: "期间结转链条不完整。",
          NO_POSTING_IN_PROGRESS_PASSED: "无进行中的过账请求。",
          NO_POSTING_IN_PROGRESS_FAILED: "存在进行中的过账请求。",
          NO_UNRESOLVED_POSTING_FAILURE_PASSED: "无未解决的过账失败。",
          NO_UNRESOLVED_POSTING_FAILURE_FAILED: "存在未解决的过账失败。",
          NO_PENDING_REVERSAL_PASSED: "无待处理的冲销申请。",
          NO_PENDING_REVERSAL_FAILED: "存在待处理的冲销申请。",
          NO_PENDING_AMENDMENT_PASSED: "无待处理的更正申请。",
          NO_PENDING_AMENDMENT_FAILED: "存在待处理的更正申请。",
          NO_ACTIVE_PERIOD_CLOSE_PASSED: "无进行中的期间结账申请。",
          NO_ACTIVE_PERIOD_CLOSE_FAILED: "存在进行中的期间结账申请。",
          NO_ACTIVE_CARRY_FORWARD_PASSED: "无进行中的期间结转申请。",
          NO_ACTIVE_CARRY_FORWARD_FAILED: "存在进行中的期间结转申请。",
          BOOK_ACTIVE_PASSED: "账簿处于有效状态。",
          BOOK_ACTIVE_FAILED: "账簿未处于有效状态。",
          YEAR_NOT_ALREADY_CLOSED_PASSED: "会计年度尚未决算。",
          YEAR_NOT_ALREADY_CLOSED_FAILED: "会计年度已完成决算。",
          YEAR_END_POLICY_CONFIGURED_PASSED: "年末决算政策已配置。",
          YEAR_END_POLICY_CONFIGURED_FAILED: "年末决算政策未配置。",
          YEAR_END_ACCOUNTS_VALID_PASSED: "决算科目配置有效。",
          YEAR_END_ACCOUNTS_VALID_FAILED: "决算科目配置无效。",
          GENERATED_LINES_BALANCED_PASSED: "生成的结转行借贷平衡。",
          GENERATED_LINES_BALANCED_FAILED: "生成的结转行借贷不平衡。",
          REQUEST_VERSION_CURRENT_PASSED: "决算申请版本为最新。",
          REQUEST_VERSION_CURRENT_FAILED: "决算申请版本已过期。",
          ARCHIVED_ACCOUNTS_INCLUDED_PASSED: "快照中无已归档科目。",
          ARCHIVED_ACCOUNTS_INCLUDED_WARNING: "快照包含已归档科目。",
          ABNORMAL_ACCOUNT_BALANCE_PASSED: "未检测到异常科目余额。",
          ABNORMAL_ACCOUNT_BALANCE_WARNING: "检测到异常科目余额。",
          UNCONFIRMED_JOURNAL_DRAFTS_PASSED: "无未确认的分录草稿。",
          UNCONFIRMED_JOURNAL_DRAFTS_WARNING: "存在未确认的分录草稿。",
          UNCONFIRMED_JOURNAL_DRAFTS_NOT_APPLICABLE:
            "未确认分录草稿检查不适用。",
          OPEN_REVIEWS_PASSED: "无未结复核案件。",
          OPEN_REVIEWS_WARNING: "存在未结复核案件。",
          OPEN_REVIEWS_NOT_APPLICABLE: "复核案件检查不适用。",
        },
      },
    },
    review: {
      staleWarning: "主体版本已过期",
      status: {
        DRAFT: "草稿",
        PENDING: "待处理",
        IN_REVIEW: "复核中",
        CHANGES_REQUESTED: "需修改",
        APPROVED: "已通过",
        REJECTED: "已拒绝",
        CANCELLED: "已取消",
        SUPERSEDED: "已被替代",
      },
      stepStatus: {},
      subjectType: {
        EXTRACTION: "字段提取",
        BUSINESS_TRANSACTION: "业务交易",
        ACCOUNTING_CLASSIFICATION: "会计分类",
        JOURNAL_DRAFT: "仕訳草稿",
      },
      actionType: {},
      actions: {
        submit: "提交",
        claim: "认领",
        approve: "通过",
        requestChanges: "要求修改",
        reject: "拒绝",
        cancel: "取消",
        reassign: "重新指派",
      },
      inbox: {
        title: "复核收件箱",
        subtitle: "待我处理的复核案件",
        backToDocuments: "返回票据",
        subject: "主体",
        type: "类型",
        status: "状态",
        step: "步骤",
        actions: "操作",
        empty: "当前没有待处理的复核案件。",
      },
    },
  },
  operator: {
    menuGroups: {
      healthManagement: "健康认定",
      overview: "总览",
      tenantOperations: "租户运营",
      commercial: "商业化",
      platformCapabilities: "平台能力",
      dataOperations: "数据与运维",
      system: "系统",
    },
    certificationStandards: "认定标准",
    certificationStandardsSubtitle: "标准版本、要求项与发布管理",
    overview: "运营总览",
    overviewSubtitle: "平台关键指标",
    ops: "运维看板",
    opsSubtitle: "进程健康、依赖连接与队列积压",
    tenants: "租户管理",
    tenantsSubtitle: "开通、编辑与功能开关",
    trialApplications: "试用申请",
    trialApplicationsSubtitle: "审核申请并开通试用租户",
    plans: "套餐管理",
    plansSubtitle: "配额与菜单功能配置",
    billing: "订阅账务",
    billingSubtitle: "套餐版本、订阅、账单与收款",
    billingEnhancements: "计费增强",
    billingEnhancementsSubtitle: "优惠券、套餐变更模拟与催收状态",
    featureFlags: "功能开关",
    featureFlagsSubtitle: "灰度开关、环境限制与定向覆盖",
    backups: "备份恢复",
    backupsSubtitle: "MySQL 加密备份与数据删除管理",
    reportDemo: "报表演示",
    reportDemoSubtitle: "使用测试数据生成 PDF 报表",
    featureDemo: "功能演示",
    importDemo: "导入演示",
    importDemoSubtitle: "批量导入/导出组件与批处理引擎演示",
    usage: "使用统计",
    usageSubtitle: "各租户用量与成本",
    apiPlatform: "API 开放平台",
    apiPlatformSubtitle: "租户 API Key 调用量与错误统计",
    apiRequestLogs: "API 调用日志",
    apiRequestLogsSubtitle: "第三方 API Key 请求记录",
    ai: "AI 管理",
    aiSubtitle: "模型、密钥、提示词与调用日志",
    userFeedbacks: "用户反馈",
    userFeedbacksSubtitle: "租户反馈列表与回复",
    notificationSend: "通知发送",
    notificationSendSubtitle: "向全体、租户或用户发送站内通知",
    notificationHistory: "历史通知",
    notificationHistorySubtitle: "查看已发送通知与内容预览",
    logs: "日志",
    auditLogs: "审计日志",
    auditLogsSubtitle: "跨租户操作记录",
    webhookLogs: "Webhook 日志",
    webhookLogsSubtitle: "投递记录与错误",
    reportLogs: "报表日志",
    reportLogsSubtitle: "PDF 报表出力记录",
    outbox: "Outbox 消息",
    outboxSubtitle: "异步投递消息明细、状态筛选与人工重试",
    emailLogs: "邮件日志",
    emailLogsSubtitle: "发送状态、内容与重试",
    emailTemplates: "邮件模板",
    emailTemplatesSubtitle: "共通布局与替换词管理",
    systemSettings: "系统设置",
    systemSettingsSubtitle: "平台全局配置",
  },
  operatorCertificationStandards: {
    title: "认定标准",
    description: "维护认定标准、版本来源和不可变发布内容",
    newStandard: "新建标准",
    empty: "尚未建立认定标准",
    requirements: "项要求",
    noRequirements: "当前版本尚无要求项",
    source: "版本来源",
    addRequirement: "添加要求项",
    publish: "发布",
    createRevision: "创建新修订",
    code: "标准编码",
    program: "认定项目",
    division: "认定分区",
    year: "认定年度",
    nameJa: "日文名称",
    nameZh: "中文名称",
    nameEn: "英文名称",
    stableKey: "稳定键",
    kind: "类型",
    parent: "上级要求",
    order: "顺序",
    titleJa: "日文标题",
    titleZh: "中文标题",
    titleEn: "英文标题",
    requirement: "要求项",
    createDescription: "建立标准主记录和首个草稿版本。",
    sourceDescription: "发布前必须记录官方来源 URL 和文件 SHA-256。",
    requirementDescription: "稳定键发布后不再变更，用于跨版本追踪。",
    status: { DRAFT: "草稿", PUBLISHED: "已发布", RETIRED: "已停用" },
    kinds: { section: "分组", question: "问题", criterion: "评价项" },
    messages: {
      created: "标准已创建",
      saved: "已保存",
      deleted: "要求项已删除",
      published: "版本已发布",
      revisionCreated: "新修订已创建",
      deleteConfirm: "删除要求项？",
      publishConfirm: "发布此版本？",
      publishImmutable: "发布后内容不可修改；后续变更必须创建新修订。",
    },
  },
  operatorFormat: {
    tenantStatuses: {
      ACTIVE: "启用",
      SUSPENDED: "暂停",
      DELETED: "已删除",
    },
    memberRoles: {
      ADMIN: "管理员",
      MANAGER: "经理",
      OPERATOR: "操作员",
      VIEWER: "只读",
      EMPLOYEE: "员工",
    },
  },
  auditActions: {
    CREATE: "创建",
    UPDATE: "更新",
    DELETE: "删除",
    ASSIGN: "分配",
    ARCHIVE: "归档",
    SEND: "发送",
    RETRY: "重试",
    TEST: "测试",
    PUBLISH: "发布",
    LOGIN: "登录",
    LOGIN_FAILED: "登录失败",
    SSO_LOGIN: "SSO 登录",
    SSO_LOGIN_FAILED: "SSO 登录失败",
    LOGOUT: "登出",
    MFA_VERIFY: "MFA 验证",
    UPDATE_PROFILE: "更新资料",
    UPDATE_AVATAR: "更新头像",
    CHANGE_PASSWORD: "修改密码",
    RECOVERY_CODES_REGENERATED: "重新生成恢复码",
    REVOKE_API_KEY: "吊销 API Key",
    PASSWORD_RESET_REQUESTED: "请求重置密码",
    PASSWORD_RESET_COMPLETED: "完成重置密码",
    USER_INVITED: "邀请用户",
    INVITATION_ACCEPTED: "接受邀请",
    ACCOUNT_UNLOCKED: "解锁账号",
    IMPERSONATE_START: "开始代运营",
    IMPERSONATE_END: "结束代运营",
    TRIAL_APPLICATION_SUBMITTED: "提交试用申请",
    TRIAL_APPLICATION_APPROVED: "通过试用申请",
    TRIAL_APPLICATION_REJECTED: "拒绝试用申请",
    transaction: {
      draft_created: "交易草稿已创建",
      validation_warning: "交易校验警告",
      updated: "交易已更新",
      confirmed: "交易已确认",
      rejected: "交易已拒绝",
      cancelled: "交易已取消",
      superseded: "交易已被替代",
      regenerated: "交易已重新生成",
    },
    accounting_classification: {
      pending: "等待会计分类",
    },
    accounting_book: {
      created: "会计账簿已创建",
      updated: "会计账簿已更新",
      archived: "会计账簿已归档",
    },
    account: {
      created: "科目已创建",
      updated: "科目已更新",
      archived: "科目已归档",
    },
    classification: {
      created: "分类已创建",
      suggested: "分类建议已生成",
      updated: "分类已更新",
      confirmed: "分类已确认",
      rejected: "分类已拒绝",
      superseded: "分类已被替代",
    },
    accounting: {
      classification: {
        requested: "会计分类已请求",
        started: "会计分类已开始",
        failed: "会计分类失败",
      },
      journal_draft: {
        requested: "仕訳草稿生成已请求",
      },
      posting: {
        requested_from_foundation: "Foundation 过账已请求",
        completed_from_foundation: "Foundation 过账已完成",
      },
    },
    journal_draft: {
      created: "仕訳草稿已创建",
      updated: "仕訳草稿已更新",
      confirmed: "仕訳草稿已确认",
      rejected: "仕訳草稿已拒绝",
      cancelled: "仕訳草稿已取消",
      superseded: "仕訳草稿已被替代",
    },
    posting: {
      pending: "等待未来过账阶段",
    },
    financial_report: {
      requested: "财务报表生成已请求",
      started: "财务报表生成已开始",
      completed: "财务报表生成已完成",
      failed: "财务报表生成失败",
      cancelled: "财务报表生成已取消",
      retried: "财务报表生成已重试",
    },
    financial_report_snapshot: {
      created: "财务报表快照已创建",
    },
    financial_report_mapping: {
      created: "科目报表映射已创建",
      updated: "科目报表映射已更新",
    },
    document_accounting_draft: {
      created: "票据会计草稿已创建",
      updated: "票据会计草稿已更新",
      validated: "票据会计草稿已校验",
      voided: "票据会计草稿已作废",
    },
    document_accounting_posting: {
      confirmed: "票据会计过账已确认",
      succeeded: "票据会计过账已成功",
      failed: "票据会计过账失败",
      retried: "票据会计过账已重试",
    },
    document_intake: {
      upload_session: { created: "票据收件上传会话已创建" },
      upload: { completed: "票据收件上传已完成" },
      original: { download_issued: "票据原件下载链接已签发" },
      reparse: { requested: "票据重新解析已请求" },
      review: { submitted: "票据复核已提交" },
      ownership: { assigned: "票据归属已分配" },
      duplicate: { marked: "票据已标记重复" },
      deletion: { requested: "票据删除已请求" },
      retention: { locked: "票据保留锁定已应用" },
      legal_hold: { applied: "票据法定留存已应用" },
      purged: "票据已清除",
    },
    document: {
      processing: {
        queued: "票据处理已入队",
        retry_requested: "票据处理重试已请求",
        exhausted: "票据处理重试已耗尽",
      },
      ocr: {
        started: "票据 OCR 已开始",
        completed: "票据 OCR 已完成",
        failed: "票据 OCR 失败",
      },
      ai_extraction: {
        started: "票据 AI 提取已开始",
        completed: "票据 AI 提取已完成",
        failed: "票据 AI 提取失败",
      },
      review_candidate: {
        created: "票据复核候选已创建",
      },
      accounting_bridge: {
        requested: "票据会计桥接已请求",
        started: "票据会计桥接已开始",
        succeeded: "票据会计桥接已成功",
        failed: "票据会计桥接失败",
        ineligible: "票据会计桥接不符合条件",
        cancelled: "票据会计桥接已取消",
        amendment_required: "票据会计桥接需修订",
      },
    },
    business_transaction: {
      created_from_document_review: "由票据复核创建业务交易",
    },
    ar_ap: {
      obligation_created: "应收应付义务已创建",
      obligation_updated: "应收应付义务已更新",
      obligation_opened: "应收应付义务已确认打开",
      obligation_cancelled: "应收应付义务已取消",
      obligation_superseded: "应收应付义务已被替代",
      obligation_settled: "应收应付义务已结清",
      obligation_reopened: "应收应付义务已重新打开",
      opening_import_batch_created: "期初应收应付导入批次已创建",
      opening_import_batch_validated: "期初应收应付导入批次已校验",
      opening_import_batch_executed: "期初应收应付导入批次已开始执行",
      opening_import_batch_completed: "期初应收应付导入批次已完成",
      opening_import_batch_failed: "期初应收应付导入批次失败",
      opening_import_batch_cancelled: "期初应收应付导入批次已取消",
    },
    payment: {
      created: "付款已创建",
      updated: "付款已更新",
      confirmed: "付款已确认（运营事实）",
      cancelled: "付款已取消",
    },
    bank_match: {
      payment_draft_created: "银行匹配入出金草稿已创建",
    },
    bank_evidence: {
      linked: "银行明细×传票核对已关联",
      excluded: "银行明细×传票核对已排除",
      unlinked: "银行明细×传票核对已解除",
      candidates_refreshed: "银行明细×传票核对候选已刷新",
      feature_disabled: "银行明细×传票核对功能已禁用",
    },
    settlement: {
      allocation_created: "结算分配已创建",
      allocation_voided: "结算分配已作废",
      accounting_date_resolved: "结算会计日期已解析",
      accounting_transaction_created: "结算会计交易已创建",
      accounting_linked: "结算会计链接已建立",
      accounting_orchestration_requested: "结算会计编排已请求",
      accounting_orchestration_completed: "结算会计编排已完成",
      accounting_orchestration_failed: "结算会计编排失败",
      accounting_classification_ensured: "结算分类建议已确保",
      accounting_journal_ensured: "结算分录草稿已确保",
      accounting_posting_requested: "结算会计过账已请求",
      accounting_orchestration_waiting: "结算会计编排等待中",
      accounting_orchestration_recovered: "结算会计编排已恢复",
    },
    line_webhook: {
      received: "LINE Webhook 已接收",
      queued: "LINE Webhook 已入队",
    },
    line_media: {
      fetch_started: "LINE 媒体获取已开始",
      fetch_succeeded: "LINE 媒体获取成功",
      fetch_failed: "LINE 媒体获取失败",
    },
    line_document_intake: {
      submitted: "LINE 票据收件已提交",
      duplicate_suppressed: "LINE 重复收件已抑制",
    },
    line: {
      account_link: {
        token_issued: "LINE 账号绑定令牌已签发",
        nonce_created: "LINE 账号绑定 nonce 已创建",
        completed: "LINE 账号绑定已完成",
        failed: "LINE 账号绑定失败",
        conflict: "LINE 账号绑定冲突",
      },
      account_unlinked: "LINE 账号绑定已解除",
      document: {
        received: "LINE 票据已接收",
        binding_rejected: "LINE 票据绑定校验已拒绝",
        download_started: "LINE 票据下载已开始",
        download_failed: "LINE 票据下载失败",
        storage_completed: "LINE 票据存储已完成",
        intake_created: "LINE 票据收件已创建",
        processing_enqueued: "LINE 票据处理已入队",
        duplicate_ignored: "LINE 重复票据已忽略",
        rejected: "LINE 票据已拒绝",
      },
      production_gate: { changed: "LINE 生产门禁已变更" },
    },
    financial_report_export: {
      requested: "财务报表导出已请求",
      queued: "财务报表导出已入队",
      started: "财务报表导出已开始",
      completed: "财务报表导出已完成",
      failed: "财务报表导出失败",
      retried: "财务报表导出已重试",
      cancelled: "财务报表导出已取消",
      expired: "财务报表导出已过期",
      download_url_issued: "财务报表导出下载链接已签发",
      artifact_deleted: "财务报表导出文件已删除",
      orphan_cleanup_failed: "财务报表导出孤儿文件清理失败",
    },
    HTTP_ERROR: "HTTP 错误",
    REQUEST_PERF: "请求性能",
    AUDIT_DB_WRITE_FAILED: "审计写入失败",
    LOGIN_ATTEMPT_WRITE_FAILED: "登录尝试写入失败",
  },
  operatorAuditLogs: {
    filters: {
      tenantName: "租户名",
      userName: "用户名/邮箱",
      allActions: "全部操作",
    },
    columns: {
      time: "时间",
      tenant: "租户",
      user: "用户",
      action: "操作",
      resource: "资源",
      details: "详情",
    },
  },
  operatorFeatureChecklist: {
    menuPermissions: "菜单权限",
    featureToggles: "功能开关",
    features: {
      apiKeys: "API Key 管理",
      mfa: "多因素认证",
      dataExport: "数据导出",
      aiGateway: "AI 网关调用",
    },
  },
  operatorUsage: {
    refreshStats: "刷新统计",
    filters: {
      dateRange: "日期范围",
      tenant: "租户",
      tenantRequired: "请选择租户",
    },
    units: "资源用量",
    apiCalls: "API 调用",
    storage: "存储",
    cost: "推计成本",
    documentCount: "伝票数",
    aiRequestCount: "AI 请求次数",
    inputTokens: "Token 入力",
    outputTokens: "Token 出力",
    planLabel: "プラン：{name}",
    planComparison: "与プラン对比",
    planComparisonHint: "进度条显示 used / limit；≥80% 警告，≥100% 超限。",
    noPlanLimit: "无プラン上限",
    costEstimateHint: "基于 Gemini トークン単価的推计",
    storageSnapshotHint: "全量快照（非期间增量）",
    unlimited: "无制限",
    selectTenant: "请选择租户",
    selectTenantDescription: "选择租户后加载该期间的利用状況与プラン对比。",
    empty: "暂无用量数据",
    emptyDescription: "该租户在所选日期范围内尚无用量记录。",
    notes: {
      noPlanLimitMetrics: "伝票数与 Token 为观测指标，プラン暂无上限。",
      costEstimate: "コスト为基于 Gemini トークン単価的推计（USD）。",
      storageSnapshot: "ストレージ为 stored_object 全量快照，非期间增量。",
    },
    messages: {
      loadFailed: "无法加载用量统计",
      loadFailedDescription: "请检查登录状态或网络后重试。",
    },
  },
  operatorApiPlatform: {
    refresh: "刷新统计",
    viewLogs: "查看调用日志",
    openDocs: "打开 API 文档",
    requestCount: "请求数",
    activeApiKeys: "活跃 Key",
    errorCount: "错误数",
    avgDuration: "平均耗时",
    apiKeyFilter: "API Key ID",
    apiKeyColumn: "API Key",
    methodColumn: "方法",
    pathColumn: "路径",
    durationColumn: "耗时",
  },
  apiDocs: {
    title: "API 文档",
    subtitle: "基于 OpenAPI 的接口说明与示例",
    backToLogin: "返回登录",
  },
  apiScopes: {
    webhooks: {
      read: "Webhook 读取",
      write: "Webhook 写入",
      delete: "Webhook 删除",
    },
    batch: {
      read: "批量任务读取",
      write: "批量任务写入",
    },
  },
  operatorUserFeedbacks: {
    filters: {
      tenantName: "租户名",
      allSubjects: "全部主题",
    },
    columns: {
      time: "时间",
      tenant: "租户",
      user: "用户",
      subject: "主题",
      status: "状态",
      summary: "摘要",
      actions: "操作",
    },
    actions: {
      details: "详情",
    },
    detail: {
      title: "反馈详情",
      description: "查看反馈内容、调整状态并回复用户。",
      tenant: "租户",
      user: "用户",
      email: "邮箱",
      subject: "主题",
      status: "状态",
    },
    reply: {
      placeholder: "回复用户...",
      sending: "发送中...",
      send: "发送回复",
    },
    messages: {
      loadDetailFailed: "加载详情失败",
      statusUpdated: "状态已更新",
      updateFailed: "更新失败",
      replyRequired: "请填写回复内容",
      replySent: "回复已发送",
      sendFailed: "发送失败",
    },
  },
  operatorNotifications: {
    form: {
      title: "发送站内通知",
      description:
        "向全体活跃用户、指定租户或指定用户发送通知，用户可在通知中心查看。",
    },
    fields: {
      audience: "发送范围",
      tenant: "目标租户",
      user: "目标用户",
      severity: "重要程度",
      title: "通知标题",
      body: "通知内容",
      actionUrl: "跳转链接",
    },
    audience: {
      all: "全体用户",
      tenant: "指定租户",
      user: "指定用户",
    },
    placeholders: {
      tenant: "选择租户",
      user: "选择用户",
      title: "例如：系统维护通知",
      actionUrl: "/billing",
    },
    hints: {
      actionUrl: "可选。填写站内路径（如 /billing）或完整 URL。",
    },
    actions: {
      send: "发送通知",
      sending: "发送中...",
    },
    validation: {
      titleRequired: "请填写通知标题",
      tenantRequired: "请选择目标租户",
      userRequired: "请选择目标用户",
    },
    toast: {
      sent: "已成功发送 {count} 条通知",
      failed: "发送失败，请稍后重试",
    },
  },
  operatorNotificationHistory: {
    filters: {
      allAudiences: "全部范围",
    },
    columns: {
      sentAt: "发送时间",
      audience: "发送范围",
      title: "标题",
      recipients: "送达人数",
      severity: "重要程度",
      status: "状态",
      actions: "操作",
    },
    status: {
      active: "有效",
      revoked: "已撤销",
    },
    actions: {
      preview: "预览",
      revoke: "撤销",
      delete: "彻底删除",
    },
    confirm: {
      revokeTitle: "撤销这条通知？",
      revokeDescription:
        "撤销后，用户将不再看到这条通知，但运营侧历史记录会保留。",
      deleteTitle: "彻底删除这条通知？",
      deleteDescription: "删除后无法恢复，所有相关用户通知记录也会被清除。",
    },
    messages: {
      revoked: "通知已撤销",
      deleted: "通知已删除",
      revokeFailed: "撤销失败",
      deleteFailed: "删除失败",
    },
    previewDescription: "查看已发送通知的完整内容。",
  },
  notificationDetail: {
    title: "通知详情",
    emptyBody: "暂无正文内容",
  },
  operatorLogPages: {
    filters: {
      tenantName: "租户名",
    },
    columns: {
      time: "时间",
      tenant: "租户",
      event: "事件",
      statusCode: "状态码",
      error: "错误",
    },
  },
  operatorEmailLogs: {
    filters: {
      allStatuses: "全部状态",
    },
    columns: {
      recipient: "收件人",
      subject: "主题",
      status: "状态",
      actions: "操作",
    },
    actions: {
      details: "详情",
      retry: "重试",
    },
    statuses: {
      QUEUED: "排队中",
      SENDING: "发送中",
      SENT: "已发送",
      FAILED: "失败",
    },
    detail: {
      title: "邮件详情",
      recipient: "收件人",
      tenant: "租户",
      status: "状态",
      subject: "主题",
      template: "模板",
      attempts: "尝试次数",
    },
    messages: {
      retryQueued: "已重新加入发送队列",
    },
  },
  operatorOutbox: {
    filters: {
      allStatuses: "全部状态",
      allChannels: "全部通道",
      tenantId: "租户 ID",
    },
    columns: {
      time: "时间",
      status: "状态",
      channel: "通道",
      eventType: "事件类型",
      tenantId: "租户 ID",
      attempts: "尝试次数",
      lastError: "最近错误",
      actions: "操作",
    },
    actions: {
      retry: "重试",
      retrying: "重试中…",
    },
    statuses: {
      PENDING: "待投递",
      PROCESSING: "处理中",
      COMPLETED: "已完成",
      DEAD: "死信",
    },
    channels: {
      audit: "审计",
      webhook: "Webhook",
      email: "邮件",
      notification: "通知",
      ai_call_log: "AI 调用日志",
      report_log: "报表日志",
      batch_event: "批处理事件",
    },
    messages: {
      retried: "已重新加入投递队列",
      retryFailed: "重试 Outbox 消息失败",
    },
  },
  operatorReports: {
    demo: {
      title: "报表出力演示",
      subtitle: "使用多语言文本、日期、金额、表格和嵌套 JSON 生成测试 PDF。",
      printButton: "出力测试报表",
      previewTitle: "测试数据预览",
      previewHint:
        "页面仅展示摘要；PDF 将包含 {images} 张图片与 {rows} 行明细，并自动分页。",
    },
    confirm: {
      title: "确认出力报表",
      description: "系统将生成一份测试 PDF，并在当前页面弹窗中预览。",
      submit: "确认出力",
    },
    preview: {
      title: "报表预览",
      description: "可翻页、缩放、下载或打印；所有页面在弹窗内连续展示。",
    },
    messages: {
      generating: "正在生成报表...",
      printStarted: "报表已生成",
    },
  },
  operatorImportDemo: {
    title: "批量导入演示",
    subtitle: "上传 CSV/Excel，预览校验后提交批处理导入，并支持列选择导出。",
    importButton: "批量导入",
    exportButton: "导出数据",
    requiredColumnsHint: "必填列：{columns}",
    recordsTitle: "导入成功记录",
    resultsTitle: "导入结果明细",
    resultsHint: "完成导入后点击「加载明细」查看行级成功/失败。",
    loadResults: "加载明细",
    importDialog: {
      title: "批量导入",
      description: "选择 CSV 或 Excel 文件，确认预览后提交导入。",
      dropHint: "拖拽文件到此处，或点击下方按钮选择文件",
      chooseFile: "选择文件",
      previewTitle: "数据预览",
      previewCount: "展示前 {count} 行",
      progress: "导入进度",
      missingColumns: "缺少必填列",
      submit: "确认导入",
    },
    exportDialog: {
      title: "导出数据",
      description: "选择要导出的列与格式。",
      columns: "导出列",
      selectAll: "全选",
      deselectAll: "全取消",
      csv: "导出 CSV",
      xlsx: "导出 Excel",
    },
    resultTable: {
      rowNo: "行号",
      status: "状态",
      error: "错误信息",
      success: "成功",
      fail: "失败",
      empty: "暂无结果",
    },
  },
  operatorReportLogs: {
    filters: {
      templateName: "模板名",
      actor: "出力人",
      allStatuses: "全部状态",
    },
    columns: {
      template: "模板",
      fileName: "文件名",
      status: "状态",
      actor: "出力人",
      locale: "语言",
      duration: "耗时",
      size: "大小",
      actions: "操作",
    },
    status: {
      PENDING: "处理中",
      SUCCEEDED: "成功",
      FAILED: "失败",
    },
    detail: {
      title: "报表日志详情",
      dataSnapshot: "数据快照",
      downloadReprint: "下载重出报表",
      reprintLoading: "正在重出报表...",
      reprintUnavailable: "仅成功且数据快照完整的日志可重出下载",
    },
    reprintConfirm: {
      title: "确认重出报表",
      description: "系统将基于该日志的数据快照重新出力 PDF，并在窗口中预览。",
      submit: "确认重出",
    },
    reprintPreview: {
      title: "重出报表预览",
      description: "可翻页、缩放、下载或打印；所有页面在弹窗内连续展示。",
    },
    messages: {
      reprintStarted: "重出报表已生成",
    },
  },
  operatorEmailTemplates: {
    testEmail: {
      label: "测试邮件",
      send: "发送测试邮件",
      hint: "邮件经队列异步发送，可在「邮件日志」查看渲染内容与最终状态。",
      recipientRequired: "请输入收件邮箱",
      queued: "已加入发送队列",
      queuedWithId: "已加入发送队列（{logId}）",
      sendFailed: "发送失败",
    },
    actions: {
      newTemplate: "新建模板",
      preview: "预览",
      edit: "编辑",
      add: "添加",
      delete: "删除",
      cancel: "取消",
      save: "保存",
    },
    dialog: {
      createTitle: "新建模板",
      editTitle: "编辑模板",
      previewTitle: "模板预览",
      formDescription: "配置模板基本信息、正文内容与替换词。",
      previewDescription: "查看模板渲染后的邮件效果。",
    },
    tabs: {
      basic: "基本信息",
      body: "正文",
      placeholders: "替换词",
    },
    themes: {
      success: "成功",
      warning: "警告",
      error: "错误",
      info: "信息",
    },
    fields: {
      language: "语言",
      name: "名称",
      subject: "邮件主题",
      theme: "主题色",
      headerTitle: "页眉标题",
      headerSubtitle: "页眉副标题",
      greeting: "问候语",
      introText: "引导文案",
      noticeText: "提示框文案",
      bodyHtml: "正文 HTML",
      customPlaceholders: "自定义替换词",
      displayName: "展示名",
      exampleValue: "示例值",
    },
    placeholders: {
      greeting: "{tenantPlaceholder} 您好",
      customLabel: "订单号",
      emptyCustom:
        "暂无自定义替换词。添加后发送时传入对应 variables 即可完成替换。",
      requiredOnSend: "发送时必填",
      tagList: "替换词（点击复制，粘贴到左侧字段）",
      livePreview: "实时预览",
      previewLoading: "预览加载中...",
      previewEmpty: "填写标题与主题后可预览",
    },
    locales: {
      zh_CN: "中文",
      ja_JP: "日语",
      en_US: "英语",
    },
    previewExamples: {
      loginAccount: "登录账号",
      loginTime: "登录时间",
    },
    validation: {
      keyRequired: "请输入模板 Key",
      nameRequired: "请输入模板名称",
      subjectRequired: "请输入邮件主题",
      titleRequired: "请输入页眉标题",
      placeholderKeyRequired: "自定义替换词 Key 不能为空",
      placeholderKeyInvalid: "替换词 Key 格式无效: {key}",
      placeholderKeyBuiltinConflict: "替换词 Key 与内置冲突: {key}",
      placeholderKeyDuplicate: "替换词 Key 重复: {key}",
    },
    messages: {
      updated: "模板已更新",
      created: "模板已创建",
      saveFailed: "保存失败",
    },
  },
  operatorPlans: {
    actions: {
      newPlan: "新建配额方案",
    },
    dialog: {
      createTitle: "新建配额方案",
      editTitle: "编辑配额方案",
      formDescription: "配置配额上限、套餐类型与菜单功能权限。",
    },
    fields: {
      name: "方案名称",
      description: "描述",
      planType: "套餐类型",
      maxUnits: "最大资源用量",
      maxApiCalls: "最大 API 调用",
      maxStorageMb: "最大存储空间 (MB)",
    },
    planTypes: {
      STANDARD: "正式套餐",
      TRIAL: "试用套餐",
    },
    statuses: {
      enabled: "启用",
      disabled: "停用",
    },
    card: {
      units: "资源用量",
      apiCalls: "API 调用",
      storage: "存储",
      menus: "菜单",
      tenants: "租户",
      defaultAll: "默认全量",
    },
    empty: {
      noDescription: "暂无描述",
    },
    hints: {
      blankUnlimited: "配额留空表示不限。",
    },
    errors: {
      nameRequired: "请输入配额方案名称",
    },
    unlimited: "不限",
    delete: {
      title: "删除配额方案",
      description: "删除后已绑定租户会解除该方案。",
    },
    messages: {
      saved: "配额方案已保存",
      created: "配额方案已创建",
      saveFailed: "保存配额方案失败",
      deleted: "配额方案已删除",
      deleteFailed: "删除配额方案失败",
    },
  },
  operatorTenantHealth: {
    title: "健康",
    description: "租户健康概览：用量、订阅、催收、反馈与 Outbox 状态。",
    evaluatedAt: "评估时间",
    statuses: {
      healthy: "健康",
      warning: "告警",
      critical: "严重",
      unknown: "未知",
    },
    alerts: {
      title: "异常项",
      none: "无异常",
    },
    subscription: {
      title: "订阅",
      status: "状态",
      plan: "套餐",
      periodEnd: "到期时间",
    },
    usage: {
      title: "用量",
      units: "单位",
      apiCalls: "API 调用",
      storageMb: "存储 (MB)",
    },
    dunning: {
      title: "催收",
    },
    feedback: {
      unread: "未读反馈",
    },
    outbox: {
      dead: "死信消息",
    },
  },
  operatorTenants: {
    filters: {
      searchPlaceholder: "搜索租户名称或标识",
      allStatuses: "全部状态",
    },
    columns: {
      tenant: "租户",
      status: "状态",
      quotaPlan: "配额方案",
      actions: "操作",
      role: "角色",
    },
    statuses: {
      ACTIVE: "启用",
      SUSPENDED: "暂停",
      DELETED: "已删除",
    },
    userStatuses: {
      ACTIVE: "启用",
      INACTIVE: "停用",
      DELETED: "已删除",
    },
    quotaPlan: {
      unset: "未设置",
    },
    actions: {
      openTenant: "开通租户",
      accounts: "账号",
      features: "功能",
      impersonate: "代运营",
      open: "开通",
      addAccount: "新增账号",
      resetPassword: "重置密码",
      suspendAccount: "停用账号",
      activateAccount: "启用账号",
    },
    impersonate: {
      failed: "进入代运营模式失败",
      success: "已进入代运营模式",
      inactiveTenant: "仅可对启用中的租户代运营",
    },
    dialog: {
      openTenant: "开通租户",
      editTenant: "编辑租户",
      openTenantDescription: "填写租户基本信息并创建首个管理员账号。",
      editTenantDescription: "修改租户名称、状态、配额方案与品牌设置。",
      features: "功能开关",
      featuresFor: "{name} · 功能开关",
      featuresDescription: "为租户配置可访问的菜单与功能开关。",
      accounts: "账号管理",
      accountsFor: "{name} · 账号管理",
      accountsDescription: "管理租户成员账号，可新增账号并查看现有成员。",
    },
    fields: {
      tenantName: "租户名称",
      tenantSlug: "租户标识",
      adminEmail: "管理员邮箱",
      adminPassword: "管理员密码",
      email: "邮箱",
      name: "姓名",
      initialPassword: "初始密码",
      newPassword: "新密码",
      logo: "侧边栏 Logo",
    },
    logo: {
      upload: "上传 Logo",
      uploading: "上传中...",
      reset: "恢复默认",
      cropTitle: "裁剪 Logo",
      confirmCrop: "确认裁剪",
      zoom: "缩放",
      hint: "支持 PNG、JPEG、WebP、SVG，最大 2MB。位图将裁剪为正方形，SVG 将原样上传。",
      saveTenantFirst: "请先保存租户后再上传 Logo。",
      invalidType: "仅支持 PNG、JPEG、WebP、SVG 格式",
      tooLarge: "图片大小不能超过 2MB",
      invalidImage: "无法读取该图片，请确认文件未损坏且格式正确",
      emptyFile: "文件为空，请选择有效图片",
      uploadFailed: "Logo 上传失败",
      uploaded: "Logo 已上传，保存租户后生效",
    },
    errors: {
      nameRequired: "请输入租户名称",
      slugRequired: "请输入租户标识",
      adminEmailRequired: "请输入管理员邮箱",
      adminPasswordRequired: "请输入管理员密码",
      emailRequired: "请输入邮箱",
      userNameRequired: "请输入姓名",
      initialPasswordRequired: "请输入初始密码",
      roleRequired: "请选择角色",
      passwordMinLength: "密码至少 8 位",
    },
    messages: {
      tenantSaved: "租户已保存",
      tenantOpened: "租户已开通",
      tenantSaveFailed: "保存租户失败",
      featuresUpdated: "租户功能已更新",
      featuresSaveFailed: "保存功能配置失败",
      accountCreated: "账号已创建",
      accountCreateFailed: "创建账号失败",
      passwordReset: "密码已重置",
      passwordResetFailed: "重置密码失败",
      accountSuspended: "账号已停用",
      accountActivated: "账号已启用",
      accountStatusFailed: "更新账号状态失败",
    },
    confirm: {
      suspendAccountTitle: "停用这个账号？",
      suspendAccountDescription: "{name} 将无法登录。",
    },
  },
  operatorFeatureFlags: {
    filters: {
      includeArchived: "包含已归档",
      environmentAll: "全部环境",
      environmentUnrestricted: "无环境限制",
      statusAll: "全部状态",
    },
    columns: {
      key: "Key",
      name: "名称",
      status: "状态",
      defaultValue: "默认值",
      rollout: "灰度比例",
      environments: "环境限制",
      overrides: "覆盖数",
      actions: "操作",
    },
    overrideColumns: {
      scopeType: "覆盖范围",
      scopeId: "对象 ID",
      value: "结果",
      reason: "原因",
      actions: "操作",
    },
    actions: {
      create: "创建功能开关",
      overrides: "覆盖管理",
      archive: "归档",
      evaluate: "评估调试",
      runEvaluate: "运行评估",
      evaluating: "评估中…",
    },
    evaluate: {
      title: "评估调试",
      description:
        "模拟指定租户/用户与环境下的功能开关判定结果，不会修改配置。",
      loadHint:
        "填写参数后点击「运行评估」查看结果（不会在打开页面时自动请求）。",
      reason: "判定原因",
      rolloutBucket: "灰度桶",
      flagId: "开关 ID",
    },
    evaluateReasons: {
      not_found: "未找到",
      archived: "已归档",
      disabled: "已禁用",
      environment: "环境不匹配",
      user_override: "用户覆盖",
      tenant_override: "租户覆盖",
      default: "默认值",
      rollout: "百分比灰度",
    },
    dialog: {
      createTitle: "新建功能开关",
      createDescription:
        "配置灰度开关、默认值、环境限制与发布比例。名称与说明默认使用日语填写。",
      editTitle: "编辑功能开关",
      editDescription: "更新开关行为，不影响「租户功能菜单」配置。",
    },
    overrideDialog: {
      title: "覆盖管理 · {key}",
      description: "按租户或用户定向覆盖功能开关的生效结果。",
    },
    fields: {
      key: "开关 Key",
      name: "名称",
      namePlaceholder: "例：銀行証憑リンク",
      nameLocaleHint: "名称与说明默认使用日语填写。",
      description: "说明",
      descriptionPlaceholder:
        "例：銀行明細と証憑候補の紐付け・確認を有効化する。",
      rolloutPercentage: "灰度比例 (0-100)",
      environments: "环境限制",
      environmentsPlaceholder: "例如：production, staging",
      isEnabled: "启用该 Flag",
      defaultValue: "默认值为启用",
      scopeType: "覆盖范围",
      scopeId: "对象 ID（UUID）",
      scopeIdPlaceholder: "输入租户或用户 UUID",
      overrideEnabled: "覆盖结果为启用",
      overrideReason: "覆盖原因",
      evaluateEnvironment: "环境",
      evaluateEnvironmentPlaceholder: "例如：production",
      evaluateTenantId: "租户 ID（可选）",
      evaluateUserId: "用户 ID（可选）",
    },
    scope: {
      tenant: "租户",
      user: "用户",
    },
    status: {
      enabled: "已启用",
      disabled: "已禁用",
      archived: "已归档",
    },
    empty: "暂无功能开关",
    emptyDescription:
      "创建第一个运行时开关后，可在这里统一管理灰度、默认值和覆盖规则。",
    overrideEmpty: "暂无覆盖规则",
    errors: {
      keyRequired: "请输入开关 Key",
      nameRequired: "请输入名称",
      rolloutRange: "灰度比例需在 0 到 100 之间",
      scopeIdRequired: "请输入对象 ID",
    },
    confirm: {
      archiveTitle: "归档功能开关「{key}」？",
      archiveDescription: "归档后该 Flag 将不再参与评估，但历史记录会保留。",
    },
    messages: {
      loadFailed: "无法加载功能开关",
      loadFailedDescription: "请检查登录状态或网络连接后重试。",
      created: "功能开关已创建",
      updated: "功能开关已更新",
      saveFailed: "保存功能开关失败",
      archived: "功能开关已归档",
      archiveFailed: "归档功能开关失败",
      overrideSaved: "覆盖规则已保存",
      overrideSaveFailed: "保存覆盖规则失败",
      overrideCleared: "覆盖规则已删除",
      overrideClearFailed: "删除覆盖规则失败",
      evaluateFailed: "评估功能开关失败",
    },
  },
  operatorOverview: {
    header: {
      thisMonth: "本月 {range}",
      rangeSeparator: "至",
    },
    metrics: {
      tenantTotal: "租户总数",
      tenantHelper: "{active} 个启用，{suspended} 个暂停",
      monthlyUnits: "本月资源用量",
      unitsHelper: "累计 {total} 个资源",
      monthlyApiCalls: "本月 API 调用",
      apiCallsHelper: "ストレージ快照 {storage}",
      monthlyCost: "推计成本",
      costHelper: "平均 {average} / AI 次 · Gemini トークン単価",
      documents: "伝票数",
      documentsHelper: "本月创建（UTC）",
      aiRequests: "AI 请求次数",
      aiRequestsHelper: "本月 ai_call_log（UTC）",
      tokens: "Token",
      tokensHelper: "输入 {input} / 输出 {output}",
      feedbackHelper: "{unread} 条未读，本月 {month} 条",
    },
    health: {
      title: "平台健康",
      description: "租户状态、API 投递与邮件发送",
      activeTenantRate: "租户启用率",
      activeTenantDetail: "{active} / {total} 个租户启用",
      apiUsage: "API 调用",
      emailSending: "邮件发送",
      recentFailures: "{count} 条近期失败",
      loadHint: "请先加载近期风险，再检查投递失败情况",
    },
    feedback: {
      title: "用户反馈",
      summary: "{open} 待处理 · {unread} 条未读",
      total: "累计反馈",
      monthlyNew: "本月新增",
      open: "待处理",
      noUnread: "暂无未读反馈",
    },
    quick: {
      title: "快速入口",
      description: "常用运营动作",
      openTenant: "开通租户",
      userFeedback: "用户反馈",
      configurePlans: "配置套餐",
      viewUsage: "查看用量",
      auditLogs: "审计日志",
      emailTemplates: "邮件模板",
      webhookLogs: "Webhook日志",
    },
    usage: {
      title: "本月用量排行",
      description: "按成本排序的租户消耗",
      empty: "暂无本月用量",
    },
    risks: {
      title: "近期风险",
      description: "Webhook 与邮件异常",
      itemCount: "{count} 项",
      empty: "近期没有异常日志",
      loadHint: "近期 Webhook 与邮件异常仅在显式操作后加载，避免占用首屏请求。",
      webhookType: "Webhook",
      emailType: "邮件",
      emailFailed: "发送失败",
    },
    focus: {
      title: "运营关注点",
      description: "当前平台需要优先关注的指标",
      planCoverage: "套餐覆盖",
      planCoverageDetail: "{covered} / {total} 个租户已绑定套餐",
      costConcentration: "成本集中度",
      costConcentrationDetail: "{tenant} 占本月成本 {percent}",
      noCostRecords: "暂无成本记录",
      feedbackDetail: "{unread} 条未读，{open} 条待处理",
      incidentHandling: "异常处理",
      incidentDetail: "{count} 条近期异常需要排查",
      incidentPendingDetail: "请先加载近期风险，再评估异常处理状态。",
    },
    units: {
      apiCallsWithCount: "{count} 次 API 调用",
      unitsWithCount: "{count} 个资源",
      documentsWithCount: "{count} 张伝票",
      tokensWithCount: "{count} tokens",
    },
    badges: {
      normal: "正常",
      watch: "关注",
      pending: "待处理",
      incomplete: "待补齐",
      notLoaded: "未加载",
    },
    actions: {
      loadRisks: "加载近期风险",
    },
    fallback: {
      unknownTenant: "未知租户",
      event: "事件",
      noResponse: "无响应",
      platform: "平台",
      recipient: "收件人",
    },
    messages: {
      loadFailed: "运营总览加载失败",
    },
  },
  operatorOps: {
    header: {
      generatedAt: "数据更新时间：{time}",
    },
    processes: {
      api: "API 进程",
      worker: "Worker 进程",
      scheduler: "Scheduler 进程",
      lastBeat: "最近心跳 {time}",
      noHeartbeat: "暂无心跳",
    },
    dependencies: {
      title: "依赖连接",
      description: "数据库、缓存、对象存储与队列连接状态（不含敏感配置）",
      mysql: "MySQL",
      redis: "Redis",
      objectStorage: "对象存储",
      queue: "任务队列",
      latency: "延迟 {ms} ms",
      provider: "提供方 {provider}",
    },
    queues: {
      title: "队列积压",
      description: "各队列等待、执行中与失败任务数量",
      waiting: "等待 {count}",
      active: "执行 {count}",
      failed: "失败 {count}",
      empty: "暂无队列数据",
    },
    outbox: {
      title: "Outbox 状态",
      description: "异步消息投递积压概览",
      pending: "待投递",
      processing: "处理中",
      completed: "已完成",
      dead: "死信",
    },
    failedJobs: {
      title: "失败任务",
      description: "各队列最近失败任务摘要",
      empty: "暂无失败任务",
    },
    recentErrors: {
      title: "最近错误",
      description: "近 24 小时 HTTP 错误摘要",
      empty: "暂无错误记录",
    },
    slowEndpoints: {
      title: "慢接口摘要",
      description: "API Key 请求中 DB 耗时或查询次数偏高的接口",
      summary: "{count} 次请求 · 平均 DB {avgDb} ms · 平均查询 {avgQueries} 次",
      empty: "暂无慢接口",
    },
    highErrorEndpoints: {
      title: "高错误率接口",
      description: "近 24 小时错误率 ≥ 10% 的 API Key 接口",
      summary: "{count} 次请求 · {errors} 次错误 · 错误率 {rate}",
      empty: "暂无高错误率接口",
    },
    statuses: {
      up: "正常",
      down: "异常",
      unknown: "未知",
      ready: "就绪",
      not_ready: "未就绪",
      ok: "正常",
    },
    messages: {
      loadFailed: "运维看板加载失败",
    },
  },
  operatorBackups: {
    backups: {
      title: "MySQL 加密备份",
    },
    deletions: {
      title: "数据删除申请",
    },
    columns: {
      time: "时间",
      status: "状态",
      size: "大小",
      backupFile: "备份文件",
      checksum: "校验值",
      actions: "操作",
      resource: "资源",
      tenant: "租户",
      executionTime: "执行时间",
      step: "步骤",
    },
    actions: {
      runBackup: "立即备份",
      download: "下载",
      encryptedVersion: "加密版本 (.age)",
      decryptedVersion: "解密版本 (.tar.gz)",
      restoreDatabase: "恢复数据库",
      restoreDrill: "演练验证",
      confirmRestore: "确认恢复",
      restore: "恢复",
      deleteNow: "立即删除",
      retry: "重试",
    },
    backupStatuses: {
      PENDING: "待执行",
      RUNNING: "进行中",
      COMPLETED: "已完成",
      FAILED: "失败",
    },
    deletionStatuses: {
      PENDING: "待删除",
      CANCELED: "已恢复",
      PROCESSING: "删除中",
      COMPLETED: "已删除",
      FAILED: "失败",
    },
    hints: {
      requiresAgeIdentityFile: "需在 backend/.env 配置 AGE_IDENTITY_FILE",
      requiresAgeIdentity: "需配置 AGE_IDENTITY_FILE",
      drillSafe: "在隔离容器中验证备份，不修改生产数据",
    },
    empty: {
      noBackups: "暂无备份记录",
      noDeletionRequests: "暂无删除申请",
    },
    fallback: {
      thisBackup: "该备份",
    },
    confirm: {
      restoreTitle: "恢复数据库",
      restoreDescription:
        "将用备份覆盖当前 MySQL 数据库，此操作不可撤销。\n\n备份文件：{fileName}",
      deleteBackupTitle: "删除备份",
      deleteBackupDescription: "确定删除备份文件吗？\n{fileName}",
      restoreDeletionTitle: "恢复删除申请",
      restoreDeletionDescription: "将取消该删除申请，相关资源不会被删除。",
      executeDeletionTitle: "立即删除",
      executeDeletionDescription:
        "将跳过等待期，立即执行数据删除，此操作不可撤销。",
    },
    messages: {
      backupCompleted: "备份已完成",
      backupFailed: "备份失败",
      drillCompleted: "演练验证已完成（未修改生产数据库）",
      drillFailed: "演练验证失败",
      databaseRestored: "数据库已恢复到 .env 配置的 DATABASE_URL，请刷新页面",
      databaseRestoreFailed: "数据库恢复失败",
      missingAgeIdentity: "未配置 AGE_IDENTITY_FILE，无法下载解密版本",
      encryptedDownloadStarted: "加密备份已开始下载",
      decryptedDownloadStarted: "解密备份已开始下载",
      downloadFailed: "下载失败",
      backupDeleted: "备份已删除",
      deleteFailed: "删除失败",
      deletionRestored: "删除申请已恢复",
      restoreFailed: "恢复失败",
      deletionSubmitted: "删除任务已提交",
      submitFailed: "提交失败",
      deletionRetried: "删除任务已重试",
      retryFailed: "重试失败",
    },
  },
  operatorAi: {
    metrics: {
      providers: "Provider 数",
      models: "模型数",
      prompts: "提示词数",
    },
    providers: {
      title: "Provider 配置",
      description: "统一维护模型供应商、API Key 和连通状态。",
    },
    models: {
      title: "模型档案",
      description: "定义默认模型、能力类型和调用参数。",
    },
    prompts: {
      title: "提示词模板",
      description: "用稳定 key 管理业务提示词、变量和发布版本。",
    },
    logs: {
      title: "调用日志",
      description: "记录实际 provider、model、token、耗时和失败摘要。",
      loadHint: "调用日志仅在显式操作后加载，避免占用首屏请求。",
    },
    fields: {
      name: "名称",
      providerType: "Provider 类型",
      baseUrl: "Base URL",
      apiKey: "API Key",
      enabled: "启用",
      provider: "Provider",
      model: "模型",
      capability: "能力",
      temperature: "温度",
      defaultModel: "默认模型",
      promptKey: "Prompt Key",
      purpose: "用途",
      systemPrompt: "System Prompt",
      userPrompt: "User Prompt",
      variablesSchema: "变量 Schema",
      outputSchema: "输出 Schema",
      status: "状态",
      tokens: "Tokens",
      duration: "耗时",
    },
    hints: {
      apiKey: "保存后不回显明文；列表仅显示脱敏后缀。",
    },
    providerTypes: {
      OPENAI_COMPATIBLE: "OpenAI 兼容",
      ANTHROPIC_COMPATIBLE: "Anthropic 兼容",
      PRIVATE_GATEWAY: "私有网关",
    },
    capabilities: {
      TEXT: "文本",
      JSON: "JSON",
      EMBEDDING: "向量",
    },
    healthStatuses: {
      UNKNOWN: "未知",
      HEALTHY: "健康",
      UNHEALTHY: "异常",
    },
    promptVersionStatuses: {
      DRAFT: "草稿",
      PUBLISHED: "已发布",
      ARCHIVED: "已归档",
    },
    callStatuses: {
      SUCCESS: "成功",
      FAILED: "失败",
    },
    placeholders: {
      selectProvider: "选择 Provider",
    },
    actions: {
      test: "测试连接",
      publish: "发布",
      loadLogs: "加载调用日志",
    },
    status: {
      default: "默认",
      available: "可用",
      published: "已发布",
      draft: "草稿",
    },
    empty: {
      providers: "还没有 Provider",
      models: "还没有模型",
      prompts: "还没有提示词",
      logs: "暂无调用日志",
      noApiKey: "未配置 API Key",
      noPurpose: "未填写用途",
    },
    messages: {
      providerCreated: "Provider 已创建",
      providerSaved: "Provider 已保存",
      providerHealthy: "Provider 可用",
      providerUnhealthy: "Provider 不可用",
      modelCreated: "模型已创建",
      modelSaved: "模型已保存",
      promptCreated: "提示词已创建",
      promptSaved: "提示词已保存",
      promptPublished: "提示词已发布",
      saveFailed: "保存失败",
    },
  },
  operatorSystemSettings: {
    title: "平台设置",
    description: "运营端全局配置，保存后立即生效",
    fields: {
      feedbackNotifyEmail: "用户反馈通知邮箱",
      auditLogRetentionDays: "审计日志保留天数",
      apiRequestLogRetentionDays: "API 请求日志保留天数",
      reportLogRetentionDays: "报表日志保留天数",
      batchAuditLogRetentionDays: "批处理审计日志保留天数",
      webhookLogRetentionDays: "Webhook 投递日志保留天数",
      emailLogRetentionDays: "邮件日志保留天数",
      aiCallLogRetentionDays: "AI 调用日志保留天数",
      logRetentionArchiveEnabled: "删除前归档到对象存储",
    },
    logRetention: {
      title: "日志保留策略",
      description:
        "统一配置各类平台日志的保留天数与归档行为，每日 04:00 由 scheduler 自动清理。",
    },
    hints: {
      feedbackNotifyEmail:
        "租户提交新反馈时，系统将发送通知至以上邮箱；每行一个，或用逗号分隔；留空则不发送。",
      auditLogRetentionDays: "超过该天数的 AuditLog 将被清理，默认 365 天。",
      apiRequestLogRetentionDays:
        "超过该天数的 ApiRequestLog 将被清理，默认 90 天。",
      reportLogRetentionDays: "超过该天数的 ReportLog 将被清理，默认 180 天。",
      batchAuditLogRetentionDays:
        "超过该天数的 BatchAuditLog 将被清理，默认 180 天。",
      webhookLogRetentionDays: "超过该天数的 WebhookLog 将被清理，默认 90 天。",
      emailLogRetentionDays: "超过该天数的 EmailLog 将被清理，默认 180 天。",
      aiCallLogRetentionDays: "超过该天数的 AiCallLog 将被清理，默认 90 天。",
      logRetentionArchiveEnabled:
        "开启后，删除前将日志批次压缩为 NDJSON 并上传至对象存储（需配置 S3）。",
    },
    lastUpdated: "上次更新：{time}",
    saving: "保存中...",
    save: "保存设置",
    validation: {
      retentionDaysRange: "请输入 1 到 3650 之间的数值",
    },
    messages: {
      saved: "平台设置已保存",
      saveFailed: "保存失败",
    },
    lineChannelBinding: {
      title: "LINE 平台 Bot（line_channel_binding）",
      description:
        "从服务器环境变量幂等写入平台统一 LINE Bot 绑定记录，供 Webhook 验签与路由使用。不会在前端显示或保存 Secret/Token。",
      statusActive: "已注册",
      statusMissing: "未注册",
      webhookUrl: "Webhook URL",
      envReady: "已配置",
      envMissing: "未配置",
      bindingId: "Binding ID：{value}",
      destination: "Destination（脱敏）：{value}",
      channelId: "Channel ID：{value}",
      lastUpdated: "最近更新：{time}",
      bootstrap: "注册 / 更新 LINE Bot 绑定",
      bootstrapping: "写入中...",
      loadFailed: "加载 LINE 绑定状态失败",
      messages: {
        created: "LINE Bot 绑定已创建",
        updated: "LINE Bot 绑定已更新（幂等）",
        failed: "LINE Bot 绑定写入失败",
      },
    },
  },
  operatorBilling: {
    subscriptions: {
      title: "租户订阅",
      cancelAtPeriodEnd: "到期取消",
    },
    invoices: {
      title: "账单",
    },
    planVersions: {
      title: "已发布套餐版本",
      month: "月",
    },
    columns: {
      tenant: "租户",
      planVersion: "套餐版本",
      status: "状态",
      periodEnd: "周期结束",
      actions: "操作",
      amountPaid: "已收",
    },
    actions: {
      generateInvoice: "生成账单",
      resume: "恢复",
      suspend: "暂停",
      cancelAtPeriodEnd: "到期取消",
      startCheckout: "发起 Stripe 支付",
    },
    subscriptionStatuses: {
      TRIALING: "试用中",
      ACTIVE: "启用",
      PAST_DUE: "逾期",
      SUSPENDED: "暂停",
      CANCELED: "已取消",
    },
    invoiceStatuses: {
      DRAFT: "草稿",
      OPEN: "待支付",
      PAID: "已支付",
      OVERDUE: "逾期",
      VOID: "已作废",
    },
    messages: {
      invoiceCreated: "账单已生成",
      invoiceCreateFailed: "生成账单失败",
      resume: "订阅已恢复",
      "suspend-now": "订阅已暂停",
      "cancel-at-period-end": "已设置周期结束取消",
      statusUpdateFailed: "订阅状态更新失败",
      checkoutCreated: "已打开 Stripe Checkout",
      checkoutFailed: "发起 Stripe 支付失败",
    },
  },
  operatorBillingEnhancements: {
    common: {
      tenantId: "租户 ID",
      tenantIdPlaceholder: "输入租户 UUID",
      loadContext: "加载订阅上下文",
      openBilling: "打开计费页",
      optional: "可选",
      selectSubscription: "选择订阅",
      selectPlanVersion: "选择目标套餐版本",
    },
    coupon: {
      title: "优惠券校验 / 兑换",
      description: "对指定租户校验优惠券折扣，并可兑换试用或账单减免。",
      redeemBoundary:
        "边界说明：兑换仅写入兑换记录；是否立即改价或冲减账单，取决于后续计费/支付流程，不会自动调整订阅单价。",
      code: "优惠券码",
      amountMinor: "订单金额（最小货币单位）",
      currency: "币种",
      subscriptionId: "订阅 ID（兑换时可选）",
      validate: "校验",
      redeem: "兑换",
    },
    simulation: {
      title: "套餐变更模拟",
      description:
        "预览 proration 差额；默认可持久化模拟记录，不会修改真实订阅。",
      subscriptionId: "订阅",
      targetPlanVersionId: "目标套餐版本",
      effectiveAt: "生效时间（可选）",
      persist: "持久化模拟记录",
      run: "运行模拟",
      changeType: "变更类型",
      prorationCharge: "按比例补缴（最小单位）",
      prorationCredit: "按比例抵扣（最小单位）",
      netAmount: "净额（最小单位）",
    },
    dunning: {
      title: "催收（Dunning）状态",
      description:
        "按租户评估逾期账单与催收阶段，并关联 OPEN/OVERDUE 发票与 PAST_DUE 订阅。",
      refresh: "刷新状态",
      empty: "该租户暂无订阅或无可评估催收状态。",
      status: "状态",
      daysPastDue: "逾期天数",
      overdueInvoice: "关联逾期账单",
      nextActionAt: "下次动作时间",
      pastDueSubscriptions: "PAST_DUE 订阅",
      openBillingToResume: "前往计费页手动恢复订阅",
      collectableInvoices: "可发起收款的账单（OPEN / OVERDUE）",
      noCollectableInvoices: "当前无待付 OPEN/OVERDUE 账单。",
      invoiceNumber: "账单号",
      invoiceStatus: "状态",
      amountDue: "待付（最小单位）",
      actions: "操作",
      collectPayment: "发起收款",
      linkedToDunning: "催收关联",
      statuses: {
        NONE: "无催收",
        REMINDER_1: "提醒 1",
        REMINDER_2: "提醒 2",
        FINAL_NOTICE: "最终通知",
        ESCALATED: "已升级",
      },
    },
    messages: {
      tenantRequired: "请先填写租户 ID",
      contextLoaded:
        "已加载 {subscriptions} 个订阅（其中 PAST_DUE {pastDue}）、{plans} 个套餐版本、{invoices} 张账单（可收款 {openOrOverdue}）",
      contextLoadFailed: "加载订阅上下文失败",
      couponCodeRequired: "请填写优惠券码",
      couponValidated: "优惠券校验通过",
      couponValidateFailed: "优惠券校验失败",
      couponRedeemed: "优惠券兑换已记录（不会自动改价，需后续支付/计费流程）",
      couponRedeemFailed: "优惠券兑换失败",
      simulationRequired: "请选择订阅与目标套餐版本",
      simulationDone: "套餐变更模拟完成",
      simulationFailed: "套餐变更模拟失败",
      dunningFailed: "催收状态加载失败",
      checkoutCreated: "已打开 Stripe Checkout",
      checkoutFailed: "发起收款失败",
    },
  },
  operatorTrialApplications: {
    filters: {
      searchPlaceholder: "搜索企业、联系人或邮箱",
      allStatuses: "全部状态",
    },
    columns: {
      organization: "企业",
      contact: "联系人",
      expectedUnits: "预计月用量",
      status: "状态",
      submittedAt: "提交时间",
      actions: "操作",
    },
    actions: {
      details: "详情",
      approve: "通过",
      reject: "拒绝",
    },
    empty: "暂无试用申请",
    statuses: {
      PENDING: "待审核",
      APPROVED: "已通过",
      REJECTED: "已拒绝",
      CANCELED: "已取消",
    },
    detail: {
      title: "试用申请详情",
      description: "查看申请人提交的企业与联系信息。",
      organization: "企业",
      contact: "联系人",
      email: "邮箱",
      phone: "电话",
      useCase: "使用场景",
      notes: "备注",
    },
    approve: {
      title: "通过试用申请",
      description: "选择试用套餐与截止日期，确认后开通试用租户。",
      trialPlan: "试用套餐",
      selectPlaceholder: "请选择",
      trialEndsAt: "试用截止日期",
      tenantSlug: "租户标识（可选）",
      tenantSlugPlaceholder: "自动根据企业名称生成",
      confirm: "确认开通",
    },
    reject: {
      title: "拒绝试用申请",
      description: "填写拒绝原因，申请人将收到通知邮件。",
      reason: "拒绝原因",
      confirm: "确认拒绝",
    },
    messages: {
      approveSuccess: "试用租户已开通，邀请邮件已发送",
      approveFailed: "开通失败",
      trialPlanRequired: "请选择试用套餐",
      trialEndsAtRequired: "请选择试用截止日期",
      rejectReasonRequired: "请填写拒绝原因",
      rejectSuccess: "申请已拒绝并发送通知",
      rejectFailed: "操作失败",
    },
  },
  dashboardPage: {
    title: "工作台",
    noWorkspaceTitle: "尚未选择工作区",
    noWorkspaceDescription: "开始作业前请先选择工作区。",
    openWorkspaces: "打开工作区",
    viewBilling: "查看账单",
    kpis: {
      pendingDocumentReview: "待确认票据",
      pendingDocumentReviewHelper: "需要人工确认的受信票据",
      unlinkedBankEvidence: "未照合银行明细",
      unlinkedBankEvidenceHelper: "尚未与票据照合的出金明细",
      pendingJournalReview: "待审核分录",
      pendingJournalReviewHelper: "复核收件箱中的待处理项",
      confirmedDocumentsThisMonth: "本月已确认票据",
      confirmedDocumentsThisMonthHelper: "本月（JST）已批准的票据数",
    },
    actions: {
      title: "需处理",
      description: "当前应优先处理的作业",
      empty: "当前没有待处理事项。",
      pendingDocumentReview: "处理待确认票据",
      unlinkedBankEvidence: "照合银行明细",
      pendingJournalReview: "处理待审核分录",
    },
    recentImports: {
      title: "最近银行明细导入",
      description: "最近导入批次状态",
      empty: "暂无最近导入。",
    },
    platform: {
      title: "租户设置",
      description: "计费、成员与集成概要",
      manageMembers: "管理成员",
      manageWebhooks: "管理 Webhook",
    },
    errors: {
      loadFailed: "加载工作台失败。",
    },
  },
  webhooksPage: {
    createWebhook: "创建 Webhook",
    createFirstWebhook: "创建第一个 Webhook",
    empty: "暂无 Webhook",
    enabled: "启用",
    disabled: "禁用",
    test: "测试",
    logs: "日志",
    logsTitle: "Webhook 日志",
    logsDescription: "查看该 Webhook 的历史投递记录，可重试失败请求。",
    editTitle: "编辑 Webhook",
    createTitle: "创建 Webhook",
    createDescription:
      "配置 Webhook 名称、回调 URL、触发事件与自定义 Headers。",
    editDescription: "修改 Webhook 配置，留空 Secret 表示不修改现有值。",
    name: "名称",
    callbackUrl: "回调 URL",
    secretPlaceholder: "留空则自动生成；编辑时留空表示不修改",
    triggerEvents: "触发事件",
    customHeaders: "自定义 Headers（JSON）",
    enableWebhook: "启用 Webhook",
    saving: "保存中...",
    event: "事件",
    eventsColumn: "事件",
    status: "状态",
    time: "时间",
    actions: "操作",
    retry: "重试",
    details: "详情",
    noLogs: "暂无日志",
    detailTitle: "Webhook 详情",
    detailDescription: "查看 Webhook 的配置详情与触发事件。",
    testResultTitle: "Webhook 测试结果",
    testResultDescription: "查看测试请求的响应状态与返回内容。",
    logDetailTitle: "Webhook 日志详情",
    logDetailDescription: "查看单次投递的请求与响应明细。",
    saved: "Webhook 已保存",
    created: "Webhook 已创建",
    saveFailed: "保存 Webhook 失败",
    deleteTitle: "删除 Webhook",
    deleteDescription: "确定要删除这个 Webhook 吗？",
    deleted: "Webhook 已删除",
    deleteFailed: "删除 Webhook 失败",
    testSuccess: "测试成功，请检查您的服务器是否收到了请求",
    testFailed: "测试失败：{error}",
    unknownError: "未知错误",
    testRequestFailed: "测试请求失败",
    retrySuccess: "Webhook 已重试",
    retryFailed: "重试失败",
    events: {
      resource_created: "资源创建",
      resource_updated: "资源更新",
      resource_deleted: "资源删除",
      resource_failed: "资源处理失败",
    },
    errors: {
      nameRequired: "请输入 Webhook 名称",
      urlRequired: "请输入有效的回调 URL",
      selectEvent: "请至少选择一个触发事件",
      headersJson: "Headers 必须是合法 JSON",
      headersObject: "Headers 必须是 JSON 对象",
      headerValueString: "Header {key} 的值必须是字符串",
    },
  },
  billingPage: {
    currentSubscription: "当前订阅",
    loading: "加载中...",
    plan: "套餐",
    monthlyFee: "月费",
    trialEndsAt: "试用到期",
    currentPeriod: "当前周期",
    planDetails: "套餐内容",
    noPlanDescription: "暂无套餐说明。",
    includedFeatures: "包含功能",
    addOnFeatures: "增值功能",
    noItems: "无",
    quotaLimits: "配额上限",
    usage: "已使用额度",
    invoices: "账单记录",
    invoiceNumber: "账单号",
    period: "周期",
    amount: "金额",
    status: "状态",
    noInvoices: "暂无账单",
    unlimited: "不限",
    accumulated: "累计",
    current: "当前",
    statuses: {
      TRIALING: "试用中",
      ACTIVE: "生效中",
      PAST_DUE: "逾期",
      SUSPENDED: "已暂停",
      CANCELED: "已取消",
    },
    invoiceStatuses: {
      DRAFT: "草稿",
      OPEN: "待支付",
      PAID: "已支付",
      OVERDUE: "逾期",
      VOID: "已作废",
    },
    quota: {
      units: "资源用量",
      apiCalls: "API 调用",
      storage: "存储空间",
      unitsUnit: "个",
      apiCallsUnit: "次",
      storageUnit: "MB",
    },
  },
  notificationsPage: {
    title: "通知中心",
    markAllRead: "全部标为已读",
    markRead: "标为已读",
    archive: "归档",
    openAction: "打开",
    loading: "加载中...",
    empty: "暂无通知",
    unread: "未读",
    previousPage: "上一页",
    nextPage: "下一页",
    filters: {
      all: "全部",
      unread: "未读",
      read: "已读",
    },
    severity: {
      INFO: "信息",
      SUCCESS: "成功",
      WARNING: "警告",
      ERROR: "错误",
    },
  },
  feedbackPage: {
    title: "我的反馈",
    newFeedback: "新建反馈",
    allStatuses: "全部状态",
    loading: "加载中...",
    empty: "暂无反馈记录",
    unread: "未读",
    previousPage: "上一页",
    nextPage: "下一页",
    createdAt: "创建于 {time} · {status}",
    closeFeedback: "关闭反馈",
    selectHint: "请选择一条反馈查看详情",
    followUpPlaceholder: "继续追问...",
    sending: "发送中...",
    sendFollowUp: "发送追问",
    loadDetailFailed: "加载详情失败",
    closeConfirmTitle: "关闭反馈",
    closeConfirmDescription:
      "关闭后将无法继续追问，且不能再次开启。确定关闭吗？",
    closeSuccess: "反馈已关闭",
    closeFailed: "关闭失败",
    followUpRequired: "请填写追问内容",
    followUpSent: "追问已发送",
    sendFailed: "发送失败",
    statuses: {
      OPEN: "待处理",
      IN_PROGRESS: "处理中",
      RESOLVED: "已解决",
      CLOSED: "已关闭",
    },
    subjects: {
      bug: "问题反馈",
      feature: "功能建议",
      billing: "账单与配额",
      account: "账号与安全",
      other: "其他",
    },
  },
  teamPage: {
    roles: {
      title: "角色",
      create: "新增角色",
      edit: "编辑角色",
      delete: "删除角色",
      refresh: "刷新角色",
      empty: "暂无角色",
      name: "角色名称",
      namePlaceholder: "例如：财务专员",
      menuPermissions: "菜单权限",
      refreshSuccess: "角色列表已刷新",
      nameRequired: "请输入角色名称",
      selectMenuPermission: "请至少选择一个菜单权限",
      createSuccess: "角色已创建",
      saveSuccess: "角色已保存",
      saveFailed: "保存失败",
      createOtherRoleFirst: "请先创建其他角色，再将成员移动过去后再删除",
      deleteConfirmTitle: "删除角色「{name}」？",
      deleteConfirmDescription: "删除后不可恢复。",
      deleteSuccess: "角色已删除",
      deleteFailed: "删除失败",
      deleteWithMembersPrefix: "角色「{name}」下还有 ",
      deleteWithMembersSuffix: " 个成员。请选择目标角色后再删除。",
      moveToRole: "移动到角色",
      moveMembersAndDelete: "移动成员并删除",
      selectTargetRole: "请选择目标角色",
      targetRoleSame: "目标角色不能与当前角色相同",
    },
    members: {
      title: "成员列表",
      add: "追加成员",
      searchPlaceholder: "搜索姓名或邮箱",
      selectRoleHint: "请选择左侧角色查看成员",
      account: "成员",
      loginEmail: "登录邮箱",
      actions: "操作",
      resetPassword: "重置密码",
      edit: "编辑成员",
      name: "姓名",
      initialPassword: "初始密码",
      memberRole: "所属角色",
      createSuccess: "成员已创建",
      createFailed: "创建失败",
      updateSuccess: "成员已更新",
      updateFailed: "更新失败",
      statusUpdated: "状态已更新",
      resetPasswordDescription: "为 {name}（{email}）设置新的登录密码。",
      confirmReset: "确认重置",
      emailRequired: "请输入有效的邮箱地址",
      nameRequired: "请输入成员姓名",
      roleRequired: "请选择成员角色",
      passwordMin: "密码至少 8 位",
      resetPasswordSuccess: "密码已重置",
      resetPasswordFailed: "重置密码失败",
      selectWorkspaceAccess: "选定工作区权限",
      workspaceAccessTitle: "选定工作区权限",
      workspaceAccessDescription:
        "选择 {name} 可以查看并编辑的工作区。未勾选的工作区不会出现在列表中，也无法切换。",
      workspaceAccessEmpty: "暂无可授权的工作区。",
      workspaceAccessSaveSuccess: "工作区权限已保存",
      workspaceAccessSaveFailed: "保存工作区权限失败",
      workspaceAccessLoadFailed: "加载工作区权限失败",
    },
    status: {
      label: "状态",
      active: "启用",
      inactive: "停用",
      deleted: "已删除",
    },
    passwordMinPlaceholder: "至少 8 位",
  },
  forbiddenPage: {
    title: "无权访问此页面",
    description: "当前套餐或租户配置未开放此功能，请联系管理员。",
    backHome: "返回首页",
  },
  enterpriseIdentity: {
    title: "企业身份",
    description:
      "配置租户 SSO、强制 MFA 与登录 IP 白名单。支持 OIDC 与 SAML 两种 SSO 协议。",
    fields: {
      ssoEnabled: "启用企业 SSO",
      allowPasswordLogin: "允许密码登录",
      requireMfa: "强制所有用户启用 MFA",
      loginIpWhitelistEnabled: "启用登录 IP 白名单",
      loginIpWhitelist: "允许登录的 IP / CIDR",
    },
    hints: {
      loginIpWhitelist: "每行一个 IP 或 CIDR，例如 203.0.113.10 或 10.0.0.0/8",
    },
    providers: {
      title: "身份提供商",
      description: "配置 OIDC 或 SAML IdP。SAML 需运营开启对应功能开关。",
      add: "添加提供商",
      empty: "尚未配置身份提供商",
      default: "默认",
      createTitle: "添加提供商",
      editTitle: "编辑提供商",
      dialogDescription: "OIDC 回调地址固定为 /api/v1/auth/sso/callback",
      fields: {
        name: "显示名称",
        issuer: "Issuer / EntityID",
        clientId: "Client ID",
        clientSecret: "Client Secret",
        providerType: "提供商类型",
      },
    },
    saml: {
      dialogDescription: "SAML ACS 地址固定为 /api/v1/auth/sso/saml/acs",
      acsUrl: "ACS URL",
      metadataUrl: "SP Metadata",
      acsHint: "将此 ACS URL 和 SP EntityID 注册到 IdP 配置中。",
      fields: {
        idpMetadataUrl: "IdP Metadata URL（可选）",
        idpSsoUrl: "IdP SSO URL",
        idpCertificatePem: "IdP 证书（PEM）",
        spEntityId: "SP EntityID",
        wantAssertionsSigned: "要求断言签名（推荐开启）",
        signAuthnRequests: "签名 AuthnRequest",
      },
    },
    scim: {
      title: "SCIM 用户同步",
      description:
        "配置 SCIM 2.0 入站用户 provisioning。IdP 通过 Bearer Token 管理用户生命周期。",
      fields: {
        scimEnabled: "启用 SCIM",
        scimDefaultMemberRoleId: "默认成员角色 ID",
        scimAllowCreate: "允许 SCIM 创建用户",
        scimAllowDeactivate: "允许 SCIM 停用用户",
      },
      token: {
        title: "SCIM Bearer Token",
        generate: "生成 Token",
        rotate: "轮换 Token",
        revoke: "吊销 Token",
        generated: "Token 已生成",
        rotated: "Token 已轮换",
        revoked: "Token 已吊销",
        lastRotatedAt: "最近轮换",
        endpointHint: "SCIM 端点",
        oneTimeWarning: "请立即复制此 Token，关闭后不再显示。",
      },
    },
    messages: {
      policySaved: "企业身份策略已保存",
      providerSaved: "身份提供商已保存",
      providerDeleted: "身份提供商已删除",
      saveFailed: "保存失败",
    },
  },
  settingsPage: {
    createdAt: "创建于 {date}",
    lastUsedAt: "最近使用 {date}",
    passkeys: {
      title: "Passkeys 安全密钥",
      description: "使用设备指纹或安全密钥快速安全地登录",
      browserSupported: "浏览器支持",
      browserUnsupported: "浏览器不支持",
      empty: "暂无已注册的安全密钥",
      platformKey: "设备密钥",
      securityKey: "安全密钥",
      backupEligible: "可备份",
      deleteTitle: "删除此密钥",
      add: "添加安全密钥",
      verifyPasswordHint: "请输入您的密码以确认身份",
      passwordPlaceholder: "输入您的密码",
      keyNameOptional: "密钥名称（可选）",
      keyNamePlaceholder: "如：MacBook Touch ID",
      registering: "注册中...",
      nextStep: "下一步",
      useSecurityKey: "使用您的安全密钥",
      browserPrompt: "请按照浏览器提示完成注册",
      registerSuccess: "安全密钥注册成功",
      registerSuccessDescription: "您现在可以使用此密钥登录了",
      registerSuccessTitle: "注册成功",
      registerTitle: "注册安全密钥",
      passwordRequired: "请输入密码",
      invalidServerResponse: "服务器返回数据格式异常",
      registerRequestFailed: "注册请求失败，请检查密码是否正确",
      registerFailed: "注册失败，请重试",
      deleteConfirmTitle: "删除安全密钥",
      deleteConfirmDescription:
        "确定要删除此安全密钥吗？删除后您将无法使用此密钥登录。",
      deleteSuccess: "安全密钥已删除",
    },
    mfa: {
      title: "多因素认证 (MFA)",
      description: "使用 Google 身份验证器等应用生成 6 位一次性验证码",
      enabledMessage: "MFA 已启用，登录时需要验证码",
      disabledMessage: "开启后将扫描二维码完成绑定",
      setupTitle: "绑定身份验证器",
      disableTitle: "关闭 MFA",
      setupDescription:
        "使用 Google 身份验证器（或其它 TOTP 应用）扫描下方二维码，然后输入 6 位验证码完成绑定。",
      disableDescription: "请输入当前密码和身份验证器中的验证码以关闭 MFA。",
      qrAlt: "MFA 二维码",
      manualSecretHint: "无法扫码？手动输入密钥：",
      loginPassword: "登录密码",
      sixDigitCode: "6 位验证码",
      passwordRequired: "请输入登录密码",
      codeRequired: "请输入 6 位验证码",
      confirmEnable: "确认启用",
      confirmDisable: "确认关闭",
      qrFailed: "二维码生成失败，请使用下方密钥手动添加",
      setupFailed: "初始化 MFA 失败",
      enableSuccess: "MFA 已成功启用",
      disableSuccess: "MFA 已关闭",
    },
    apiKeys: {
      description: "使用 API Key 以程序化方式访问已开放接口",
      empty: "暂无 API Key",
      rateLimit: "每分钟 {count} 次",
      rateLimitLabel: "每分钟限额",
      expiresAt: "过期 {date}",
      expiresAtLabel: "过期时间（可选）",
      scopes: "权限范围",
      scopesRequired: "请至少选择一个权限范围",
      create: "创建 API Key",
      createTitle: "创建 API Key",
      createDescription: "为 API Key 设置名称，创建后请立即保存密钥。",
      namePlaceholder: "如: Production Key",
      nameRequired: "请输入 API Key 名称",
      revealedOnce: "API Key（仅显示一次）",
      loadFailed: "加载 API Key 失败",
      createSuccess: "API Key 已创建，请立即保存",
      createFailed: "创建 API Key 失败",
      deleteTitle: "删除 API Key",
      deleteDescription: "确定要删除这个 API Key 吗？",
      revokeSuccess: "API Key 已撤销",
      revokeFailed: "撤销 API Key 失败",
    },
    apiRequestLogs: {
      title: "API 调用日志",
      description: "仅记录 API Key 请求的元数据，不含请求体与密钥。",
      empty: "暂无 API 调用记录",
      apiKeyFilter: "按 API Key ID 筛选",
      columns: {
        time: "时间",
        method: "方法",
        path: "路径",
        status: "状态码",
        duration: "耗时",
      },
    },
    profile: {
      title: "个人信息",
      avatar: "头像",
      avatarUpload: "上传头像",
      avatarUploading: "上传中...",
      avatarHint: "支持 PNG、JPG、WebP，最大 2 MB；保存前会裁剪为正方形。",
      avatarCropTitle: "裁剪头像",
      avatarConfirmCrop: "使用头像",
      avatarZoom: "缩放",
      avatarInvalid: "请上传有效的图片文件。",
      avatarTooLarge: "头像图片不能超过 2 MB。",
      avatarSaveSuccess: "头像已更新",
      avatarSaveFailed: "头像更新失败",
      name: "名称",
      save: "保存修改",
      saveSuccess: "个人信息已保存",
      saveFailed: "保存个人信息失败",
    },
    security: {
      title: "安全设置",
      description: "修改您的登录密码",
      currentPassword: "当前密码",
      newPassword: "新密码",
      confirmPassword: "确认新密码",
      changePassword: "修改密码",
      currentPasswordRequired: "请输入当前密码",
      passwordMismatch: "两次输入的密码不一致",
      passwordTooShort: "密码长度不能少于 8 位",
      changeSuccess: "密码已修改，请重新登录",
      changeFailed: "修改密码失败",
    },
  },
  dataTable: {
    loading: "加载中...",
    empty: "暂无记录",
    total: "共 {total} 条",
    perPage: "{size} / 页",
    goTo: "前往",
    page: "页",
    sortAsc: "按 {label} 升序排序",
    sortDesc: "按 {label} 降序排序",
    sortable: "按 {label} 排序",
  },
  datePicker: {
    placeholder: "选择日期",
    rangePlaceholder: "选择日期范围",
    clear: "清除日期",
    year: "{year}年",
    month: "{month}月",
    yearPlaceholder: "年份",
    monthPlaceholder: "月份",
  },
  pdfPreview: {
    title: "PDF 预览",
    description:
      "可上下滚动浏览全部页面；翻页按钮会滚动到对应页。支持缩放、下载与打印。",
    loading: "正在加载 PDF...",
    rendering: "正在渲染页面...",
    previousPage: "上一页",
    nextPage: "下一页",
    zoomOut: "缩小",
    zoomIn: "放大",
    fitWidth: "适应宽度",
    download: "下载",
    print: "打印",
    pageLabel: "第 {page} / {total} 页",
  },
  dialog: {
    discardTitle: "放弃未保存的修改？",
    discardDescription: "当前弹出窗口中有未保存的内容，关闭后这些修改会丢失。",
    discardNavigationDescription:
      "当前弹出窗口中有未保存的内容，离开页面后这些修改会丢失。",
    discardConfirm: "放弃修改",
    keepEditing: "继续编辑",
    defaultDescription: "弹窗内容",
  },

  openingArApImport: {
    page: {
      title: "期初应收应付导入",
      description:
        "导入切换日未结应收/应付余额。仅创建 OPEN 义务，不生成仕訳。",
    },
    actions: {
      upload: "上传 CSV",
      execute: "执行导入",
      retry: "重试失败执行",
      cancelBatch: "取消批次",
      viewReceivable: "查看应收",
      viewPayable: "查看应付",
      viewReceivablesAging: "应收账龄",
      viewPayablesAging: "应付账龄",
      openMigration: "导入期初余额",
    },
    empty: {
      title: "暂无期初导入批次",
      description: "上传 UTF-8 CSV，迁移切换日未结应收应付余额。",
    },
    filters: {
      allStatuses: "全部状态",
    },
    upload: {
      title: "上传期初应收应付 CSV",
      description:
        "仅支持 UTF-8 CSV。金额为切换日未结余额，不是历史发票原金额。",
      utf8Only: "仅支持 UTF-8 CSV（OPENING_AR_AP_CSV_V1）",
      shiftJisUnsupported: "不支持 Shift-JIS。请先转换为 UTF-8 再上传。",
      requiredColumns: "必填列",
      optionalColumns: "可选列",
      downloadTemplate: "下载 UTF-8 模板",
      fileLabel: "CSV 文件",
      defaultReceivableAccount: "默认应收科目",
      defaultPayableAccount: "默认应付科目",
      cutoverDate: "切换日（可选）",
      sourceSystem: "来源系统（可选）",
      memo: "备注（可选）",
      submit: "上传并校验",
    },
    amountSemantics: {
      title: "导入金额 = 截止迁移日未结余额",
      body: "请填写系统启用时点尚未清算的未收/未付余额，不是历史发票原金额。",
      example: "例：历史发票 1,000,000，已付 400,000 → 导入 600,000。",
      noHistoryReconstruction: "系统不会重建历史收款/付款或核销。",
    },
    zeroAccounting: {
      title: "导入不生成仕訳",
      body: "本取込仅登记期初运营应收/应付余额，不会创建仕訳或转记。",
    },
    summary: {
      title: "批次摘要",
      file: "文件",
      totalRows: "总行数",
      validRows: "有效行",
      invalidRows: "无效行",
      receivableTotal: "应收合计",
      payableTotal: "应付合计",
      importedRows: "已导入",
      failedRows: "失败",
      cutoverDate: "切换日",
      accountingBook: "会计账簿",
      bookAtExecute: "执行时解析（ACTIVE）",
      defaultReceivableAccount: "默认应收科目",
      defaultPayableAccount: "默认应付科目",
    },
    table: {
      rowNumber: "行号",
      side: "方向",
      counterparty: "交易对手",
      externalReference: "外部参照号",
      amount: "未结余额",
      dueDate: "到期日",
      account: "应收/应付科目",
      status: "状态",
      error: "错误",
      override: "覆盖",
    },
    dueDate: {
      none: "无到期日",
    },
    side: {
      RECEIVABLE: "应收",
      PAYABLE: "应付",
      unknown: "—",
    },
    batchStatus: {
      RECEIVED: "已接收",
      VALIDATING: "校验中",
      READY: "预览就绪",
      VALIDATION_FAILED: "校验失败",
      EXECUTING: "执行中",
      COMPLETED: "已完成",
      COMPLETED_WITH_ERRORS: "完成（含错误）",
      FAILED: "失败",
      CANCELLED: "已取消",
    },
    rowStatus: {
      PENDING: "待处理",
      VALID: "有效",
      INVALID: "无效",
      DUPLICATE_CONFLICT: "重复冲突",
      IMPORTING: "导入中",
      IMPORTED: "已导入",
      FAILED: "失败",
      SKIPPED: "跳过",
    },
    history: {
      createdAt: "创建时间",
      file: "文件",
      status: "状态",
      rows: "行数",
      imported: "已导入",
      failed: "失败",
      executedAt: "执行时间",
    },
    executeDialog: {
      title: "确认执行期初导入",
      description:
        "将为有效行直接创建 OPEN 义务。预览是人工门控，执行必须显式确认。",
      validRows: "将导入的有效行",
      invalidRows: "不会导入的无效行",
      receivableTotal: "应收合计",
      payableTotal: "应付合计",
      accountingBook: "当前有效会计账簿",
      noAutoExecute: "上传不会自动执行。",
      confirm: "执行导入",
      executing: "执行中…",
    },
    progress: {
      executing: "正在执行导入。页面会自动刷新直至结束。",
    },
    partialSuccess: {
      banner: "部分成功：部分行已导入，部分失败。这不是整批失败。",
      noRetry: "含错误完成的批次不可重试。请处理冲突后上传新批次。",
    },
    correction: {
      guidance:
        "更正未核销的期初项目：在应收/应付明细中取消，再用新文件重新导入。不支持批次撤销。",
    },
    settlement: {
      note: "导入后的 OPEN 义务可像普通应收应付一样进行入金/出金核销。",
    },
    featureDisabled: {
      title: "期初应收应付导入未启用",
      body: "功能开关 accounting.ar_ap.opening_migration.enabled 对该租户关闭。无法上传或执行。",
    },
    cancelDialog: {
      title: "取消该批次？",
      description: "在执行前取消批次。已导入的义务不会被删除。",
    },
    errors: {
      generic: "期初导入请求失败",
      permissionDenied: "无期初导入权限",
      permissionDeniedBody: "需要 ar_ap.opening_import.read 才能查看此工作区。",
    },
    failureCodes: {
      GENERIC: "期初导入错误",
      AR_AP_OPENING_MIGRATION_FEATURE_DISABLED: "期初迁移功能未启用",
      AR_AP_FEATURE_DISABLED: "应收应付功能未启用",
      AR_AP_OPENING_IMPORT_NOT_FOUND: "未找到期初导入批次",
      AR_AP_OPENING_IMPORT_STATUS_TRANSITION_INVALID: "批次状态转换无效",
      AR_AP_OPENING_IMPORT_VERSION_CONFLICT: "批次版本冲突，请刷新后重试",
      AR_AP_OPENING_CSV_ENCODING_UNSUPPORTED:
        "不支持的 CSV 编码（需要 UTF-8；不支持 Shift-JIS）",
      AR_AP_OPENING_CSV_HEADER_REQUIRED: "需要 CSV 表头",
      AR_AP_OPENING_CSV_HEADER_INVALID: "CSV 表头无效",
      AR_AP_OPENING_CSV_HEADER_DUPLICATE: "CSV 表头列重复",
      AR_AP_OPENING_CSV_HEADER_MISSING_REQUIRED: "缺少必填 CSV 列",
      AR_AP_OPENING_CSV_FORBIDDEN_COLUMN: "存在禁止的 CSV 列",
      AR_AP_OPENING_FORMAT_UNSUPPORTED: "不支持的格式配置",
      AR_AP_OPENING_FILENAME_INVALID: "文件名无效",
      AR_AP_OPENING_FILE_SIZE_INVALID: "文件大小无效",
      AR_AP_OPENING_CONTENT_TYPE_INVALID: "内容类型无效，需要 CSV",
      AR_AP_OPENING_FILE_REQUIRED: "需要 CSV 文件",
      AR_AP_OPENING_SIDE_INVALID: "方向必须是 RECEIVABLE 或 PAYABLE",
      AR_AP_OPENING_COUNTERPARTY_REQUIRED: "交易对手必填",
      AR_AP_OPENING_COUNTERPARTY_TOO_LONG: "交易对手过长",
      AR_AP_OPENING_AMOUNT_INVALID: "金额必须是正整数最小货币单位字符串",
      AR_AP_OPENING_EXTERNAL_REFERENCE_REQUIRED: "外部参照号必填",
      AR_AP_OPENING_EXTERNAL_REFERENCE_TOO_LONG: "外部参照号过长",
      AR_AP_OPENING_EXTERNAL_REFERENCE_INVALID: "外部参照号无效",
      AR_AP_OPENING_EXTERNAL_REFERENCE_DUPLICATE_IN_BATCH: "批内外部参照号重复",
      AR_AP_OPENING_EXTERNAL_REFERENCE_CONFLICT:
        "工作区内已存在该外部参照号的有效期初义务",
      AR_AP_OPENING_DUE_DATE_INVALID: "到期日必须是 YYYY-MM-DD",
      AR_AP_OPENING_ACCOUNT_REQUIRED: "无法解析应收/应付科目",
      AR_AP_OPENING_ACCOUNT_NOT_FOUND: "未找到科目",
      AR_AP_OPENING_ACCOUNT_ID_INVALID: "科目 ID 无效",
      AR_AP_OPENING_NO_VALID_ROWS: "没有可执行的有效行",
      AR_AP_OPENING_ROW_INCOMPLETE: "行数据不完整",
      AR_AP_OPENING_ROW_IMPORT_FAILED: "行导入失败",
      ACCOUNTING_BOOK_ACTIVE_REQUIRED: "需要有效会计账簿",
      SETTLEMENT_ACCOUNTING_ACCOUNT_NOT_ELIGIBLE: "科目不符合应收应付资格",
      WORKSPACE_PERMISSION_DENIED: "工作区权限不足",
      IDEMPOTENCY_KEY_REUSED: "幂等键被不同请求复用",
      IDEMPOTENCY_KEY_REQUIRED: "需要 Idempotency-Key",
    },
  },
  arAp: {
    pages: {
      receivables: {
        title: "应收",
        subtitle:
          "运营侧应收义务。OPEN 表示未决济开放项，不代表已过账或已匹配。",
      },
      payables: {
        title: "应付",
        subtitle:
          "运营侧应付义务。OPEN 表示未决济开放项，不代表已过账或已匹配。",
      },
    },
    status: {
      DRAFT: "草稿",
      OPEN: "未结算",
      SETTLED: "已结算",
      CANCELLED: "已取消",
      SUPERSEDED: "已替换",
    },
    side: {
      RECEIVABLE: "应收",
      PAYABLE: "应付",
    },
    source: {
      MANUAL: "手工",
      BUSINESS_TRANSACTION: "业务交易",
    },
    table: {
      counterparty: "对方",
      counterpartyEmpty: "（暂无对方）",
      amount: "金额",
      dueDate: "到期日",
      dueDateEmpty: "—",
      status: "状态",
      source: "来源",
      createdAt: "创建时间",
      updatedAt: "更新时间",
      actions: "操作",
    },
    filters: {
      status: "状态",
      sourceType: "来源",
      dueFrom: "到期日起",
      dueTo: "到期日止",
      allStatuses: "全部状态",
      allSources: "全部来源",
      apply: "应用",
      reset: "重置",
    },
    actions: {
      create: "新建义务",
      refresh: "刷新",
      viewDetail: "详情",
      edit: "编辑",
      save: "保存",
      confirm: "确认为未结算",
      cancel: "取消义务",
    },
    fields: {
      side: "方向",
      status: "状态",
      amount: "金额（日元）",
      currency: "币种",
      dueDate: "到期日",
      counterparty: "对方",
      registrationNumber: "发票登记号",
      sourceType: "来源类型",
      sourceId: "来源 ID",
      version: "版本",
      createdAt: "创建时间",
      openedAt: "开放时间",
      cancelledAt: "取消时间",
      updatedAt: "更新时间",
      arApAccount: "应收/应付科目",
      accountingBook: "会计账簿",
    },
    create: {
      title: "创建义务",
      description: "创建草稿义务。稍后确认为 OPEN（未结算）。",
      amountPlaceholder: "例如 1250000",
      counterpartyHint: "确认为 OPEN 前必填对方。草稿阶段可选。",
      submit: "创建草稿",
      submitting: "创建中…",
      errors: {
        amountInvalid: "请输入大于 0 的整数日元金额。",
      },
    },
    detail: {
      title: "义务详情",
      description: "运营开放项详情。取消不等于冲销分录或总账。",
      loading: "加载中…",
      saving: "保存中…",
      businessTransaction: "业务交易",
      businessTransactionHint: "仅展示血缘 ID。本阶段不提供业务交易编辑页。",
      sections: {
        basic: "基本信息",
        counterparty: "对方",
        source: "来源",
        lifecycle: "生命周期",
        accounting: "会計設定",
      },
    },
    account: {
      required: "需要设置应收/应付科目",
      immutableHint: "科目一旦设置后不可更改。",
      dialog: {
        title: "设置应收/应付科目",
        description: "选择与本义务会计账簿一致的科目。",
      },
      actions: {
        set: "设置科目",
        save: "保存科目",
        saving: "保存中…",
      },
    },
    confirmDialog: {
      title: "确认为未结算义务？",
      description: "将状态设为 OPEN（未结算）。不会创建分录、过账或付款。",
    },
    cancelDialog: {
      title: "取消该义务？",
      description: "仅取消应收/应付义务。不是分录冲销，也不会变更总账。",
    },
    empty: {
      receivables: "还没有应收",
      payables: "还没有应付",
      description: "可先手工创建草稿义务。",
      filteredReceivables: "没有符合条件的应收",
      filteredPayables: "没有符合条件的应付",
      filteredDescription: "请调整状态、来源或到期日筛选。",
    },
    featureDisabled: {
      banner: "本租户已关闭应收应付写操作。现有义务仍可只读查看。",
    },
    errors: {
      loadFailed: "加载义务失败",
      loadDetailFailed: "加载义务详情失败",
      createFailed: "创建义务失败",
      updateFailed: "更新义务失败",
      confirmFailed: "确认义务失败",
      cancelFailed: "取消义务失败",
      setAccountFailed: "设置科目失败",
      noPermissionTitle: "无访问权限",
      noPermission: "您没有查看应收应付义务的权限。",
    },
    failureCodes: {
      AR_AP_OBLIGATION_FEATURE_DISABLED: "功能开关已关闭应收应付写操作。",
      AR_AP_OBLIGATION_NOT_FOUND: "未找到该义务。",
      AR_AP_OBLIGATION_STATUS_TRANSITION_INVALID: "不允许该状态变更。",
      AR_AP_OBLIGATION_SOURCE_CONFLICT: "该来源已存在有效义务。",
      AR_AP_OBLIGATION_AMOUNT_INVALID: "金额必须为正整数日元。",
      AR_AP_OBLIGATION_SIDE_INVALID: "义务方向无效。",
      AR_AP_OBLIGATION_CURRENCY_UNSUPPORTED: "仅支持日元。",
      AR_AP_OBLIGATION_VERSION_CONFLICT:
        "记录已被他人更新，已重新加载最新版本。",
      AR_AP_OBLIGATION_COUNTERPARTY_REQUIRED: "确认为开放前必须填写对方名称。",
      AR_AP_OBLIGATION_BUSINESS_TRANSACTION_NOT_CONFIRMED:
        "业务交易必须先确认。",
      AR_AP_OBLIGATION_AR_AP_ACCOUNT_STATUS_INVALID:
        "当前义务状态不允许设置科目。",
      AR_AP_OBLIGATION_AR_AP_ACCOUNT_IMMUTABLE: "科目已配置，不可更改。",
      AR_AP_OBLIGATION_ACCOUNTING_BOOK_REQUIRED: "请先设置会计账簿。",
      AR_AP_OBLIGATION_HAS_CONFIRMED_ALLOCATIONS:
        "请先在本页取消可取消的核销，再取消应收/应付。请勿自动串联操作。",
      WORKSPACE_PERMISSION_DENIED: "您没有执行此操作的权限。",
      IDEMPOTENCY_KEY_REUSED: "该请求已用不同内容提交过。",
    },
    aging: {
      title: "账龄与到期日运营",
      asOf: "截至 {date}",
      timezoneHint: "工作区时区：{timezone}",
      dueScheduleHint: "到期日程展示 OPEN 义务剩余金额与到期日，不是现金预测。",
      noDueDate: "无到期日",
      viewMode: {
        groupLabel: "视图模式",
        list: "列表",
        aging: "账龄",
      },
      summary: {
        outstanding: "未清余额",
        overdue: "已逾期",
        dueSoon: "即将到期",
        noDueDate: "无到期日",
        obligationCount: "{count} 笔 OPEN",
      },
      buckets: {
        groupLabel: "账龄分段",
        count: "{count} 笔",
        CURRENT: "未到期",
        D1_30: "1–30 天",
        D31_60: "31–60 天",
        D61_90: "61–90 天",
        D90_PLUS: "90+ 天",
        NO_DUE_DATE: "无到期日",
      },
      dueState: {
        OVERDUE: "已逾期",
        DUE_SOON: "即将到期",
        NOT_DUE: "未到期",
        NO_DUE_DATE: "无到期日",
      },
      daysPastDue: {
        noDueDate: "无到期日",
        today: "今日到期",
        overdue: "已逾期",
        overdueCount: "逾期 {days} 天",
        notYetDue: "未到期",
      },
      table: {
        original: "原始金额",
        remaining: "剩余",
        allocated: "已核销",
        dueState: "到期状态",
        bucket: "账龄分段",
      },
      filters: {
        asOfDate: "截至日期",
        dueState: "到期状态",
        counterparty: "往来方名称",
        counterpartyPlaceholder: "快照名称（精确匹配）",
        allDueStates: "全部到期状态",
      },
      empty: {
        receivables: "无未清应收",
        payables: "无未清应付",
        filteredReceivables: "无匹配的应收账龄",
        filteredPayables: "无匹配的应付账龄",
        description: "没有 OPEN 义务，或已全部结清。",
        filteredDescription: "请调整截至日期、到期状态或往来方筛选。",
      },
      errors: {
        loadFailed: "加载账龄数据失败",
      },
    },
  },

  payments: {
    pages: {
      title: "收付款",
      subtitle: "运营层现金事实。确认 ≠ 过账；核销在义务侧单独进行。",
    },
    status: { DRAFT: "草稿", CONFIRMED: "已确认", CANCELLED: "已取消" },
    direction: { INCOMING: "入金", OUTGOING: "出金" },
    source: { MANUAL: "手工", BANK_MATCH: "银行匹配" },
    allocationState: {
      UNALLOCATED: "未分配",
      PARTIALLY_ALLOCATED: "部分分配",
      FULLY_ALLOCATED: "全部分配",
    },
    table: {
      paymentDate: "支付日",
      direction: "方向",
      counterparty: "对方",
      counterpartyEmpty: "（无对方）",
      amount: "金额",
      allocated: "已分配",
      unallocated: "未分配",
      status: "状态",
      allocation: "分配状态",
      source: "来源",
      actions: "操作",
    },
    filters: {
      direction: "方向",
      status: "状态",
      paymentDate: "支付日范围",
      allStatuses: "全部状态",
      allDirections: "全部方向",
      allocationAll: "全部分配",
      allocationNeeds: "需分配",
      allocationFull: "已全部分配",
      apply: "应用",
      reset: "重置",
    },
    actions: {
      create: "新建收付款",
      refresh: "刷新",
      viewDetail: "详情",
      edit: "编辑",
      confirm: "确认收付款",
      cancel: "取消收付款",
    },
    fields: {
      direction: "方向",
      status: "状态",
      amount: "金额（日元）",
      paymentDate: "支付日",
      counterparty: "对方",
      reference: "参考号",
      description: "摘要",
      bankAccount: "银行账户",
      version: "版本",
      confirmedAt: "确认时间",
    },
    form: {
      createTitle: "新建收付款",
      createDescription: "创建 MANUAL 草稿。确认后为运营现金事实（非过账）。",
      editTitle: "编辑收付款",
      editDescription: "更新草稿字段。",
      amountPlaceholder: "例如 125000",
      bankAccountEmpty: "（无）",
      bankMatchHint: "银行来源字段只读。可调整摘要或对方名称。",
      submit: "保存",
      submitting: "保存中…",
      errors: {
        amountInvalid: "请输入正整数日元金额。",
        dateRequired: "请选择支付日。",
      },
    },
    detail: {
      title: "收付款详情",
      description: "运营现金事实。取消不会冲销分录、银行匹配或核销。",
      loading: "加载中…",
      total: "合计",
      allocated: "已分配",
      unallocated: "未分配",
      provenance: "来源",
      bankMatchDecision: "决策 ID",
      viewBankReconciliation: "查看银行对账",
      cancelBlockedHint:
        "存在已确认核销时不可取消。请先在本页取消可取消的核销，再取消入金/出金。",
    },
    allocations: {
      title: "核销分配",
      empty: "该收付款尚无核销分配。",
      summary: "{total} 条分配 · {inProgress} 进行中 · {posted} 已过账",
      unallocatedGuidance: "已确认但未分配。请从应收或应付侧进行核销。",
      goReceivables: "打开应收",
      goPayables: "打开应付",
      viewObligation: "查看义务",
    },
    confirmDialog: {
      title: "确认收付款？",
      description: "标记为运营现金事实。非分录过账或核销。",
      bankMatchDescription: "确认银行关联草稿。确认前请核对银行血缘。",
    },
    cancelDialog: {
      title: "取消收付款？",
      description: "取消收付款记录。非分录冲销或银行取消匹配。",
    },
    featureDisabled: {
      banner: "收付款变更功能已关闭，仍可查看列表。",
    },
    empty: {
      title: "暂无收付款",
      description: "可手工创建或从银行匹配关联。",
      filteredTitle: "无匹配结果",
      filteredDescription: "请调整筛选或分配快捷筛选。",
    },
    errors: {
      loadFailed: "加载收付款失败",
      loadDetailFailed: "加载详情失败",
      createFailed: "创建失败",
      updateFailed: "更新失败",
      confirmFailed: "确认失败",
      cancelFailed: "取消失败",
      cancelBlockedByAllocations: "存在已确认核销时不可取消。",
      noPermissionTitle: "无访问权限",
      noPermission: "您无权查看收付款。",
    },
    failureCodes: {
      PAYMENT_FEATURE_DISABLED: "收付款变更功能已关闭。",
      PAYMENT_NOT_FOUND: "收付款不存在。",
      PAYMENT_STATUS_TRANSITION_INVALID: "不允许此状态变更。",
      PAYMENT_VERSION_CONFLICT: "记录已被他人更新，请重新加载。",
      PAYMENT_AMOUNT_INVALID: "金额须为正整数日元。",
      PAYMENT_DIRECTION_INVALID: "方向无效。",
      PAYMENT_CURRENCY_UNSUPPORTED: "仅支持日元。",
      PAYMENT_DATE_INVALID: "支付日无效。",
      PAYMENT_HAS_CONFIRMED_ALLOCATIONS: "须先作废已确认核销方可取消。",
      WORKSPACE_PERMISSION_DENIED: "您没有执行此操作的权限。",
      IDEMPOTENCY_KEY_REUSED: "该请求已用不同内容提交过。",
    },
  },

  differenceSettlement: {
    section: {
      title: "差额处理",
      empty: "该义务暂无差额处理记录。",
      viewPayment: "查看收付款",
    },
    actions: {
      create: "差额处理",
    },
    types: {
      BANK_FEE: "银行手续费",
      SMALL_DIFFERENCE: "少额差额",
    },
    status: {
      CONFIRMED: "已确认",
      CANCELLED: "已取消",
    },
    dialog: {
      createTitle: "差额处理",
      createDescription: "独立于收付款核销，通过差额处理减少义务剩余。",
    },
    confirmDialog: {
      title: "确认差额处理？",
      description:
        "确认差额处理后，未核销余额将相应减少。分录过账将另行按会计处理进度进行，不会在此步骤自动完成。",
    },
    fields: {
      type: "差额类型",
      amount: "差额金额",
      maximum: "上限金额",
      remaining: "义务剩余",
      differenceAccount: "差额科目",
      differenceAccountPlaceholder: "选择科目",
      reason: "原因（可选）",
      bankFeeContext: "关联收付款（可选）",
      paymentAmount: "收付款金额",
      allocationAmount: "对本义务的核销额",
      relatedPaymentOptional: "关联收付款为可选，不会改变收付款未核销金额。",
    },
    validation: {
      INVALID: "请输入正整数金额。",
      OVER_REMAINING: "金额超过义务剩余。",
      OVER_TYPE_MAX: "超过少额差额上限。",
    },
    errors: {
      reloadFailed: "重新加载义务版本失败。",
      versionConflict: "义务已被其他操作更新，请刷新剩余后重试。",
    },
    cancel: {
      title: "取消差额处理",
      description: "取消运营差额处理，不会自动冲销已过账分录。",
      reason: "原因（可选）",
      confirm: "取消差额处理",
      reversalRequired: "已过账，需先冲销。",
      viewProgress: "查看会计进度",
    },
    failureCodes: {
      UNKNOWN: "差额处理失败。",
      AR_AP_DIFFERENCE_FEATURE_DISABLED: "差额处理功能已关闭。",
      AR_AP_DIFFERENCE_NOT_FOUND: "未找到差额处理。",
      AR_AP_DIFFERENCE_OBLIGATION_NOT_OPEN: "仅 OPEN 义务可差额处理。",
      AR_AP_DIFFERENCE_AMOUNT_INVALID: "差额金额无效。",
      AR_AP_DIFFERENCE_OVER_SETTLEMENT: "差额将超过义务原金额。",
      AR_AP_DIFFERENCE_VERSION_CONFLICT: "义务版本冲突。",
      AR_AP_DIFFERENCE_CURRENCY_MISMATCH: "差额币种必须与义务一致。",
      AR_AP_DIFFERENCE_ACCOUNT_MISSING: "需要差额科目。",
      AR_AP_DIFFERENCE_ACCOUNT_INVALID: "差额科目不符合规则。",
      AR_AP_DIFFERENCE_ACCOUNT_INACTIVE: "差额科目未启用。",
      AR_AP_DIFFERENCE_ACCOUNT_REQUIRED: "需要差额科目。",
      AR_AP_DIFFERENCE_TYPE_UNSUPPORTED: "差额类型不支持。",
      AR_AP_DIFFERENCE_STATUS_TRANSITION_INVALID: "无效状态转换。",
      AR_AP_DIFFERENCE_CANCEL_REQUIRES_REVERSAL: "已过账，需冲销。",
      AR_AP_DIFFERENCE_PERMISSION_DENIED: "无权限。",
      AR_AP_DIFFERENCE_BOOK_REQUIRED: "义务需要会计账簿。",
    },
  },
  differenceAccounting: {
    panel: { title: "差额会计进度", differenceAmount: "差额金额" },
    stages: {
      difference: "差额",
      transaction: "交易",
      classification: "分类",
      journal: "分录",
      posting: "过账",
      posted: "已过账",
    },
    actions: {
      retry: "重试会计",
      resolveDate: "设置会计日",
      setDifferenceAccount: "设置差额科目",
      setArApAccount: "设置应收应付科目",
    },
    fields: { accountingDate: "会计日", postedJournal: "已过账分录" },
    warnings: { needsReversal: "已过账，取消差额前需冲销。" },
    featureDisabled: { banner: "差额处理仍有效，但会计编排已关闭。" },
    dateDialog: {
      title: "设置会计日",
      description: "会计日仅用于差额会计/过账。",
      accountingDate: "会计日",
      openPeriodHint: "仅 OPEN 期间日期可接受。",
      submit: "确认",
      errors: { required: "会计日必填。" },
    },
    outcomes: {
      UNKNOWN: { label: "未知", description: "无法确定会计进度。" },
      SOURCE_NOT_READY: { label: "未就绪", description: "会计来源未就绪。" },
      WAITING_ACCOUNTING_DATE: {
        label: "需会计日",
        description: "请设置会计日。",
      },
      WAITING_AR_AP_ACCOUNT: {
        label: "需应收应付科目",
        description: "请设置义务应收应付科目。",
      },
      WAITING_DIFFERENCE_ACCOUNT: {
        label: "需差额科目",
        description: "请设置差额损益科目。",
      },
      WAITING_TRANSACTION_CONFIRMATION: {
        label: "待交易确认",
        description: "需确认业务交易。",
      },
      WAITING_CLASSIFICATION_CONFIRMATION: {
        label: "待分类确认",
        description: "需确认分类。",
      },
      CLASSIFICATION_ENSURED: {
        label: "分类已创建",
        description: "分类待确认。",
      },
      JOURNAL_ENSURED: { label: "分录草稿已创建", description: "分录待确认。" },
      WAITING_JOURNAL_CONFIRMATION: {
        label: "待分录确认",
        description: "需确认分录。",
      },
      WAITING_JOURNAL_REVIEW: {
        label: "待分录复核",
        description: "需分录复核。",
      },
      WAITING_OPEN_PERIOD: {
        label: "待 OPEN 期间",
        description: "目标期间未 OPEN。",
      },
      ORCHESTRATION_DISABLED: {
        label: "编排已关闭",
        description: "功能开关关闭自动会计。",
      },
      POSTING_REQUESTED: { label: "过账已请求", description: "等待过账开始。" },
      POSTING_ALREADY_ENSURED: {
        label: "过账请求已确保",
        description: "过账请求已存在。",
      },
      POSTING_IN_PROGRESS: { label: "过账中", description: "过账处理中。" },
      POSTED: { label: "已过账", description: "已过账分录存在。" },
      POSTING_RETRYABLE_FAILED: {
        label: "过账失败（可重试）",
        description: "过账处于可重试失败状态。",
      },
      POSTING_PERMANENTLY_FAILED: {
        label: "过账失败（永久）",
        description: "永久过账失败。",
      },
      NEEDS_REVERSAL: {
        label: "需冲销",
        description: "已过账，请走冲销流程。",
      },
      ACCOUNTING_REVERSED: {
        label: "会计已冲销",
        description: "冲销分录已完成。",
      },
      TARGET_TERMINAL: { label: "终态", description: "无需进一步会计编排。" },
      FAILED: { label: "失败", description: "会计处理失败，可重试。" },
      ALREADY_READY_FOR_POSTING: {
        label: "可过账",
        description: "人工关卡已完成。",
      },
      POSTING_BLOCKED_FOUNDATION_FLAG: {
        label: "过账被阻止",
        description: "基础过账开关阻止过账。",
      },
      BOOK_MISMATCH: { label: "账簿不匹配", description: "会计账簿不匹配。" },
      ACCOUNT_INACTIVE: {
        label: "科目未启用",
        description: "快照科目非 ACTIVE。",
      },
    },
    failureCodes: {
      AR_AP_DIFFERENCE_ACCOUNTING_FEATURE_DISABLED: "差额会计编排已关闭。",
      AR_AP_DIFFERENCE_ACCOUNTING_BOOK_MISMATCH: "会计账簿不匹配。",
      AR_AP_DIFFERENCE_ACCOUNTING_WAITING_ACCOUNTING_DATE: "需要会计日。",
      AR_AP_DIFFERENCE_ACCOUNTING_AR_AP_ACCOUNT_REQUIRED: "需要应收应付科目。",
      AR_AP_DIFFERENCE_ACCOUNTING_DIFFERENCE_ACCOUNT_REQUIRED: "需要差额科目。",
      AR_AP_DIFFERENCE_ACCOUNTING_PERIOD_NOT_OPEN: "会计期间未 OPEN。",
      AR_AP_DIFFERENCE_ACCOUNTING_PERIOD_NOT_FOUND: "未找到会计期间。",
      AR_AP_DIFFERENCE_ACCOUNTING_DATE_BEFORE_EVIDENCE_DATE:
        "会计日早于证据日。",
      AR_AP_DIFFERENCE_ACCOUNTING_DATE_FISCAL_YEAR_MISMATCH:
        "会计日财年不匹配。",
      AR_AP_DIFFERENCE_ACCOUNTING_SNAPSHOT_IMMUTABLE: "会计快照不可变。",
      AR_AP_DIFFERENCE_ACCOUNTING_SOURCE_NOT_ELIGIBLE:
        "差额来源不符合会计条件。",
      AR_AP_DIFFERENCE_ACCOUNTING_ACCOUNT_INACTIVE: "会计科目未启用。",
      AR_AP_DIFFERENCE_ACCOUNTING_NOT_READY: "差额会计未就绪。",
      AR_AP_DIFFERENCE_ACCOUNTING_ACCOUNTS_REQUIRED: "需要会计科目。",
      AR_AP_DIFFERENCE_ACCOUNTING_ACCOUNT_COLLISION: "会计科目冲突。",
    },
  },

  settlementWrite: {
    actions: {
      addAllocation: "添加核销",
      create: "核销",
      loadMore: "加载更多",
    },
    dialog: {
      createTitle: "核销入金/出金",
      createDescription: "在收款/付款与债权/债务之间创建已确认的消込。",
    },
    fields: {
      amount: "核销金额",
      suggested: "建议金额",
      maximum: "最大金额",
      unallocated: "未核销入金",
      remaining: "剩余债权/债务",
      selectPayment: "选择收款/付款",
      selectObligation: "选择债权/债务",
      searchCandidates: "搜索候选",
    },
    empty: {
      noEligiblePayment: "没有可核销的收款/付款",
      noEligibleObligation: "没有可核销的债权/债务",
    },
    validation: {
      INVALID: "请输入正整数金额。",
      OVER_PAYMENT: "金额超过未核销入金余额。",
      OVER_OBLIGATION: "金额超过债权/债务剩余余额。",
    },
    errors: {
      versionConflict: "状态已被其他操作更新。请重新确认金额后再提交。",
      reloadFailed: "重新加载版本失败。",
    },
    void: {
      title: "撤销核销",
      description: "这将作废消込记录，不会自动冲销已过账仕译。",
      reason: "原因（可选）",
      confirm: "撤销核销",
      reversalRequired: "已过账。请先进行取消仕译，再撤销消込。",
      viewProgress: "查看会计进度",
    },
    failureCodes: {
      UNKNOWN: "核销失败。",
      SETTLEMENT_FEATURE_DISABLED: "核销功能当前已关闭。",
      SETTLEMENT_PAYMENT_NOT_FOUND: "找不到收款/付款。",
      SETTLEMENT_OBLIGATION_NOT_FOUND: "找不到债权/债务。",
      SETTLEMENT_ALLOCATION_NOT_FOUND: "找不到核销记录。",
      SETTLEMENT_PAYMENT_NOT_CONFIRMED: "仅已确认的收款/付款可核销。",
      SETTLEMENT_PAYMENT_CANCELLED: "已取消的收款/付款不可核销。",
      SETTLEMENT_OBLIGATION_NOT_OPEN: "仅 OPEN 状态的债权/债务可核销。",
      SETTLEMENT_DIRECTION_MISMATCH: "收款/付款方向与债权/债务方向不匹配。",
      SETTLEMENT_ACCOUNTING_BOOK_MISMATCH: "会计账簿不一致。",
      SETTLEMENT_CURRENCY_UNSUPPORTED: "仅支持日元核销。",
      SETTLEMENT_AMOUNT_INVALID: "核销金额无效。",
      SETTLEMENT_PAYMENT_OVER_ALLOCATED: "核销金额超过收款/付款金额。",
      SETTLEMENT_OBLIGATION_OVER_ALLOCATED: "核销金额超过债权/债务金额。",
      SETTLEMENT_PAYMENT_VERSION_CONFLICT: "收款/付款已被并发更新。",
      SETTLEMENT_OBLIGATION_VERSION_CONFLICT: "债权/债务已被并发更新。",
      SETTLEMENT_ALLOCATION_VERSION_CONFLICT: "核销记录已被并发更新。",
      SETTLEMENT_CONCURRENCY_CONFLICT: "核销并发冲突，请重试。",
      SETTLEMENT_STATUS_TRANSITION_INVALID: "无效的核销状态转换。",
      SETTLEMENT_VOID_REQUIRES_REVERSAL: "已过账 — 需要先冲销。",
      IDEMPOTENCY_KEY_REQUIRED: "需要幂等键。",
      IDEMPOTENCY_KEY_INVALID: "幂等键无效。",
      IDEMPOTENCY_IN_PROGRESS: "相同请求仍在处理中。",
      IDEMPOTENCY_KEY_PAYLOAD_CONFLICT: "幂等键被不同载荷复用。",
    },
  },

  settlementAccounting: {
    section: {
      title: "核销与会计进度",
      empty: "该义务尚无核销记录。",
      viewPayment: "查看收付款",
    },
    panel: {
      title: "会计进度",
      allocationAmount: "核销金额",
      retryHint:
        "重试会从当前状态继续会计处理，不会重新确认核销、交易、科目或分录。",
    },
    stages: {
      settlement: "核销",
      transaction: "交易",
      classification: "科目",
      journal: "分录",
      posting: "过账",
      posted: "已过账",
    },
    allocationStatus: {
      CONFIRMED: "已核销",
      VOIDED: "已作废",
    },
    actions: {
      retry: "重试会计处理",
      resolveDate: "指定记账日期",
      configureBankGlMapping: "配置银行科目映射",
      setArApAccount: "设置应收/应付科目",
    },
    fields: {
      accountingDate: "记账日期",
      businessTransaction: "业务交易",
      classification: "会计分类",
      journalDraft: "分录草稿",
      posting: "过账请求",
      postedJournal: "已过账分录",
    },
    warnings: {
      needsReversal: "已过账，必须通过冲销分录处理。作废核销不会自动回滚总账。",
    },
    featureDisabled: {
      banner:
        "核销本身仍然有效。当前租户已关闭会计编排，不会自动推进会计处理。",
    },
    dateDialog: {
      title: "指定记账日期",
      description: "付款日期不会改变。记账日期仅用于会计/过账。",
      paymentDate: "付款日期（只读）",
      paymentDateHint: "付款日期作为证据保留，不会被修改。",
      accountingDate: "记账日期",
      openPeriodHint: "仅可选择 OPEN 会计期间内的日期。",
      submit: "确认",
      errors: {
        required: "请填写记账日期。",
      },
    },
    outcomes: {
      UNKNOWN: { label: "状态未知", description: "无法确认会计进度。" },
      SOURCE_NOT_READY: { label: "准备中", description: "会计来源尚未就绪。" },
      WAITING_ACCOUNTING_DATE: {
        label: "需要指定记账日期",
        description: "付款日期不在 OPEN 期间，需显式指定记账日期。",
      },
      WAITING_BANK_ACCOUNT_MAPPING: {
        label: "等待银行科目映射",
        description: "请配置银行账户 GL 映射。",
      },
      WAITING_AR_AP_ACCOUNT: {
        label: "等待应收/应付科目",
        description: "请为义务设置应收/应付科目。",
      },
      WAITING_TRANSACTION_CONFIRMATION: {
        label: "等待交易确认",
        description: "需要确认业务交易。",
      },
      WAITING_CLASSIFICATION_CONFIRMATION: {
        label: "等待科目确认",
        description: "需要确认会计分类。",
      },
      CLASSIFICATION_ENSURED: {
        label: "已生成科目建议",
        description: "分类已创建，等待确认。",
      },
      JOURNAL_ENSURED: {
        label: "已生成分录草稿",
        description: "分录草稿已创建，等待确认。",
      },
      WAITING_JOURNAL_CONFIRMATION: {
        label: "等待分录确认",
        description: "需要确认分录。",
      },
      WAITING_JOURNAL_REVIEW: {
        label: "等待分录审核",
        description: "需要审核通过分录。",
      },
      WAITING_OPEN_PERIOD: {
        label: "等待期间 OPEN",
        description: "目标会计期间未 OPEN。请检查日期或期间。",
      },
      ORCHESTRATION_DISABLED: {
        label: "会计编排已关闭",
        description: "功能开关关闭了自动会计处理。",
      },
      POSTING_REQUESTED: {
        label: "已请求过账",
        description: "等待过账处理开始。",
      },
      POSTING_ALREADY_ENSURED: {
        label: "过账请求已存在",
        description: "过账请求已确保。",
      },
      POSTING_IN_PROGRESS: {
        label: "过账处理中",
        description: "过账工作器正在处理。",
      },
      POSTED: {
        label: "已过账",
        description: "已创建过账分录。核销状态仍为已核销。",
      },
      POSTING_RETRYABLE_FAILED: {
        label: "过账失败（可重试）",
        description: "过账处于可重试失败状态。",
      },
      POSTING_PERMANENTLY_FAILED: {
        label: "过账失败（永久）",
        description: "过账永久失败，需要运维介入。",
      },
      NEEDS_REVERSAL: {
        label: "需要冲销分录",
        description: "已过账，必须走冲销流程。",
      },
      ACCOUNTING_REVERSED: {
        label: "会计已冲销",
        description: "对应冲销分录已完成。",
      },
      TARGET_TERMINAL: { label: "终态", description: "无需继续会计确保。" },
      FAILED: { label: "处理失败", description: "会计处理失败，可考虑重试。" },
      ALREADY_READY_FOR_POSTING: {
        label: "已可过账",
        description: "人工门控完成，可创建过账请求。",
      },
      POSTING_BLOCKED_FOUNDATION_FLAG: {
        label: "过账门控阻止",
        description: "基础过账开关阻止了过账。",
      },
      BOOK_MISMATCH: {
        label: "账簿不一致",
        description: "付款与义务的会计账簿不一致。",
      },
      ACCOUNT_INACTIVE: {
        label: "科目未启用",
        description: "快照科目不是 ACTIVE。",
      },
      DIRECTION_SIDE_MISMATCH: {
        label: "方向/侧别不匹配",
        description: "付款方向与义务侧别不一致。",
      },
    },
    failureCodes: {
      UNKNOWN: "会计处理失败。",
      SETTLEMENT_ACCOUNTING_FEATURE_DISABLED: "会计编排当前已关闭。",
      SETTLEMENT_ACCOUNTING_DATE_REQUIRED: "需要指定记账日期。",
      SETTLEMENT_ACCOUNTING_PERIOD_NOT_OPEN: "指定日期的会计期间不是 OPEN。",
      SETTLEMENT_ACCOUNTING_BOOK_MISMATCH: "会计账簿不一致。",
      SETTLEMENT_ACCOUNTING_BANK_ACCOUNT_MAPPING_REQUIRED: "需要银行 GL 映射。",
      SETTLEMENT_ACCOUNTING_AR_AP_ACCOUNT_REQUIRED: "需要设置应收/应付科目。",
      SETTLEMENT_VOID_REQUIRES_REVERSAL: "已过账，需要冲销分录。",
      SETTLEMENT_ALLOCATION_NOT_FOUND: "未找到核销。",
      ACCOUNTING_PERIOD_NOT_OPEN: "会计期间不是 OPEN。",
      REVIEW_CASE_NOT_APPROVED: "分录审核尚未通过。",
    },
  },

  bankAccountSettings: {
    page: {
      title: "账户设定",
      subtitle: "统一管理银行/信用卡账户登记，以及会计账簿的 GL 科目映射。",
    },
    tabs: {
      accounts: "账户主数据",
      glMappings: "GL 映射",
    },
  },
  bankGlMapping: {
    page: {
      title: "银行口座 会計設定",
      subtitle: "管理 workspace 银行账户与 GL 科目的映射关系。",
    },
    fields: {
      bankAccount: "银行账户",
      maskedAccount: "账号（掩码）",
      bankBranch: "银行/支行",
      accountingBook: "会计账簿",
      mappedAccount: "映射科目",
      status: "映射状态",
      updatedAt: "更新时间",
      action: "操作",
    },
    filters: {
      bankAccount: "银行账户",
      mappingStatus: "映射状态",
      allBankAccounts: "全部账户",
      allStatuses: "全部",
      mapped: "已映射",
      unmapped: "未映射",
    },
    status: {
      mapped: "已映射",
      unmapped: "未映射",
    },
    actions: {
      configure: "配置映射",
      change: "更改映射",
      save: "保存映射",
      saving: "保存中…",
      archive: "归档映射",
      archiving: "归档中…",
      refresh: "刷新",
      return: "返回",
    },
    dialog: {
      createTitle: "创建银行科目映射",
      editTitle: "编辑银行科目映射",
      description:
        "选择同一会计账簿下的 ACTIVE ASSET 科目。不支持 9990 暂记科目。",
    },
    archiveDialog: {
      title: "归档此映射？",
      description: "归档后该映射不再生效，历史会计快照不会被改写。",
    },
    selector: {
      placeholder: "选择科目",
      ariaLabel: "GL 科目选择",
    },
    empty: {
      title: "暂无映射数据",
      description: "请先添加银行账户，或调整筛选条件。",
      manageAccounts: "管理银行账户",
    },
    readOnly: "只读",
    errors: {
      loadFailed: "加载银行科目映射失败",
      noPermissionTitle: "无访问权限",
      noPermission: "您没有查看银行科目映射的权限。",
    },
    failureCodes: {
      UNKNOWN: "银行科目映射操作失败。",
      WORKSPACE_BANK_ACCOUNT_NOT_FOUND: "未找到银行账户。",
      WORKSPACE_BANK_ACCOUNT_ARCHIVED: "银行账户已归档，无法更新映射。",
      SETTLEMENT_ACCOUNTING_BOOK_NOT_ACTIVE: "会计账簿未启用。",
      SETTLEMENT_ACCOUNTING_ACCOUNT_INACTIVE: "所选科目未启用。",
      SETTLEMENT_ACCOUNTING_ACCOUNT_NOT_ELIGIBLE:
        "所选科目不符合银行现金科目规则。",
      BANK_ACCOUNT_GL_MAPPING_NOT_FOUND: "未找到映射记录，已刷新列表。",
      WORKSPACE_PERMISSION_DENIED: "您没有管理银行科目映射的权限。",
    },
  },

  bankAccounts: {
    page: {
      title: "银行账户",
      subtitle:
        "登记工作区内的银行账户与信用卡账户，作为流水导入与会计映射的前提。",
    },
    actions: {
      create: "添加账户",
      edit: "编辑",
      archive: "归档",
    },
    filters: {
      status: "状态",
      active: "有效",
      archived: "已归档",
      all: "全部",
    },
    fields: {
      displayName: "显示名称",
      accountType: "账户类型",
      bankBranch: "银行 / 支行",
      bankCode: "银行代码",
      bankName: "银行名称",
      branchCode: "支行代码",
      branchName: "支行名称",
      accountNumberMasked: "账号（掩码）",
      externalAccountKey: "外部账户键",
      currency: "币种",
      status: "状态",
      updatedAt: "更新时间",
      action: "操作",
    },
    accountTypes: {
      ORDINARY: "普通存款",
      CURRENT: "活期/当座",
      SAVINGS: "储蓄存款",
      CREDIT_CARD: "信用卡",
      OTHER: "其他",
    },
    status: {
      active: "有效",
      archived: "已归档",
    },
    placeholders: {
      externalAccountKey: "例如：BANK_001",
      accountNumberMasked: "例如：****1234",
    },
    hints: {
      externalAccountKey: "工作区内唯一标识，创建后不可修改。",
      accountNumberMasked: "不可保存明文账号，请仅填写掩码后的值。",
    },
    dialog: {
      createTitle: "添加银行账户",
      editTitle: "编辑银行账户",
      description: "设置显示名称与账户属性。请勿输入明文账号。",
    },
    archiveDialog: {
      title: "归档此账户？",
      description: "将归档「{name}」。归档后无法用于新导入，既有数据仍保留。",
    },
    empty: {
      title: "暂无银行账户",
      descriptionWithCreate:
        "添加银行或信用卡账户后，即可开始流水导入与 GL 映射。",
      descriptionReadOnly: "没有可显示的银行账户。",
      filteredTitle: "没有符合条件的账户",
      filteredDescription: "请调整状态筛选。",
    },
    readOnly: "只读",
    errors: {
      loadFailed: "加载银行账户失败",
      createFailed: "创建银行账户失败",
      updateFailed: "更新银行账户失败",
      archiveFailed: "归档银行账户失败",
      noPermissionTitle: "无访问权限",
      noPermission: "您没有查看银行账户的权限。",
    },
  },

  accountingExport: {
    title: "会计数据导出",
    description: "将已过账分录导出为通用 CSV 格式。",
    format: {
      GENERIC_CSV: "通用 CSV",
      unknown: "未知格式",
    },
    status: {
      REQUESTED: "已受理",
      GENERATING: "生成中",
      READY: "可下载",
      FAILED: "生成失败",
      EXPIRED: "已过期",
      processing: "生成中",
    },
    table: {
      requestedAt: "请求时间",
      book: "会计账簿",
      period: "分录日期区间",
      format: "格式",
      status: "状态",
      lineCount: "行数",
      expiresAt: "有效期",
      actions: "操作",
    },
    filters: {
      status: "状态",
      book: "会计账簿",
      allStatuses: "全部状态",
      allBooks: "全部账簿",
      reset: "重置筛选",
    },
    actions: {
      create: "新建导出",
      download: "下载",
      retry: "重试",
      reExport: "按相同条件新建",
    },
    create: {
      title: "新建导出",
      description: "按分录日期区间导出已过账分录。",
      book: "会计账簿",
      bookPlaceholder: "选择会计账簿",
      periodLabel: "分录日期区间",
      periodHint: "请使用分录日期（journalDate）语义指定区间。",
      format: "导出格式",
      submit: "创建导出",
      errors: {
        bookRequired: "请选择会计账簿。",
        periodRequired: "请指定日期区间。",
        invalidPeriod: "开始日期不能晚于结束日期。",
        periodTooLong: "日期区间最长 {maxDays} 天。",
      },
    },
    retry: {
      confirmTitle: "重试此导出？",
      confirmDescription:
        "将使用创建该导出时冻结的快照数据，从同一导出请求重新生成文件。",
    },
    empty: {
      title: "暂无导出记录",
      descriptionWithCreate: "导出已过账分录，供会计软件导入。",
      descriptionReadOnly: "没有可显示的导出记录。",
      filteredTitle: "没有符合条件的记录",
      filteredDescription: "当前筛选条件下没有导出记录。",
    },
    featureDisabled: {
      banner: "当前无法创建新的导出。您仍可查看并下载已有导出。",
    },
    errors: {
      listFailed: "加载导出列表失败。",
      createFailed: "创建导出失败。",
      retryFailed: "重试导出失败。",
      downloadFailed: "获取下载链接失败。",
      readForbidden: "您没有权限查看此页面。",
      genericFailure: "导出生成失败。",
    },
    failureCodes: {
      ACCOUNTING_EXPORT_FEATURE_DISABLED: "当前无法创建新的导出。",
      ACCOUNTING_EXPORT_INVALID_PERIOD: "指定的日期区间无效。",
      ACCOUNTING_EXPORT_TOO_LARGE: "导出数据量过大。",
      ACCOUNTING_EXPORT_EXPIRED: "导出文件已过期，请创建新的导出。",
      ACCOUNTING_EXPORT_NOT_READY: "导出文件尚未就绪。",
      ACCOUNTING_EXPORT_RETRY_NOT_ALLOWED: "此导出不可重试。",
      ACCOUNTING_EXPORT_FORMAT_NOT_SUPPORTED: "不支持的导出格式。",
      ENQUEUE_FAILED: "启动导出处理失败，请重试。",
      WORKER_TIMEOUT: "导出处理超时。",
      GENERATION_FAILED: "生成导出文件失败。",
      STORAGE_UPLOAD_FAILED: "保存导出文件失败。",
      ACCOUNTING_EXPORT_SOURCE_QUERY_FAILED: "读取导出源数据失败。",
      WORKSPACE_PERMISSION_DENIED: "您没有执行此操作的权限。",
      IDEMPOTENCY_KEY_REUSED: "该请求已处理。",
    },
  },
  bankImport: {
    title: "银行流水导入",
    description: "上传银行流水 CSV 并查看导入批次状态。",
    status: {
      RECEIVED: "已受理",
      QUEUED: "排队中",
      PARSING: "解析中",
      PARSED: "解析完成",
      NORMALIZING: "标准化中",
      READY_FOR_MATCHING: "待匹配",
      PARTIALLY_READY: "部分就绪",
      FAILED: "导入失败",
      CANCELLED: "已取消",
      processing: "处理中",
    },
    statusPhase: {
      WAITING: "等待执行",
      RUNNING: "执行中",
      SUCCEEDED: "导入成功",
      FAILED: "导入失败",
    },
    parseStatus: {
      STORED: "已保存",
      PARSE_ERROR: "解析错误",
    },
    normalizationStatus: {
      NORMALIZED: "已标准化",
      FAILED: "标准化失败",
      DUPLICATE_SKIPPED: "重复已跳过",
      unknown: "未知",
    },
    direction: {
      WITHDRAWAL: "支出",
      DEPOSIT: "收入",
      unknown: "未知",
    },
    errorStage: {
      PARSING: "解析阶段",
      NORMALIZATION: "标准化阶段",
      unknown: "未知",
    },
    table: {
      created: "创建时间",
      bankAccount: "银行账户",
      file: "文件",
      rows: "行数",
      normalized: "已标准化",
      duplicate: "重复",
      failed: "失败",
      status: "状态",
      createdBy: "创建者",
      actions: "操作",
    },
    filters: {
      status: "状态",
      bankAccount: "银行账户",
      allStatuses: "全部状态",
      allAccounts: "全部账户",
      apply: "筛选",
      reset: "重置",
    },
    actions: {
      upload: "上传流水",
      download: "下载",
      retry: "重试",
      cancel: "取消",
      detail: "详情",
    },
    upload: {
      title: "上传银行流水",
      description: "选择工作区账户并上传对应格式的 CSV 文件。",
      bankAccount: "工作区账户",
      bankAccountPlaceholder: "选择账户",
      file: "CSV 文件",
      fileHint: "选择 .csv 文件。乐天卡 e-navi 支持 UTF-8 / Shift-JIS。",
      fileDrop: "点击选择或拖拽文件到此处",
      clearFile: "移除文件",
      noAccounts: "当前工作区尚无可用的银行账户。",
      noCardAccounts: "尚无信用卡账户可用于卡明细导入。可在下方创建。",
      format: "解析格式",
      formatOptions: {
        GENERIC_BANK_CSV_V1: "GENERIC_BANK_CSV_V1（通用银行 CSV）",
        RAKUTEN_ENAVI_CARD_CSV_V1: "乐天卡 e-navi CSV",
      },
      formatHints: {
        GENERIC_BANK_CSV_V1:
          "英文字段头（transaction_date, amount, direction, description）的 UTF-8 CSV。",
        RAKUTEN_ENAVI_CARD_CSV_V1:
          "含 利用日 / 利用店名・商品名 / 利用金額 的 e-navi 明细 CSV，需绑定信用卡账户。",
      },
      downloadTemplate: "下载模板",
      createCardAccountTitle: "创建信用卡账户",
      createCardAccountHint: "e-navi 明细只能导入到 CREDIT_CARD 类型账户。",
      createCardAccountPlaceholder: "例如：乐天卡",
      createCardAccount: "创建信用卡账户",
      creatingCardAccount: "创建中…",
      submit: "开始导入",
      errors: {
        bankAccountRequired: "请选择工作区账户。",
        fileRequired: "请选择 CSV 文件。",
        fileType: "仅支持 .csv 文件。",
        cardAccountNameRequired: "请输入信用卡账户名称。",
      },
    },
    retry: {
      confirmTitle: "重试此导入？",
      confirmDescription: "将重新解析并标准化原始文件。",
    },
    cancel: {
      confirmTitle: "取消此导入？",
      confirmDescription: "已排队的批次将被取消，已处理的数据保留用于审计。",
    },
    empty: {
      title: "暂无导入批次",
      descriptionWithCreate: "上传银行流水 CSV 文件以开始导入。",
      descriptionReadOnly: "没有可显示的导入批次。",
      filteredTitle: "没有符合条件的批次",
      filteredDescription: "当前筛选条件下没有导入批次。",
    },
    featureDisabled: {
      banner: "当前无法创建新的导入批次。您仍可查看已有批次与下载历史。",
    },
    errors: {
      listFailed: "加载导入批次列表失败。",
      createFailed: "创建导入批次失败。",
      createCardAccountFailed: "创建信用卡账户失败。",
      retryFailed: "重试导入批次失败。",
      cancelFailed: "取消导入批次失败。",
      downloadFailed: "获取下载链接失败。",
      detailFailed: "加载批次详情失败。",
      bankAccountsFailed: "加载银行账户列表失败。",
      readForbidden: "您没有权限查看此页面。",
      genericFailure: "导入处理失败。",
    },
    detail: {
      title: "批次详情",
      empty: "暂无数据",
      noErrors: "暂无错误",
      prevPage: "上一页",
      nextPage: "下一页",
      pageOf: "第 {page} / {total} 页",
      tabs: {
        summary: "概要",
        raw: "原始行",
        normalized: "标准化行",
        errors: "错误",
        audit: "审计时间线",
      },
      summary: {
        status: "状态",
        created: "创建时间",
        finished: "完成时间",
        attempt: "尝试次数",
        rawCount: "原始行数",
        normalizedCount: "标准化行数",
        duplicateCount: "重复行数",
        failedCount: "失败行数",
        blankCount: "空行数",
      },
      raw: {
        lineNumber: "行号",
        attempt: "尝试",
        parseStatus: "解析状态",
        payload: "原始内容",
      },
      normalized: {
        line: "行",
        date: "交易日",
        amount: "金额",
        direction: "方向",
        description: "描述",
        status: "标准化状态",
      },
      errors: {
        line: "行",
        stage: "阶段",
        code: "错误码",
        message: "说明",
      },
      audit: {
        actor: "操作者",
      },
    },
    audit: {
      "bank_import.received": "已受理",
      "bank_import.queued": "已入队",
      "bank_import.retried": "已重试",
      "bank_import.cancelled": "已取消",
      "bank_import.enqueue_failed": "入队失败",
      "bank_import.download_url_issued": "下载链接已签发",
      "bank_import.parsing_started": "解析开始",
      "bank_import.parsed": "解析完成",
      "bank_import.normalization_started": "标准化开始",
      "bank_import.ready_for_matching": "待匹配就绪",
      "bank_import.partially_ready": "部分就绪",
      "bank_import.failed": "导入失败",
      "bank_import.line_failed": "行处理失败",
    },
    lineErrorMessages: {
      BANK_IMPORT_REQUIRED_HEADER_MISSING: "缺少必需的表头。",
      BANK_IMPORT_DUPLICATE_HEADER: "存在重复的表头。",
      BANK_IMPORT_COLUMN_LIMIT_EXCEEDED: "列数超出限制。",
      BANK_IMPORT_ROW_LIMIT_EXCEEDED: "行数超出限制。",
      BANK_IMPORT_CSV_MALFORMED: "CSV 格式错误。",
      BANK_IMPORT_ROW_COLUMN_COUNT_MISMATCH: "行的列数与表头不一致。",
      BANK_LINE_INVALID_DATE: "日期格式无效。",
      BANK_LINE_INVALID_AMOUNT: "金额格式无效。",
      BANK_LINE_INVALID_DIRECTION: "方向字段无效。",
      BANK_LINE_UNSUPPORTED_CURRENCY: "不支持的币种。",
      BANK_LINE_EMPTY_DESCRIPTION: "描述为空。",
      BANK_LINE_DUPLICATE: "重复行。",
      BANK_LINE_NORMALIZATION_FAILED: "标准化失败。",
    },
    failureCodes: {
      BANK_IMPORT_FEATURE_DISABLED: "当前无法创建新的导入批次。",
      BANK_IMPORT_ENQUEUE_FAILED: "启动导入处理失败，请重试。",
      BANK_IMPORT_STORAGE_FAILED: "保存文件失败。",
      BANK_IMPORT_UNSUPPORTED_FILE_TYPE: "不支持的文件类型。",
      BANK_IMPORT_FILE_TOO_LARGE: "文件大小超出限制。",
      BANK_IMPORT_UNSUPPORTED_ENCODING: "不支持的文件编码。",
      BANK_IMPORT_EMPTY_FILE: "文件为空。",
      BANK_IMPORT_REQUIRED_HEADER_MISSING: "缺少必需的表头。",
      BANK_IMPORT_DUPLICATE_HEADER: "存在重复的表头。",
      BANK_IMPORT_COLUMN_LIMIT_EXCEEDED: "列数超出限制。",
      BANK_IMPORT_ROW_LIMIT_EXCEEDED: "行数超出限制。",
      BANK_IMPORT_CSV_MALFORMED: "CSV 格式错误。",
      BANK_IMPORT_SOURCE_UNAVAILABLE: "原始文件不可用。",
      BANK_IMPORT_PARSING_FAILED: "解析失败。",
      BANK_IMPORT_NORMALIZATION_FAILED: "标准化失败。",
      BANK_IMPORT_ORIGINAL_UNAVAILABLE: "原始文件不可用。",
      BANK_IMPORT_ORIGINAL_EXPIRED: "原始文件已过期。",
      BANK_IMPORT_NOT_FOUND: "导入批次不存在。",
      BANK_IMPORT_RETRY_NOT_ALLOWED: "此批次不可重试。",
      BANK_IMPORT_CANCEL_NOT_ALLOWED: "此批次不可取消。",
      BANK_LINE_INVALID_DATE: "日期格式无效。",
      BANK_LINE_INVALID_AMOUNT: "金额格式无效。",
      BANK_LINE_INVALID_DIRECTION: "方向字段无效。",
      BANK_LINE_UNSUPPORTED_CURRENCY: "不支持的币种。",
      BANK_LINE_EMPTY_DESCRIPTION: "描述为空。",
      BANK_LINE_DUPLICATE: "重复行。",
      BANK_LINE_NORMALIZATION_FAILED: "标准化失败。",
      IDEMPOTENCY_KEY_REQUIRED: "缺少幂等键。",
      IDEMPOTENCY_KEY_REUSED: "幂等键已被使用。",
      ENQUEUE_FAILED: "入队失败。",
      BANK_ACCOUNT_NOT_FOUND: "银行账户不存在。",
      BANK_ACCOUNT_ARCHIVED: "银行账户已归档。",
      BANK_IMPORT_ACCOUNT_TYPE_MISMATCH: "所选账户类型与此 CSV 格式不匹配。",
      WORKSPACE_PERMISSION_DENIED: "您没有执行此操作的权限。",
    },
  },

  periodReconciliation: {
    title: "期间对账",
    subtitle: "按期间核对银行明细与业务交易",
    status: {
      matched: "已匹配",
      bankOnly: "仅银行侧",
      businessOnly: "仅业务侧",
      possibleMatch: "有匹配候选",
      ignored: "已忽略",
    },
    summary: {
      bankMatchRate: "银行匹配率",
      bankMatchRateHelp: "银行明细中已匹配的比例（忽略项不计入分母）",
      businessCoverageRate: "业务覆盖率",
      businessCoverageRateHelp: "合格业务交易中已与银行明细匹配的比例",
      bankAmount: "银行金额",
      businessAmount: "业务交易金额",
      bankCount: "银行明细 {count} 笔",
      businessCount: "业务交易 {count} 笔",
      periodGap: "期间金额差距（参考）",
      periodGapTooltip: "银行明细与业务交易的期间合计差，不代表会计差异结算。",
      periodGapNote: "仅供参考，不是差异结算。",
      gapOutgoing: "支出侧",
      gapIncoming: "收入侧",
    },
    exceptions: {
      bankOnly: "仅银行侧",
      businessOnly: "仅业务侧",
      possibleMatch: "有匹配候选",
    },
    filters: {
      period: "期间",
      account: "银行账户",
      matchState: "匹配状态",
      direction: "收支方向",
      flow: "现金流",
      allAccounts: "全部账户",
      allMatchStates: "全部状态",
      allDirections: "全部",
      allFlows: "全部",
      thisMonth: "本月",
      prevMonth: "上月",
      advanced: "高级筛选",
      includeAllSources: "显示全部合格来源",
      timezoneHint: "期间使用工作区时区（{timezone}）的日历日",
    },
    tabs: {
      label: "期间对账标签",
      overview: "概览",
      bank: "银行明细",
      business: "业务交易",
      difference: "差异",
    },
    direction: {
      WITHDRAWAL: "出金",
      DEPOSIT: "入金",
    },
    flow: {
      OUTFLOW: "流出",
      INFLOW: "流入",
    },
    bankTable: {
      date: "交易日",
      account: "银行账户",
      description: "摘要",
      counterparty: "对手方",
      reference: "参考号",
      direction: "方向",
      amount: "金额",
      status: "状态",
      actions: "操作",
    },
    businessTable: {
      date: "交易日",
      counterparty: "对手方",
      type: "交易类型",
      amount: "金额",
      paymentMethod: "支付方式",
      source: "来源",
      status: "状态",
      reconStatus: "对账状态",
      actions: "操作",
    },
    actions: {
      refresh: "刷新",
      apply: "应用",
      openMatching: "去核对",
      openBankLine: "查看银行明细",
      openDocument: "打开票据",
    },
    difference: {
      bankSection: "银行侧（仅银行 / 有候选）",
      bankHint: "银行明细分页独立。",
      businessSection: "业务侧（仅业务）",
      businessHint: "业务交易分页独立。",
    },
    overview: {
      hint: "点击异常卡片可跳转到对应列表标签。",
    },
    empty: {
      noSummary: "无法显示期间汇总。",
      noExceptionsTitle: "没有未匹配差异",
      noExceptionsBody: "本期间没有仅银行侧、仅业务侧或匹配候选。",
    },
    errors: {
      featureDisabled: "期间对账功能已关闭。",
      featureDisabledHelp:
        "请启用 bank.reconciliation.period.enabled。不影响银行流水匹配。",
      invalidPeriod: "期间无效。请使用 YYYY-MM-DD，且开始日不晚于结束日。",
      periodTooLarge: "期间跨度超过 12 个月上限。",
      unsupportedCurrency: "期间对账目前仅支持 JPY。",
      permissionDenied: "您没有查看期间对账的权限。",
      permissionDeniedHelp:
        "需要同时具备 bank_match.read 与 transaction.read。",
      summaryFailed: "汇总加载失败。",
      bankFailed: "银行明细加载失败。",
      businessFailed: "业务交易加载失败。",
    },
  },
  bankEvidence: {
    title: "银行明细×传票核对",
    description: "将出金明细与已确认传票段关联。不显示出金以外的方向。",
    lineStatus: {
      UNLINKED: "未核对",
      LINKED: "已核对",
      EXCLUDED: "已排除",
      NEEDS_REVIEW: "需确认",
    },
    rule: {
      EXACT: "完全匹配",
      FUZZY_AMOUNT_DATE: "金额·日期近似",
      FUZZY_AMOUNT_CPTY: "金额·交易对方近似",
      AMOUNT_ONLY: "仅金额",
      MANUAL: "手动",
      unknown: "未知",
    },
    linkSource: {
      CANDIDATE: "候选",
      MANUAL: "手动",
      AUTO: "批量核对",
    },
    reason: {
      exact: "金额、日期、交易对方一致",
      fuzzyAmountDate: "金额·日期接近",
      fuzzyAmountCpty: "金额·交易对方接近",
      amountOnly: "仅金额一致",
      manual: "手动选择",
      unknown: "未知",
    },
    excludeReasons: {
      FEE: "手续费",
      PERSONAL: "个人使用",
      PAPER_HELD: "纸质凭证另行保管",
      DUPLICATE_ADJUST: "重复·调整",
      OTHER: "其他",
    },
    historyEvent: {
      CANDIDATES_GENERATED: "已生成候选",
      LINKED: "已与传票核对",
      EXCLUDED: "已排除该明细",
      UNLINKED: "已解除核对",
      NEEDS_REVIEW: "已标为需确认",
      unknown: "未知",
    },
    filters: {
      status: "状态",
      bankAccount: "银行账户",
      dateFrom: "开始日期",
      dateTo: "结束日期",
      dateFromPlaceholder: "选择开始日期",
      dateToPlaceholder: "选择结束日期",
      allStatuses: "全部状态",
      allAccounts: "全部账户",
      apply: "筛选",
      reset: "重置",
    },
    table: {
      date: "日期",
      amount: "金额",
      description: "描述",
      evidenceStatus: "照合状态",
      documentNumber: "传票番号",
      documentNumberUnknown: "番号不明",
      candidateCount: "候选数",
      actions: "操作",
      total: "共 {total} 条 · 第 {page} 页（每页 {pageSize}）",
    },
    pagination: {
      prev: "上一页",
      next: "下一页",
    },
    actions: {
      detail: "详情",
      link: "关联",
      exclude: "排除",
      unlink: "解除",
      refreshCandidates: "刷新候选",
      batchRefreshCandidates: "批量更新候选",
      batchAutoLink: "批量核对",
      searchSegments: "搜索传票段",
      manualSearch: "手动搜索",
      addCandidate: "添加为候选",
    },
    featureDisabled: {
      banner:
        "银行明细×传票核对功能当前已禁用。可浏览列表与详情，但关联、排除与解除不可用。",
    },
    empty: {
      title: "暂无出金明细",
      description: "请先导入银行流水。本页仅显示出金。",
      filteredTitle: "无匹配明细",
      filteredDescription: "请调整状态、账户或日期筛选。",
    },
    detail: {
      title: "照合详情",
      tabs: {
        summary: "摘要",
        history: "历史",
      },
      summary: {
        date: "日期",
        amount: "金额",
        evidenceStatus: "照合状态",
        candidateCount: "候选数",
        linkSource: "关联来源",
        segment: "传票段",
        counterparty: "交易对方",
        reference: "参考号",
        excludeReason: "排除原因",
        linkedDocument: "已核对传票",
      },
    },
    candidate: {
      sectionTitle: "候选传票",
      hint: "对照银行明细与候选传票段，确认后点击关联。",
      empty: "暂无待处理候选。请刷新或使用手动搜索。",
      segment: "传票段",
      score: "分数",
      matchScore: "一致度 {score}%",
      rule: "规则",
      reason: "理由",
      date: "日期",
      amount: "金额",
      counterparty: "交易对方",
      documentNumber: "传票番号",
      description: "摘要",
      actions: "操作",
      selected: "已关联",
    },
    manual: {
      title: "手动搜索传票",
      hint: "按相同金额搜索已确认传票段，添加为候选后再在摘要中确认关联。",
      search: "关键词",
      searchPlaceholder: "交易对方、单据号…",
      date: "日期",
      amount: "金额",
      counterparty: "交易对方",
      documentNumber: "单据号",
      empty: "未找到传票段。请调整搜索或日期范围。",
      moreHidden: "另有 {count} 条未显示",
    },
    history: {
      empty: "暂无历史记录。",
      systemActor: "系统",
    },
    batchRefresh: {
      title: "批量更新候选",
      description:
        "将按当前筛选条件，对最多 1000 条未照合明细异步刷新候选。完成后可在通知中心查看结果。",
    },
    batchAutoLink: {
      title: "批量核对",
      description:
        "将按当前筛选刷新未核对明细的候选：交易日与金额都一致且仅有一个候选时直接核对完成；有多个候选时标为需确认。",
    },
    toast: {
      batchRefreshQueued:
        "已排队更新 {count} 条未照合明细的候选，完成后将发送通知。",
      batchAutoLinkQueued:
        "已排队对 {count} 条未核对明细执行批量核对，完成后将发送通知。",
      candidateAdded: "已添加为候选。",
    },
    exclude: {
      title: "排除明细",
      description: "将该出金标记为无需传票证据。银行明细本身不会被删除。",
      reasonLabel: "原因",
      noteLabel: "备注",
      notePlaceholder: "选择「其他」时至少填写 {min} 个字符",
      noteHint: "选择「其他」时备注至少 {min} 个字符。",
    },
    unlink: {
      title: "解除照合",
      description:
        "移除当前关联或排除，使该明细可再次关联。会计记录不会被修改。",
    },
    failureCodes: {
      BANK_EVIDENCE_FEATURE_DISABLED: "银行明细×传票核对功能已禁用。",
      BANK_EVIDENCE_NOT_FOUND: "未找到明细行或候选。",
      BANK_EVIDENCE_NOT_WITHDRAWAL: "仅出金明细可进行核对。",
      BANK_EVIDENCE_SEGMENT_NOT_ELIGIBLE: "该传票段不符合核对条件。",
      BANK_EVIDENCE_EXCLUDE_REASON_REQUIRED: "请选择排除原因。",
      BANK_EVIDENCE_EXCLUDE_NOTE_REQUIRED:
        "选择「其他」时需填写至少 8 个字符的备注。",
      BANK_EVIDENCE_LINE_ALREADY_ACTIVE: "该明细已关联或已排除。",
      BANK_EVIDENCE_CANDIDATE_NOT_AVAILABLE:
        "候选已过期或不可用，请刷新后重试。",
      BANK_EVIDENCE_BATCH_TOO_LARGE:
        "未照合明细超过 1000 条，请收窄筛选后再试。",
      BANK_EVIDENCE_BATCH_EMPTY: "当前筛选下没有未照合明细。",
      WORKSPACE_PERMISSION_DENIED: "您没有执行此操作的权限。",
      IDEMPOTENCY_KEY_REQUIRED: "缺少幂等键。",
      IDEMPOTENCY_KEY_REUSED: "幂等键已被使用。",
      VALIDATION_FAILED: "校验失败。",
    },
    errors: {
      listFailed: "加载明细失败。",
      detailFailed: "加载详情失败。",
      linkFailed: "关联失败。",
      excludeFailed: "排除失败。",
      unlinkFailed: "解除失败。",
      refreshFailed: "刷新候选失败。",
      batchRefreshFailed: "批量更新候选失败。",
      batchAutoLinkFailed: "批量核对失败。",
      addCandidateFailed: "添加候选失败。",
      segmentSearchFailed: "搜索传票段失败。",
      readForbidden: "您没有查看银行明细×传票核对的权限。",
      conflict: "该明细已在别处更新。请刷新后重试。",
      genericFailure: "操作失败，请重试。",
    },
  },
  bankMatch: {
    title: "银行流水匹配",
    description: "将银行流水行与候选项匹配并确认匹配决策。",
    status: {
      UNKNOWN: "未确定",
      UNMATCHED: "未匹配",
      CANDIDATES_READY: "候选就绪",
      MATCH_SELECTED: "已选候选",
      REVIEW_REQUIRED: "需复核",
      CONFIRMED: "已确认",
    },
    lineStatus: {
      UNMATCHED: "未匹配",
      MATCHED: "已匹配",
      IGNORED: "已忽略",
    },
    direction: {
      WITHDRAWAL: "支出",
      DEPOSIT: "收入",
      unknown: "未知",
    },
    rule: {
      EXACT: "完全一致",
      FUZZY_AMOUNT_DATE: "金额日期近似",
      FUZZY_AMOUNT_COUNTERPARTY: "金额交易方近似",
      AMOUNT_ONLY: "仅金额",
      unknown: "未知",
    },
    targetType: {
      BUSINESS_TRANSACTION: "交易",
      POSTED_JOURNAL: "分录",
      unknown: "未知",
    },
    candidateStatus: {
      PENDING: "待处理",
      SELECTED: "已选择",
      REJECTED: "已拒绝",
      EXPIRED: "已过期",
      unknown: "未知",
    },
    historyEvent: {
      CANDIDATE_SELECTED: "选择候选",
      REVIEW_REQUESTED: "请求复核",
      REVIEW_REJECTED: "退回复核",
      MATCH_CONFIRMED: "确认匹配",
      MATCH_OVERRIDDEN: "强制覆盖",
      VERSION_CONFLICT: "版本冲突",
      unknown: "未知",
    },
    filters: {
      status: "状态",
      bankAccount: "银行账户",
      dateFrom: "开始日期",
      dateFromPlaceholder: "选择开始日期",
      allStatuses: "全部状态",
      allAccounts: "全部账户",
      reset: "重置筛选",
    },
    table: {
      date: "日期",
      amount: "金额",
      direction: "方向",
      description: "描述",
      matchStatus: "匹配状态",
      candidateCount: "候选数",
      decisionStatus: "决策状态",
      actions: "操作",
    },
    actions: {
      detail: "详情",
      select: "选择",
      confirm: "确认",
      override: "覆盖",
      ignore: "忽略",
      unmatch: "解除匹配",
    },
    featureDisabled: {
      banner:
        "银行流水匹配功能当前已关闭。可以查看列表与详情，但不能执行选择、确认或覆盖操作。",
    },
    ignore: {
      title: "忽略流水行",
      description: "将该银行流水标记为无需匹配。不会删除该流水。",
    },
    unmatch: {
      title: "解除匹配",
      description:
        "解除匹配不会撤销、删除或冲销已生成的会计分录。系统将重新生成候选项。",
    },
    orchestration: {
      title: "会计编排",
      retry: "重试",
      lastAttempt: "上次尝试",
      postingRequest: "过账请求",
      postedJournal: "已过账分录",
      waitingReason: "等待原因",
      reversedWarning:
        "匹配后已创建冲销分录。当前匹配仍指向原分录（不会自动迁移或解除）。",
      waitingReasons: {
        TRANSACTION: "等待交易确认",
        CLASSIFICATION: "等待分类确认",
        JOURNAL: "等待分录确认",
        PERIOD: "等待会计期间开放",
        FEATURE: "等待会计编排开关",
      },
      labels: {
        reconciliationOnly: "已关联核对",
        alreadyComplete: "会计处理已完成",
        postingRequested: "已受理过账处理",
        waitingTransaction: "等待确认（交易）",
        waitingClassification: "等待确认（分类）",
        waitingJournal: "等待确认（分录）",
        periodBlocked: "会计期间未开放",
        featureDisabled: "会计编排当前已关闭",
        targetIssue: "目标存在问题",
        inProgress: "会计编排处理中",
      },
      descriptions: {
        reconciliationOnly: "已与既存分录建立关联，不会再次过账。",
        alreadyComplete: "会计处理已经完成。",
        postingRequested: "已创建过账请求，由 Worker 继续处理。",
        waitingTransaction: "需要人工确认交易。这不是错误状态。",
        waitingClassification: "需要人工确认分类。这不是错误状态。",
        waitingJournal: "需要人工确认分录。这不是错误状态。",
        periodBlocked: "目标期间未 OPEN 前无法过账。期间修正后可重试。",
        featureDisabled: "会计编排功能已关闭。启用后可重试。",
        targetNotFound: "找不到匹配目标。",
        targetTerminal: "匹配目标已处于终态，无法继续。",
        inProgress: "正在补齐缺失的会计阶段。",
      },
    },
    paymentEvidence: {
      title: "入出金证据",
      description:
        "从此已确认的银行匹配创建运营入出金草稿。此处不会将入出金分配到应收应付义务。",
      createDraft: "创建入出金草稿",
      featureDisabled:
        "银行匹配 → 入出金草稿当前已关闭。仍可查看证据，但无法创建草稿。",
      sourceMatchSuperseded:
        "已关联的入出金仍存在，但此银行匹配已不再是有效来源。",
      linkedPayment: "已关联入出金",
      viewPayment: "在工作区查看收付款",
      paymentStatus: {
        DRAFT: "草稿",
        CONFIRMED: "已确认",
        CANCELLED: "已取消",
        unknown: "未知",
      },
      eligibility: {
        ELIGIBLE: "可创建入出金草稿。",
        NOT_CONFIRMED: "请先确认匹配后再创建入出金草稿。",
        LINE_NOT_MATCHED: "流水行必须处于已匹配状态。",
        POSTED_JOURNAL_RECONCILIATION_ONLY:
          "已过账分录匹配仅用于核对，不适用入出金草稿。",
        MANUAL_TARGET_NOT_ELIGIBLE: "手工匹配目标无法创建入出金草稿。",
        UNSUPPORTED_TARGET: "此匹配目标无法创建入出金草稿。",
        PAYMENT_ALREADY_EXISTS: "此匹配已关联入出金。",
        PAYMENT_CANCELLED_REPLACEMENT_DEFERRED:
          "已关联已取消的入出金；替换暂缓。",
        FEATURE_DISABLED: "入出金草稿创建已关闭。",
        UNSUPPORTED_CURRENCY: "仅 JPY 流水可创建入出金草稿。",
        INVALID_AMOUNT: "流水金额不适用于入出金草稿。",
        UNKNOWN: "此匹配不可创建入出金草稿。",
      },
    },
    candidate: {
      topHint: "显示分数最高的 {n} 个候选项",
      empty: "暂无候选项。",
      moreHidden: "还有 {count} 个候选项。",
      type: "类型",
      target: "目标",
      score: "分数",
      rule: "规则",
      date: "生成日期",
      amount: "状态",
      actions: "操作",
      selected: "已选择",
    },
    decision: {
      title: "决策",
      reviewRequired: "此匹配需要复核。请由具备确认权限的成员确认。",
      confirmed: "此流水行已确认。",
      noActions: "当前没有可执行的操作。",
    },
    confirm: {
      title: "确认匹配",
      description: "确定使用此候选项确认匹配吗？",
    },
    override: {
      title: "强制覆盖",
      description: "确定要强制确认此匹配吗？需要覆盖权限。",
    },
    detail: {
      title: "流水行详情",
      overridden: "由强制覆盖确认",
      tabs: {
        summary: "概要",
        candidates: "候选项",
        history: "历史",
        audit: "审计",
      },
      summary: {
        date: "交易日期",
        amount: "金额",
        direction: "方向",
        matchStatus: "匹配状态",
        candidateCount: "候选数",
        decisionStatus: "决策状态",
        version: "版本",
        overridden: "覆盖",
        counterparty: "交易对方",
        reference: "参考号",
      },
    },
    history: {
      empty: "暂无历史记录。",
    },
    audit: {
      hint: "审计日志请前往审计中心查看。",
      viaAuditCenter: "在审计中心查看",
    },
    empty: {
      title: "暂无银行流水行",
      description: "导入银行流水后，可在此查看匹配候选项。",
      filteredTitle: "没有符合条件的流水行",
      filteredDescription: "请调整筛选条件。",
    },
    errors: {
      listFailed: "获取流水行列表失败。",
      detailFailed: "获取详情失败。",
      selectFailed: "选择候选项失败。",
      confirmFailed: "确认匹配失败。",
      overrideFailed: "强制覆盖失败。",
      ignoreFailed: "忽略失败。",
      unmatchFailed: "解除匹配失败。",
      retryOrchestrationFailed: "会计编排重试失败。",
      createPaymentDraftFailed: "创建入出金草稿失败。",
      versionConflict: "此流水行已被其他成员更新，请获取最新信息。",
      genericFailure: "发生错误。",
      readForbidden: "您没有查看银行流水匹配的权限。",
    },
    failureCodes: {
      BANK_MATCH_FEATURE_DISABLED: "银行流水匹配功能已关闭。",
      FEATURE_DISABLED: "期间对账功能已关闭。",
      INVALID_PERIOD:
        "期间参数无效。请提供有效的 YYYY-MM-DD，且开始日不得晚于结束日。",
      PERIOD_RANGE_TOO_LARGE: "期间跨度超过 12 个月上限。",
      UNSUPPORTED_CURRENCY_FILTER: "期间对账目前仅支持 JPY。",
      BANK_MATCH_NOT_FOUND: "未找到流水行。",
      BANK_MATCH_NOT_READY: "匹配尚未就绪。",
      BANK_MATCH_CANDIDATE_NOT_FOUND: "未找到候选项。",
      BANK_MATCH_INVALID_TRANSITION: "当前状态不允许此操作。",
      BANK_MATCH_DECISION_CONFLICT: "其他成员同时更新，操作冲突。",
      BANK_MATCH_NO_CANDIDATES: "未指定候选项或目标。",
      BANK_MATCH_OVERRIDE_REQUIRED: "需要强制覆盖权限。",
      BANK_MATCH_TARGET_NOT_FOUND: "未找到匹配目标。",
      BANK_MATCH_TARGET_ALREADY_CONFIRMED: "该目标已被其他流水行确认。",
      BANK_MATCH_ORCH_RETRY_NOT_CONFIRMED: "仅已确认的匹配可重试编排。",
      BANK_MATCH_ORCH_RETRY_FLAG_DISABLED: "会计编排功能已关闭，无法重试。",
      BANK_MATCH_PAYMENT_LINK_FEATURE_DISABLED: "银行匹配 → 入出金草稿已关闭。",
      BANK_MATCH_PAYMENT_DECISION_CONFLICT: "匹配已被同时更新，请刷新后重试。",
      BANK_MATCH_PAYMENT_SOURCE_NOT_CONFIRMED:
        "仅已确认且有效的银行匹配可创建入出金草稿。",
      BANK_MATCH_PAYMENT_TARGET_NOT_ELIGIBLE: "此匹配目标无法创建入出金草稿。",
      BANK_MATCH_PAYMENT_UNSUPPORTED_DIRECTION:
        "该流水方向不支持创建入出金草稿。",
      BANK_MATCH_PAYMENT_UNSUPPORTED_CURRENCY: "仅 JPY 流水可创建入出金草稿。",
      BANK_MATCH_PAYMENT_INVALID_AMOUNT: "流水金额不适用于入出金草稿。",
      WORKSPACE_PERMISSION_DENIED: "您没有执行此操作的权限。",
      IDEMPOTENCY_KEY_REQUIRED: "请求需要幂等键。",
      VALIDATION_FAILED: "输入内容无效。",
    },
  },
  financialReports: {
    title: "财务报表",
    subtitle: "基于不可变会计事实的只读报表投影",
    disclaimer: "本报表是系统生成的会计投影，不等同于税务申报或法定提交文件。",
    filters: {
      reportType: "报表类型",
      basis: "报表基础",
      period: "会计期间",
      fiscalYear: "财政年度",
      account: "科目",
      comparativeMode: "比较模式",
    },
    reportTypes: {
      trialBalance: "试算表",
      balanceSheet: "资产负债表",
      profitAndLoss: "损益表",
      accountDetail: "科目明细",
      fiscalYearSummary: "财政年度摘要",
    },
    basis: {
      liveLedger: "实时总账（暂定）",
      periodSnapshot: "期间结账快照（最终）",
      yearEndSnapshot: "年结快照（最终）",
    },
    comparative: {
      none: "不比较",
      previousPeriod: "上一期间",
      previousFiscalYear: "上一财政年度",
    },
    actions: { generate: "生成报表" },
    export: {
      title: "导出",
      disclaimer:
        "本文件为系统生成的会计报表导出结果，不等同于法定申报或税务提交文件。",
      format: "导出格式",
      locale: "语言",
      generate: "生成导出",
      download: "下载",
      cancel: "取消导出",
      retry: "重试导出",
      polling: "等待导出 Worker 处理…",
      status: "状态",
      checksum: "文件校验和",
      sizeBytes: "文件大小（字节）",
      expiresAt: "过期时间",
      noSnapshot: "请先生成报表快照后再导出",
      formats: {
        csv: "CSV",
        xlsx: "XLSX",
        json: "JSON 包",
      },
      statuses: {
        QUEUED: "排队中",
        PROCESSING: "处理中",
        COMPLETED: "已完成",
        FAILED: "失败",
        CANCELLED: "已取消",
        EXPIRED: "已过期",
      },
      failureCodes: {
        EXPORT_STORAGE_UPLOAD_FAILED: "导出文件上传失败，请稍后重试。",
        EXPORT_CHECKSUM_MISMATCH: "导出文件校验失败，请重试。",
        EXPORT_ARTIFACT_EXPIRED: "导出文件已过期。",
      },
      errors: { failed: "导出失败", noPermission: "无导出权限" },
    },
    header: {
      period: "期间",
      basis: "基础",
      version: "版本",
      checksum: "校验和",
    },
    balanceSheet: {
      equation: "资产 {assets} = 负债 {liabilities} + 权益 {equity}",
    },
    profitAndLoss: {
      summary: "收入 {revenue} - 费用 {expense} = 净利润 {netIncome}",
    },
    sections: { title: "分组" },
    table: {
      account: "科目",
      amount: "金额",
      comparative: "比较值",
      variance: "差异",
    },
    errors: {
      generateFailed: "报表生成失败",
    },
  },
  japaneseFinancialStatements: {
    title: "日本式财务报表",
    subtitle: "基于不可变财务报告快照生成的日本式列示",
    disclaimer:
      "本报表为根据系统会计数据生成的财务展示文件。它不是法定申报表、税务提交文件、审计报告或官方提交文件。请在使用前确认内容。",
    fields: {
      statementType: "报表类型",
      amountUnit: "金额单位",
      snapshotId: "财务报告快照 ID",
      status: "状态",
      profile: "配置文件",
      comparativeMode: "比较模式",
      locale: "显示语言",
      period: "期间",
      checksum: "源校验和",
      sourceChecksum: "源报表校验和",
      presentationChecksum: "列示校验和",
      generatedAt: "生成时间",
    },
    sourceSnapshot: {
      title: "源财务报告快照",
    },
    profile: {
      profileVersion: "配置版本",
      labelVersion: "标签版本",
      sectionOrderVersion: "分组顺序版本",
      disclaimerVersion: "免责声明版本",
    },
    comparative: {
      currentOnly: "仅当期",
      currentAndPrior: "当期与前期",
    },
    types: {
      balanceSheet: "资产负债表",
      profitAndLoss: "损益表",
      trialBalance: "试算表",
      accountDetail: "科目明细",
      comparative: "比较报表",
    },
    amountUnits: {
      yen: "日元",
      thousandYen: "千日元",
      millionYen: "百万日元",
    },
    actions: {
      prepareSource: "生成源报表",
      generate: "生成日本式列示",
    },
    statuses: {
      QUEUED: "排队中",
      PROCESSING: "处理中",
      COMPLETED: "已完成",
      FAILED: "失败",
      CANCELLED: "已取消",
      SUPERSEDED: "已替换",
    },
    table: {
      section: "分组",
      line: "行",
      amount: "金额",
      comparative: "前期",
      variance: "增减率",
    },
    validation: {
      title: "校验结果",
      infoCount: "信息",
      warningCount: "警告",
      errorCount: "错误",
      blockingCount: "阻断",
      severities: {
        INFO: "信息",
        WARNING: "警告",
        ERROR: "错误",
        BLOCKING: "阻断",
      },
    },
    unmapped: {
      title: "未映射科目",
      linkLabel: "查看科目映射",
    },
    presentation: {
      metadata: "列示元数据",
    },
    export: {
      title: "导出",
      disclaimer:
        "此文件为系统生成的日本式财务报表导出，不是法定申报或税务提交文件。",
      format: "导出格式",
      locale: "语言",
      generate: "生成导出",
      download: "下载",
      polling: "正在等待导出 Worker 处理…",
      status: "状态",
      checksum: "文件校验和",
      sizeBytes: "文件大小（字节）",
      blockedHint: "校验阻断项解除前无法导出。",
      formats: { csv: "CSV", xlsx: "XLSX", json: "JSON 包" },
      statuses: {
        QUEUED: "排队中",
        PROCESSING: "处理中",
        COMPLETED: "已完成",
        FAILED: "失败",
        CANCELLED: "已取消",
        EXPIRED: "已过期",
      },
      errors: {
        failed: "导出失败",
        noPermission: "没有导出权限",
        blocked: "存在校验阻断项，无法导出",
      },
    },
    warnings: {
      unmapped: "存在未映射科目，请检查科目报表映射。",
    },
    errors: {
      noBook: "未找到会计账簿",
      noPermission: "没有生成日本式财务报表的权限",
      generateSourceFailed: "源报表生成失败",
      generateFailed: "日本式列示生成失败",
    },
  },
  feedback: {
    title: "我要反馈",
    subject: "联系主题",
    selectPlaceholder: "请选择",
    details: "详细信息",
    detailsPlaceholder: "请描述您遇到的问题或建议...",
    attachments: "附件（可选，最多 3 个）",
    submitting: "提交中...",
    incomplete: "请填写完整信息",
    submitted: "反馈已提交，我们会尽快处理",
    submitFailed: "提交失败",
    thread: {
      me: "我",
      operations: "运营",
      user: "用户",
    },
  },
  feedbackLabels: {
    subjects: {
      bug: "问题反馈",
      feature: "功能建议",
      billing: "账单与配额",
      account: "账号与安全",
      other: "其他",
    },
    statuses: {
      OPEN: "待处理",
      IN_PROGRESS: "处理中",
      RESOLVED: "已解决",
      CLOSED: "已关闭",
    },
  },
  emailEditor: {
    bold: "加粗",
    italic: "斜体",
    underline: "下划线",
    strike: "删除线",
    paragraph: "正文",
    heading2: "小标题",
    heading3: "标题",
    textColor: "字体颜色",
    highlight: "背景高亮",
    clearFormatting: "清除颜色",
    clearHighlight: "清除高亮",
    alignLeft: "左对齐",
    alignCenter: "居中",
    alignRight: "右对齐",
    bulletList: "项目符号列表",
    numberedList: "编号列表",
    link: "链接",
    insertTable: "插入表格",
    undo: "撤销",
    redo: "重做",
    linkPrompt: "链接地址",
  },
  addressLocation: {
    label: "住所",
    areaCodePlaceholder: "请输入 7 位邮编",
    prefixPlaceholder: "都道府县 市区町村 町域",
    addressPlaceholder: "番地、楼层、房间号等详细地址",
    loading: "正在查询地址...",
    notFound: "未找到对应地址。",
  },
  employeeManagement: {
    menu: { employees: "员工一览", retired: "退职者一览", tags: "标签一览" },
    list: {
      title: "员工一览",
      description: "维护员工基本信息、所属与账号发行状态。",
      empty: "暂无员工。",
    },
    retired: {
      title: "退职者一览",
      description: "查看已退职处理的员工。",
      badge: "已退职",
      retiredNotice: "退职日期：{date} / 退职原因：{reason}",
    },
    tags: {
      title: "标签一览",
      description: "维护可付与员工的标签。",
      add: "追加标签",
      edit: "编辑标签",
      empty: "暂无标签。",
      dialogDescription: "设置标签名称、颜色和备注。",
      deleteTitle: "确认删除标签",
      importTitle: "导入标签",
      importDescription: "批量导入标签名称和颜色。",
      exportTitle: "导出标签",
      exportDescription: "选择要导出的标签字段。",
    },
    detail: {
      title: "员工详细",
      subtitle: "员工档案",
      profile: "基本档案",
      employment: "雇佣信息",
      location: "住所",
      account: "账号",
      notFound: "未找到员工。",
    },
    dialog: {
      addEmployee: "追加员工",
      editEmployee: "编辑员工",
      employeeDescription: "录入员工基本信息和所属信息。",
    },
    actions: {
      addEmployee: "追加员工",
      issueAccount: "账号发行",
      resetPassword: "密码重置",
      retire: "退职处理",
      restore: "复元",
      bulkInvite: "批量邀请",
      bulkEmail: "批量送信",
      selectVisible: "选择当前页员工",
    },
    summary: {
      total: "员工数",
      dimension: "统计维度",
      dimensions: {
        employmentType: "雇佣形态",
        workStatus: "勤務状况",
        employmentStatus: "雇佣状况",
        position: "职位",
        jobType: "职种",
      },
    },
    filters: { keyword: "关键字" },
    columns: { employee: "従業員", tags: "标签" },
    fields: {
      employeeNo: "社員番号",
      avatarUrl: "头像",
      birthDate: "生年月日",
      gender: "性别",
      name: "姓名",
      nameKanji: "姓名（汉字）",
      familyName: "姓",
      givenName: "名",
      nameKana: "姓名（片假名）",
      familyNameKana: "姓（片假名）",
      givenNameKana: "名（片假名）",
      hiredOn: "入职日期",
      retiredOn: "退职日期",
      retiredReason: "退职原因",
      hireType: "入职类型",
      phone: "电话",
      email: "邮件地址",
      location: "住所",
      office: "事业所",
      department: "部署",
      workStatus: "勤務状况",
      employmentType: "雇佣形态",
      employmentStatus: "雇佣状况",
      jobType: "职种",
      position: "职位",
      insuranceNumber: "保险证番号",
      insuranceSymbol: "保险证记号",
      remarks: "备注",
      accountIssueStatus: "账号发行状况",
      userId: "系统账号ID",
      lastLoginAt: "最终登录日時",
      tagName: "标签名称",
      tagColor: "标签颜色",
      tagColorDescription: "备注",
      employeeCount: "员工数",
    },
    common: { unselected: "未设置", ageSuffix: "岁", personSuffix: "人" },
    validation: {
      required: "必填项目。",
      email: "请输入有效的邮件地址。",
      japanesePhone: "请输入有效的日本电话号码。",
      maxLength: "请在 {max} 个字符以内输入。",
    },
    avatar: {
      upload: "上传头像",
      uploading: "上传中...",
      clear: "移除",
      hint: "支持 PNG、JPG、WebP，最大 2 MB；保存前会裁剪为正方形。",
      cropTitle: "裁剪头像",
      confirmCrop: "使用头像",
      zoom: "缩放",
      invalid: "请上传有效的图片文件。",
      tooLarge: "头像图片不能超过 2 MB。",
      uploadFailed: "头像上传失败",
      uploaded: "头像已上传",
    },
    bulkInvite: {
      confirmTitle: "确认发送批量邀请",
      confirmDescription: "将向 {count} 名员工发送邀请邮件。",
    },
    bulkEmail: {
      title: "批量给员工送信",
      description: "向选中的员工邮箱发送邮件。",
      subject: "标题",
      message: "正文",
    },
    resetPassword: {
      title: "密码再设定",
      description: "请输入新的登录密码。",
      newPassword: "新密码",
      confirmPassword: "确认新密码",
      passwordPlaceholder: "至少 8 个字符",
      passwordMin: "密码至少需要 8 个字符。",
      passwordMismatch: "两次输入的密码不一致。",
    },
    import: {
      title: "导入员工",
      description: "批量导入员工主数据。",
      employeeNoRequired: "唯一识别员工的编号。",
      familyNameRequired: "姓名（汉字）的姓，必须填写。",
      givenNameRequired: "姓名（汉字）的名，必须填写。",
      familyNameKanaRequired: "姓名（片假名）的姓，必须填写。",
      givenNameKanaRequired: "姓名（片假名）的名，必须填写。",
      tagNameRequired: "标签名称，必须填写。",
    },
    export: { title: "导出员工", description: "选择要导出的字段。" },
    retire: { title: "退职处理", description: "请输入退职日期和退职原因。" },
    history: {
      title: "更新履历",
      actions: {
        CREATE: "追加",
        UPDATE: "更新",
        RETIRE: "退职处理",
        RESTORE: "复元",
      },
    },
    messages: {
      saved: "保存成功",
      deleted: "删除成功",
      accountIssued: "已发送账号邀请",
      passwordResetSent: "密码已重置",
      passwordResetRequested: "密码已重置",
      retired: "退职处理完成",
      restoreTitle: "确认复元该员工",
      restored: "员工已复元",
      emailRequiredForAccount: "员工邮件地址未设定，不能发行账号。",
      selectEmployeesRequired: "请选择员工。",
      bulkInviteAccepted:
        "已发送批量邀请（成功 {count} 件，失败 {failed} 件）。",
      bulkInviteFailed: "批量邀请发送失败。",
      bulkEmailAccepted: "已受理批量送信。",
    },
    enums: {
      gender: {
        MALE: "男性",
        FEMALE: "女性",
        OTHER: "其他",
        UNDISCLOSED: "未设定",
      },
      hireType: { NEW_GRADUATE: "新卒", EXPERIENCED: "既卒" },
      employmentType: {
        EXECUTIVE: "役員",
        REGULAR: "正社員",
        SECONDMENT_IN: "受入出向社員",
        CONTRACT: "契約社員",
        SHOKUTAKU: "嘱託社員",
        PART_TIME: "パート/アルバイト",
        FREELANCE: "フリーランス/業務委託",
        TEMPORARY_DISPATCH_IN: "受入派遣社員",
        DISPATCH_STAFF: "派遣スタッフ",
        TRAINEE: "実習生",
      },
      workStatus: {
        NORMAL_DOMESTIC: "通常勤務（国内）",
        NORMAL_OVERSEAS: "通常勤務（海外）",
        LEAVE_PRIVATE_INJURY: "休業（私傷病）",
        LEAVE_CHILDCARE: "休業（育産休）",
        LEAVE_CAREGIVING: "休業（介護）",
        LEAVE_OTHER: "休業（その他）",
        WORK_RESTRICTED: "就業制限",
      },
      employmentStatus: {
        ACTIVE: "在籍",
        SECONDED: "出向",
        CONTRACT_ENDED: "契約満了",
        RETIRED: "退職",
      },
      position: {
        STAFF: "一般職",
        STAFF_J: "一般職（J）",
        STAFF_M: "一般職（M）",
        STAFF_H: "一般職（H）",
        MANAGER: "管理職",
        MANAGER_J: "管理職（J）",
        MANAGER_M: "管理職（M）",
        MANAGER_H: "管理職（H）",
        EXECUTIVE: "役員",
        OTHER: "その他",
      },
      jobType: {
        MANAGEMENT: "管理職",
        PROFESSIONAL_TECHNICAL: "専門・技術職",
        CLERICAL: "事務職",
        SALES: "販売・営業職",
        SERVICE: "サービス職",
        SECURITY: "保安職",
        AGRICULTURE_FORESTRY_FISHERY: "農林漁業職",
        PRODUCTION: "生産工程職",
        TRANSPORT_MACHINE: "輸送・機械運転職",
        CONSTRUCTION_MINING: "建設・採掘職",
        CARRYING_CLEANING_PACKAGING: "運搬・清掃・包装等",
        OTHER: "その他",
      },
      accountIssueStatus: {
        NOT_ISSUED: "未发行",
        ISSUED: "已发行",
        ACTIVATED: "已激活",
        SUSPENDED: "一时停用",
      },
      userStatus: { ACTIVE: "有效", INACTIVE: "无效", DELETED: "已删除" },
    },
  },
  organizationHealth: {
    tabs: {
      profile: "基本信息",
      offices: "事业所管理",
      departments: "部门管理",
    },
    profile: {
      title: "企业基本信息",
      description: "维护法人和主要联系方式。",
      logo: "企业 Logo",
      logoHint: "只读，来源于租户品牌设置。",
      employeeCount: "从业员人数",
    },
    offices: {
      title: "事业所",
      description: "维护各事业所的联系与劳动保险信息。",
      add: "追加事业所",
      edit: "编辑事业所",
      empty: "暂无事业所",
      dialogDescription: "录入事业所基本信息。",
      importTitle: "导入事业所",
      importDescription: "使用字段名模板批量追加事业所。",
      exportTitle: "导出事业所",
      exportDescription: "选择要导出的字段。",
    },
    departments: {
      title: "部门层级",
      description: "维护部门上下级关系和显示顺序。",
      add: "追加部门",
      edit: "编辑部门",
      empty: "暂无部门",
      root: "顶级部门",
      memberCountSuffix: "（{count}名）",
      dialogDescription: "设置部门名称、上级部门和排序。",
      importTitle: "导入部门",
      importDescription: "父部门必须先存在或排在父行之后。",
      exportTitle: "导出部门",
      exportDescription: "导出当前部门层级。",
    },
    fields: {
      companyName: "企业名称",
      representativeName: "代表者",
      corporateNumber: "法人番号",
      location: "住所",
      areaCode: "邮编",
      postalCode: "邮编",
      prefecture: "都道府县",
      city: "市区町村",
      area: "町域",
      address: "详细地址",
      addressLine: "地址",
      contactName: "担当者",
      contactDepartment: "担当者部门",
      contactPhone: "担当者电话",
      contactEmail: "担当者邮箱",
      websiteUrl: "官网 URL",
      industry: "行业",
      foundedOn: "设立日期",
      name: "事业所名称",
      laborInsuranceNumber: "劳动保险番号",
      departmentName: "部门名称",
      parentDepartment: "上级部门",
      sortOrder: "排序",
    },
    import: {
      dropHint: "拖放 CSV/XLSX 文件，或选择文件。",
      choose: "选择文件",
      preview: "数据预览",
      previewCount: "最多预览 20 行",
      progress: "导入进度",
      missing: "缺少必要字段",
      requiredFields: "必填项目",
      downloadTemplate: "下载导入模板",
      officeNameRequired: "用于识别事业所的名称，必须填写。",
      departmentNameRequired: "用于识别部门的名称，必须填写。",
    },
    export: { columns: "导出字段", selectAll: "全选", deselectAll: "全取消" },
    messages: {
      saved: "保存成功",
      deleted: "删除成功",
      deleteTitle: "确认删除",
    },
  },
  documentSummary: {
    title: "伝票集計",
    subtitle: "处理完成伝票的财务汇总",
    noWorkspace: "请先选择工作区。",
    noWorkspaceHint: "显示伝票集計需要有效的工作区。",
    readOnly: "没有查看伝票集計的权限。",
    readOnlyHint: "需要 document_intake.read 权限。",
    empty: "没有符合条件的处理完成伝票。",
    split: {
      progress: "分割 {index}/{count}",
    },
    total: "共 {total} 条（第 {page} 页，每页 {pageSize} 条）",
    errors: {
      loadFailed: "伝票集計加载失败。",
    },
    stats: {
      title: "年度汇总",
      dateRange: "汇总交易日",
      fiscalYear: "会计年度",
      fiscalYearOption: "{year}年度",
      apply: "更新汇总",
      syncList: "与列表日期同步",
      count: "件数",
      totalAmount: "含税合计金额",
      averageAmount: "平均单笔金额",
      byAccount: "勘定科目占比",
      byMonth: "按月金额",
      monthLabel: "{year}年{month}月",
      emptyChart: "暂无汇总数据",
      unknownAccount: "未设置科目",
    },
    filters: {
      keyword: "关键词",
      keywordPlaceholder: "取引先 / 摘要",
      documentDate: "交易日",
      account: "勘定科目",
      taxRate: "税率",
      invoicePresence: "インボイス",
      invoicePresent: "有",
      invoiceAbsent: "无",
      allInvoicePresence: "全部",
      amount: "金额",
      amountMin: "金额下限",
      amountMax: "金额上限",
      amountMinPlaceholder: "下限",
      amountMaxPlaceholder: "上限",
      allAccounts: "全部科目",
      allTaxRates: "全部税率",
      apply: "筛选",
      reset: "重置",
    },
    taxRate: {
      "10": "10%",
      "8": "8%（軽減）",
      "10_NO_INVOICE": "10%（无发票登记号）",
      "8_NO_INVOICE": "8%（軽減・无发票登记号）",
      NON_TAXABLE: "非課税",
      OUT_OF_SCOPE: "不課税",
      EXEMPT: "不明",
      UNKNOWN: "不明",
      multiple: "多种税率",
    },
    columns: {
      documentDate: "交易日",
      documentNumber: "伝票番号",
      counterparty: "取引先",
      description: "摘要",
      account: "勘定科目",
      accountCode: "勘定科目代码",
      accountName: "勘定科目名",
      taxRate: "税率",
      amount: "含税金额",
      amountMinor: "含税金额（minor）",
      paymentMethod: "支付方式",
      invoiceRegistrationNumber: "注册番号",
      cardLastFour: "卡号后4位",
      currency: "货币",
      filename: "Intake 文件名",
      actions: "操作",
      documentIntakeId: "伝票ID",
      documentSegmentId: "セグメントID",
    },
    actions: {
      detail: "详情",
    },
    pagination: {
      prev: "上一页",
      next: "下一页",
    },
    export: {
      button: "导出",
      title: "输出伝票集計",
      description:
        "按当前筛选结果导出 CSV 或 Excel（上限 5000 行）。列名使用逻辑名称，默认全选。",
      columns: "输出列",
      selectAll: "全选",
      deselectAll: "全不选",
      csv: "CSV",
      xlsx: "Excel",
      tooLarge: "导出行数超过上限",
    },
  },
  documentIntake: {
    inbox: {
      title: "书类受信箱",
      subtitle: "当前工作区的受理书类",
      noWorkspace: "请先选择工作区。",
      readOnly: "没有查看书类受信箱的权限。",
      emptyTitle: "暂无受理书类",
      emptyDescription: "上传收据或发票后将显示在此处。",
      total: "共 {total} 条（第 {page} 页，每页 {pageSize} 条）",
      bulkApprove: {
        action: "批量承认",
        running: "承认中...",
        select: "选择待确认书类",
        selectAll: "选择当前页全部待确认书类",
        failed: "{count} 张书类承认失败。",
      },
      foundation: {
        columns: {
          file: "文件",
          processing: "处理状态",
          accounting: "会计状态",
          documentType: "书类类型",
          counterparty: "交易方",
          date: "交易日",
          amount: "金额",
          taxRate: "税率",
          invoiceRegistrationNumber: "注册番号",
          account: "勘定科目",
          actions: "操作",
        },
        taxRate: {
          multiple: "多种税率",
        },
        split: {
          badge: "分割 {count}",
          expand: "展开分割明细",
          collapse: "收起分割明细",
        },
        file: {
          multipleDocuments: "多份书类",
        },
        processing: {
          UPLOADED: "已受理",
          QUEUED: "待处理",
          PROCESSING: "处理中",
          REPROCESSING: "再解析中",
          NEEDS_REVIEW: "待确认",
          COMPLETED: "已完成",
          FAILED: "处理错误",
          CANCELLED: "已取消",
          UNKNOWN: "状态不明",
        },
        processingStage: {
          PREPROCESSING: "图像处理中",
          OCR: "OCR 解析中",
          FIELD_EXTRACTION: "字段提取中",
          REVIEW: "待确认",
          COMPLETED: "处理完成",
          FAILED: "处理失败",
          CANCELLED: "处理取消",
          LEGACY_PARSING: "旧解析处理",
          UNKNOWN: "状态不明",
        },
        review: {
          NOT_READY: "未就绪",
          NEEDS_REVIEW: "未确认",
          PARTIALLY_REVIEWED: "部分已确认",
          COMPLETED: "已确认",
          REJECTED: "已驳回",
          NON_ACCOUNTING: "非会计对象",
          MIXED: "部分已确认",
          UNKNOWN: "待确认（未汇总）",
          progress: "{confirmed}/{total} 已确认",
        },
        accounting: {
          NOT_STARTED: "未开始",
          PROCESSING: "会计处理中",
          TRANSACTION_REVIEW_REQUIRED: "交易待确认",
          DOWNSTREAM_PROCESSING: "仕訳处理中",
          COMPLETED: "仕訳已确认",
          FAILED: "错误",
          AMENDMENT_REQUIRED: "需要修正",
          MIXED: "部分完成",
          UNKNOWN: "状态不明",
        },
        segments: {
          zero: "0件",
          count: "{count}件",
          updating: "分段更新中",
          updatingShort: "更新中",
        },
        amount: {
          multiple: "多个",
        },
        actions: {
          review: "确认",
          details: "详情",
          retry: "重试",
          reprocess: "再解析",
          cancelProcessing: "取消处理",
          cancelFailed: "取消处理失败。",
          retryFailed: "重试处理失败。",
        },
        rowAriaLabel: "打开 {filename}",
        tooltips: {
          reviewStaleMetadata: "正在更新汇总",
          reviewMixed: "已确认 {confirmed} · 已驳回 {rejected} · 合计 {total}",
          accountingMasked: "无会计信息查看权限",
          accountingSegmentPartial: "{total} 件中 {completed} 件完成",
          accountingSegmentReviewRequired: "{count} 件待确认",
          partialAmount: "部分金额无法获取",
          processingFailedGeneric: "处理失败",
        },
        foreignCurrency: {
          badge: "海外票据 · 需填写日元金额",
        },
        failureCodes: {
          PROCESSING_FAILED: "处理失败",
          OCR_FAILED: "OCR 失败",
          EXTRACTION_FAILED: "提取失败",
        },
      },
    },
    upload: {
      title: "上传书类",
      subtitle: "向工作区提交收据与发票",
      backToInbox: "返回受信箱",
      dropTitle: "点击或拖放文件到此处",
      dropHint: "PDF、JPEG、PNG、HEIC（最大 {maxMb} MB）",
      browse: "选择文件",
      progress: "上传中 {progress}%",
      ownership: "归属类型",
      limits: "最大文件大小：{maxMb} MB",
      allowedTypes: "支持格式：PDF、JPEG、PNG、HEIC",
      submit: "上传并解析",
      uploading: "上传中...",
      viewInbox: "查看受信箱",
      privacyNotice: "上传的书类将安全保存用于会计处理，访问受权限控制。",
      aiNotice: "AI 字段提取仅为建议，最终确认前请人工核对。",
      invalidType: "不支持的文件格式。",
      tooLarge: "文件超过 {maxMb} MB 上限。",
      sessionFailed: "创建上传会话失败。",
      failed: "上传失败。",
      noPermission: "没有上传书类的权限。",
      csvRoutedToBankImport: "CSV 文件请从银行明细导入页面上传。",
    },
    review: {
      title: "书类确认",
      backToInbox: "返回受信箱",
      originalPreview: "原件预览",
      originalRestricted: "没有查看原件的权限。",
      previewLoading: "正在加载预览...",
      parsedFields: "解析与建议字段",
      aiNotice: "AI 建议需人工确认后方可批准。",
      warnings: "警告",
      note: "确认备注",
      weakGrounding: {
        needsConfirm: "要确认",
      },
      accountingDraft: {
        create: "创建会计草稿",
        open: "打开会计草稿",
        creating: "正在创建草稿...",
        featureDisabled: "票据会计 Demo 未启用。",
        notApproved: "仅已批准的书类可创建会计草稿。",
      },
      reprocessQuery: {
        permissionDenied: "当前无法再解析。",
        segmentModeRequired: "请从分段确认画面执行再解析。",
      },
      foundationAccounting: {
        title: "Foundation 会计流程",
        subtitle: "Segment 确认后的业务交易、分类与分录草稿状态。",
        accountingStatus: {
          NOT_STARTED: "未开始",
          CLASSIFICATION_PENDING: "分类处理中",
          CLASSIFICATION_REVIEW_REQUIRED: "分类待确认",
          CLASSIFICATION_CONFIRMED: "分类已确认",
          CLASSIFICATION_TERMINAL: "分类已终止",
          JOURNAL_REVIEW_REQUIRED: "分录草稿待审核",
          JOURNAL_CONFIRMED: "分录草稿已确认",
          JOURNAL_TERMINAL: "分录草稿已终止",
        },
        bridge: {
          empty: "桥接未请求",
          status: {
            PENDING: "桥接排队中",
            PROCESSING: "桥接处理中",
            SUCCEEDED: "桥接成功",
            FAILED: "桥接失败",
            INELIGIBLE: "不符合桥接条件",
            CANCELLED: "桥接已取消",
            AMENDMENT_REQUIRED: "需修订后重试",
          },
        },
        transaction: {
          title: "业务交易",
          total: "合计 {amount} JPY",
          confirm: "确认交易",
          pending: "交易草稿创建中，请稍后刷新。",
          empty: "交易事实尚未生成。",
          splitList: "分割后的交易",
        },
        classification: {
          title: "费用分类",
          confirm: "确认分类",
          request: "重新请求分类",
          taxTreatment: "税务处理",
        },
        journal: {
          title: "分录草稿",
          date: "分录日期",
          debitTotal: "借方合计",
          creditTotal: "贷方合计",
          balance: "借贷",
          balanced: "平衡",
          unbalanced: "不平衡",
          reviewedAt: "确认时间",
          confirmedHint: "分录已确认",
          reviewRequiredHint: "需要确认分录",
          confirm: "确认分录",
          openDocumentDetail: "打开单据详情",
        },
        refreshing: "刷新中…",
        retry: "重试",
        failure: {
          retryStatus: "重新检查状态",
          BRIDGE_FAILED: {
            title: "会计桥接失败",
          },
          BRIDGE_INELIGIBLE: {
            title: "不符合会计桥接条件",
            message: "此票据不符合会计桥接条件。",
          },
          BRIDGE_CANCELLED: {
            title: "会计桥接已取消",
            message: "会计桥接已取消。",
          },
          BRIDGE_AMENDMENT_REQUIRED: {
            title: "需要修订",
            message: "需要修订票据或会计数据，请检查票据确认结果。",
          },
          TRANSACTION_FAILED: {
            title: "交易无法继续",
            message: "交易无法继续，请检查票据确认结果。",
          },
          CLASSIFICATION_FAILED: {
            title: "分类无法继续",
            message: "分类无法继续，请检查会计状态。",
          },
          JOURNAL_FAILED: {
            title: "分录草稿无法继续",
            message: "分录草稿无法继续，请检查会计状态。",
          },
          bridge: {
            generic: "会计桥接未能完成，请重新检查状态或复核票据确认结果。",
            codes: {
              BRIDGE_PAYLOAD_CONFLICT:
                "会计桥接数据冲突，请复核已确认的票据字段。",
              PROCESSING_FAILED: "会计桥接处理失败，请稍后重新检查状态。",
            },
          },
        },
        error: {
          loadFailed: "无法加载会计数据，请重试。",
          confirmTransactionFailed: "无法确认交易。",
          confirmClassificationFailed: "无法确认分类。",
          confirmJournalFailed: "无法确认分录草稿。",
          permissionDenied: "您没有执行此会计操作的权限。",
          versionConflict: "会计数据已更新，请刷新后重试。",
          amendmentRequired: "需要先修订此 Segment 才能继续会计处理。",
          workerDisabled: "当前环境未启用会计处理。",
          notFound: "未找到会计数据。",
          periodLocked: "会计期间已锁定。",
        },
        workflow: {
          v2: {
            navLabel: "会计工作流",
            awaitingSegmentConfirm: "确认票据后将开始会计处理",
            unknownStatus: "无法确认状态",
            stages: {
              BRIDGE: "会计桥接",
              TRANSACTION: "交易确认",
              CLASSIFICATION: "分类确认",
              JOURNAL: "分录确认",
            },
            status: {
              BRIDGE: {
                NOT_STARTED: "未开始",
                PROCESSING: "处理中",
                REVIEW_REQUIRED: "待确认",
                CONFIRMED: "已完成",
                FAILED: "失败",
              },
              TRANSACTION: {
                NOT_STARTED: "未开始",
                PROCESSING: "处理中",
                REVIEW_REQUIRED: "待确认",
                CONFIRMED: "已完成",
                FAILED: "失败",
              },
              CLASSIFICATION: {
                NOT_STARTED: "未开始",
                PROCESSING: "处理中",
                REVIEW_REQUIRED: "待确认",
                CONFIRMED: "已完成",
                FAILED: "失败",
              },
              JOURNAL: {
                NOT_STARTED: "未开始",
                PROCESSING: "处理中",
                REVIEW_REQUIRED: "待确认",
                CONFIRMED: "分录已确认",
                FAILED: "失败",
              },
            },
            description: {
              BRIDGE: {
                NOT_STARTED: "等待票据确认后开始。",
                PROCESSING: "正在处理会计桥接。",
                REVIEW_REQUIRED: "需要确认会计桥接。",
                CONFIRMED: "会计桥接已完成。",
                FAILED: "会计桥接未完成。",
              },
              TRANSACTION: {
                NOT_STARTED: "等待会计桥接完成。",
                PROCESSING: "正在创建业务交易。",
                REVIEW_REQUIRED: "请确认业务交易。",
                CONFIRMED: "业务交易已确认。",
                FAILED: "业务交易无法继续。",
              },
              CLASSIFICATION: {
                NOT_STARTED: "等待交易确认完成。",
                PROCESSING: "正在生成分类。",
                REVIEW_REQUIRED: "请确认分类。",
                CONFIRMED: "分类已确认。",
                FAILED: "分类无法继续。",
              },
              JOURNAL: {
                NOT_STARTED: "等待分类确认完成。",
                PROCESSING: "正在生成分录草稿。",
                REVIEW_REQUIRED: "请审核分录草稿。",
                CONFIRMED: "分录已确认",
                FAILED: "分录草稿无法继续。",
              },
            },
          },
          status: {
            generic: {
              NOT_STARTED: "未开始",
              PROCESSING: "处理中",
              REVIEW_REQUIRED: "待确认",
              COMPLETED: "已完成",
              FAILED: "失败",
              DISABLED: "不可用",
            },
            JOURNAL: {
              COMPLETED: "分录已确认",
            },
            POSTING: {
              DISABLED: "此页面不提供过账",
              NOT_STARTED: "未开始",
            },
          },
        },
      },
      workspace: {
        foundationBadge: "Foundation 确认",
        accountingRail: {
          expand: "展开会计流程",
          collapse: "收起会计流程",
        },
      },
      preview: {
        ocr: "OCR 图像",
        original: "原始文件",
        mineruDebug: "MINERU",
        geminiRequestDebug: "Gemini 请求",
        geminiResponseDebug: "Gemini 返回",
        debugEmpty: "暂无调试数据。",
        debugReconstructedHint:
          "该数据由已保存的 OCR/候选结果重建，可能与当时实际请求略有差异。重新处理后会保存完整请求与响应。",
        originalFullUploadHint:
          "元ファイル为上传的完整原图；OCR 图像为当前 Segment 的裁剪区域，两者内容范围可能不同。",
        zoomIn: "放大",
        zoomOut: "缩小",
        rotateLeft: "向左旋转",
        rotateRight: "向右旋转",
        fitWidth: "适应宽度",
        reset: "重置缩放",
        previousPage: "上一页",
        nextPage: "下一页",
        loadFailed: "无法加载预览。",
        permissionDenied: "没有查看原始文件的权限。",
        ocrLoadFailed: "无法加载 OCR 图像",
        ocrReload: "重新加载 OCR 图像",
        ocrUrlExpiredHint: "OCR 图像链接可能已过期",
        noFieldLocation: "无法使用位置信息",
        focusActive: "正在显示所选项目",
        invalidCoordinates: "坐标信息无效",
      },
      summary: {
        legacy: "旧格式票据",
        empty: "无有效 Segment",
        progress: "已确认 {confirmed}/{total}",
        needsReview: "待确认 {count} 件",
        excluded: "已排除 {count} 件",
        rejected: "已拒绝 {count} 件",
        duplicate: "重复 {count} 件",
        nonAccounting: "非会计 {count} 件",
        processing: "处理中 {count} 件",
        selectedOutcome: "当前选择：{outcome}",
      },
      legacyBanner: {
        badge: "旧格式",
        title: "旧格式票据",
        description: "此票据在旧版确认界面中显示。",
      },
      tabs: {
        document: "票据",
        fields: "输入内容",
        validation: "校验",
        accounting: "会计",
      },
      validation: {
        title: "校验结果",
        empty: "暂无校验项。",
        failures: "失败",
        warnings: "警告",
        passed: "通过 ({count})",
        unknown: "状态不明",
        fallback: "校验问题 ({ruleCode})",
        overridden: "已例外承认",
        locateField: "定位字段",
        locateDocument: "定位到票据",
        noLocation: "该字段暂无位置信息。",
        showPassed: "展开通过项",
        hidePassed: "折叠通过项",
        summary: {
          fail: "FAIL {count}",
          overridden: "例外 {count}",
          warning: "WARNING {count}",
          pass: "PASS {count}",
          unknown: "UNKNOWN {count}",
        },
        messages: {
          required_total_missing: "缺少合计金额。",
          required_date_missing: "缺少单据日期。",
          supplier_missing: "缺少交易对手。",
          subtotal_plus_tax_equals_total: "小计与税额之和不等于合计。",
          date_in_future: "单据日期在未来。",
          date_too_old: "单据日期超过 10 年。",
          service_end_before_start: "服务结束日晚于开始日。",
          registration_number_format_invalid: "登记号格式无效。",
          qualified_invoice_without_registration_number: "适格发票缺少登记号。",
          possible_full_card_number: "检测到可能的完整卡号。",
          sensitive_data_redacted: "敏感数据已脱敏。",
        },
      },
      confirmGuard: {
        unsavedChanges: "未保存的更改",
        saveBeforeConfirm: "确认前请先保存更改。",
        unresolvedFailures:
          "仍有未解决的校验失败。请修正或在有权限时例外承认。",
        unknownValidation: "部分校验结果无法识别。确认前请检查校验区域。",
        processing: "处理尚未完成。",
        alreadyConfirmed: "该片段已确认。",
        overrideSyncPending: "校验例外正在同步，请先完成保存后再确认。",
      },
      missingDate: {
        title: "未输入日期",
        message:
          "无日期确认可能导致会计日期或分录日期需要人工复核。是否继续无日期确认？",
        cancel: "取消",
        confirmWithoutDate: "无日期确认",
      },
      override: {
        title: "例外承认",
        message: "将此校验错误作为例外处理。请输入理由。",
        comment: "理由",
        submit: "保存例外",
        action: "例外承认",
        forbidden: "无校验例外权限。",
      },
      error: {
        versionConflict: "其他用户已更新内容。请重新加载最新数据。",
        candidateSuperseded: "提取结果已更新，正在加载最新内容。",
        alreadyConfirmed: "该片段已确认。",
        invalidDate: "需要单据日期或显式无日期确认。",
        locationUnavailable: "该字段暂无 OCR 坐标。",
      },
      actions: {
        more: "更多",
      },
    },
    reviewMode: {
      segmentLoadFailed: "无法加载票据信息，请稍后重试。",
      retry: "重试",
      unavailable: "当前无法确认此票据。",
      invalidSelection: "指定的票据当前不可用。",
      processing: {
        waitingForSegments: "处理完成后可进行确认。",
      },
    },
    segmentReview: {
      segmentsTitle: "Segment 列表",
      previewTitle: "图像与定位",
      processing: "正在处理识别结果…",
      candidateStale: "识别结果已更新，请重新加载后再确认。",
      reload: "重新加载",
      operations: {
        editSplit: "编辑分割",
        manualSplit: "手动拆分",
        recrop: "重新裁切",
        merge: "合并片段",
        reopen: "重新打开审核",
        editBoundary: "编辑边界",
        dialogDescription:
          "在整页图像上调整片段边界。提交后将重新分配 OCR 并抽取字段。",
        selectSegments: "选择要合并的片段",
        reasonCode: "原因代码",
        reasonComment: "原因说明",
        submit: "提交边界调整",
        overlapWarning: "部分矩形重叠过高，请调整后再提交。",
        phaseCreatingRevision: "正在创建修订…",
        phaseGeneratingCrop: "正在生成裁切图…",
        phaseReallocatingOcr: "正在重新分配 OCR…",
        phaseExtractingFields: "正在重新抽取字段…",
        phaseFailed: "片段操作失败，请重试。",
      },
      list: {
        pageSegment: "第 {page} 页 · Segment {index}",
        failCount: "FAIL {count}",
        warningCount: "WARN {count}",
        manualEdit: "人工边界调整",
      },
      outcome: {
        NEEDS_REVIEW: "待审核",
        CONFIRMED: "已确认",
        REJECTED: "已驳回",
        DUPLICATE: "重复",
        NON_ACCOUNTING: "非记账",
        MANUALLY_EXCLUDED: "已排除",
        PROCESSING: "处理中",
      },
      field: {
        documentNumber: "单据编号",
        memo: "摘要",
        subtotal: "小计（JPY）",
        tax: "税额（JPY）",
        taxRate: "税率",
        taxRateUnset: "未设置",
        taxRate10: "10%",
        taxRate8: "8%（軽減）",
        taxRateExempt: "不明",
        paymentMethod: "支付方式",
        currency: "币种",
        registrationNumber: "登记号",
        taxSummary: "税额摘要",
        suggestedAccount: "会计科目候选",
        accountHint: "勘定科目",
        cardLastFour: "卡号后四位",
        lineItemsEmpty: "未检测到明细行。",
        lineItemsTotal: "共 {count} 行明细",
        confidence: "置信度 {value}%",
        source: "来源: {value}",
        moreCandidates: "另有 {count} 项",
        locate: "定位",
        lowConfidence: "低置信字段：{fields}",
      },
      taxBreakdown: {
        title: "税率明细",
        amount: "金额",
        taxAmount: "税额",
        addRow: "添加行",
        removeRow: "删除行",
        empty: "暂无税率明细，可手动添加。",
        exceedsTotal: "税率明细合计（金额＋税额）不能超过票据合计。",
      },
      voucherSplit: {
        open: "传票分割",
        edit: "编辑分割",
        detailsTitle: "分割明细",
        dialogTitle: "传票分割",
        dialogDescription: "按行指定含税金额、税率和会计科目。各行合计必须等于传票合计。",
        amount: "含税金额",
        addRow: "添加行",
        removeRow: "删除行",
        save: "保存分割",
        clear: "撤销分割",
        totalHint: "传票合计 {total} / 分割合计 {sum}",
        sumMismatch: "分割后的含税金额合计与传票合计不一致。",
        incomplete: "请为每一行填写金额、税率和会计科目。",
        amendmentRequired: "会计处理已完成，不能再修改分割。",
        readonlyEmpty: "没有税率明细。",
      },
      paymentMethod: {
        CASH: "现金",
        CREDIT_CARD: "信用卡",
        DEBIT_CARD: "借记卡",
        BANK_TRANSFER: "转账",
        QR_PAYMENT: "扫码支付",
        ELECTRONIC_MONEY: "电子货币",
        POINT: "积分",
        UNKNOWN: "未知",
        OTHER: "其他",
      },
      sections: {
        basic: "基本信息",
        amountTax: "金额·税",
        paymentAccounting: "支付·勘定科目",
        lineItems: "明细（只读）",
      },
      foreignCurrency: {
        notice: "这是海外票据。请填写日元金额，优先使用信用卡或银行的日元结算额。",
        faceAmount: "票面：{amount}",
      },
      overlay: {
        noImage: "暂无可显示图像。",
      },
      actions: {
        save: "保存",
        saveTemporary: "暂存",
        saving: "保存中…",
        approve: "承认",
        approving: "承认中…",
        correct: "订正",
        correcting: "订正中…",
        confirm: "确认 Segment",
        confirming: "确认中…",
        reject: "驳回",
        exclude: "排除",
        reprocess: "重新处理",
      },
      reprocess: {
        title: "重新处理",
        description:
          "选择重处理范围。重新 OCR 可能产生外部费用；完整重处理会生成新 Segment。",
        descriptionFullOnly:
          "将从头重新处理本单据，现有抽取结果会被新结果替换。",
        reExtract: "仅重新抽取",
        reOcr: "重新 OCR",
        full: "完整重处理",
      },
      reject: {
        title: "驳回 Segment",
        description: "该 Segment 将标记为驳回且不参与记账。",
        commentPlaceholder: "可选备注",
      },
      exclude: {
        title: "排除 Segment",
        description: "手动排除，不计入记账结果。",
        commentPlaceholder: "可选备注",
      },
      errors: {
        multipleSegments: "存在多个 Segment，请分别审核。",
        amendmentRequired: "已桥接或已过账，需走修正流程。",
        reviewAlreadyConfirmed: "审核已确认，请先重新打开审核后再重处理。",
        segmentEditForbidden: "无片段边界编辑权限。",
        confirmedReopenRequired: "已确认片段需先重新打开审核。",
        segmentCrossPageMerge: "不能合并跨页片段。",
        bboxOverlap: "片段边界重叠过高。",
        operationNoChange: "边界未变化，无需提交。",
        operationRunning: "已有片段操作正在处理中。",
        reopenForbidden: "无重新打开审核权限。",
        generic: "操作失败，请稍后重试。",
      },
    },
    unassigned: {
      title: "未分配队列",
      subtitle: "为未分配归属的书类指定归属",
      noPermission: "没有分配书类归属的权限。",
      emptyTitle: "暂无未分配书类",
      emptyDescription: "所有书类均已分配归属。",
    },
    retention: {
      title: "保留状态",
      description: "书类保留与删除策略的当前状态。",
      status: "受理状态",
      fileStatus: "原件文件",
      uploadedAt: "上传时间",
      scanStatus: "安全扫描",
      lockedNotice: "此书类处于保留锁定或法律保全中，无法删除。",
    },
    filters: {
      status: "处理状态",
      documentDate: "交易日",
      keyword: "关键词",
      keywordPlaceholder: "取引先 / 摘要",
      account: "勘定科目",
      taxRate: "税率",
      invoicePresence: "インボイス",
      invoicePresent: "有",
      invoiceAbsent: "无",
      allInvoicePresence: "全部",
      amount: "金额",
      amountMinPlaceholder: "下限",
      amountMaxPlaceholder: "上限",
      allAccounts: "全部科目",
      allTaxRates: "全部税率",
      all: "全部",
      apply: "搜索",
      reset: "重置",
    },
    fields: {
      documentType: "书类类型",
      documentDate: "交易日期",
      counterparty: "交易对手",
      amount: "金额",
    },
    table: {
      file: "文件",
      status: "状态",
      source: "来源",
      uploader: "上传者",
      ownership: "归属",
      type: "类型",
      date: "交易日",
      amount: "金额",
      review: "确认",
      actions: "操作",
    },
    documentType: {
      RECEIPT: "收据",
      INVOICE: "发票",
      REQUEST_FOR_PAYMENT: "付款请求",
      OTHER: "其他",
      UNKNOWN: "未知",
    },
    status: {
      UPLOADED: "已上传",
      SECURITY_SCANNING: "扫描中",
      SECURITY_REJECTED: "扫描拒绝",
      STORED: "已存储",
      OCR_QUEUED: "等待 OCR",
      OCR_PROCESSING: "OCR 处理中",
      OCR_COMPLETED: "OCR 完成",
      AI_EXTRACTION_QUEUED: "等待候选生成",
      AI_EXTRACTING: "候选生成中",
      PROCESSING_FAILED: "处理失败",
      PROCESSING_CANCELLED: "已取消",
      QUEUED_FOR_PARSING: "等待解析",
      PARSING: "解析中",
      PARSED: "解析完成",
      NEEDS_REVIEW: "需要确认",
      APPROVED: "已批准",
      REJECTED: "已拒绝",
      DUPLICATE: "重复",
      PARSING_FAILED: "解析失败",
      ARCHIVED: "已归档",
      UNKNOWN: "未知",
    },
    reviewStatus: {
      PENDING: "待确认",
      IN_REVIEW: "确认中",
      APPROVED: "已批准",
      REJECTED: "已拒绝",
      DUPLICATE: "重复",
      REPARSE_REQUESTED: "已请求再解析",
    },
    ownershipStatus: {
      UNASSIGNED: "未分配",
      AUTO_ASSIGNED: "自动分配",
      ASSIGNED: "已分配",
    },
    retentionStatus: {
      ACTIVE: "有效",
      RETENTION_LOCKED: "保留锁定",
      DELETION_REQUESTED: "已申请删除",
      LEGAL_HOLD: "法律保全",
      PURGED: "已清除",
    },
    ownership: {
      personal: "个人",
      soleProprietor: "个体经营者",
      corporation: "法人",
      client: "顾问客户",
      unassigned: "未分配",
    },
    usage: {
      PERSONAL_USE: "个人利用",
      BUSINESS_USE: "事业利用",
      MIXED_USE: "家事按分",
      UNKNOWN: "未知",
    },
    suggestion: {
      title: "分类与科目候选",
      usage: "利用区分",
      accountCandidate: "勘定科目候选",
      accountCode: "科目代码",
      businessUseRatio: "事业利用比例（%）",
    },
    duplicate: {
      probable: "可能重复",
      viewCandidate: "查看疑似重复票据",
      previewTitle: "疑似重复票据",
      previewDescription: "可查看疑似重复的已有票据内容。",
    },
    duplicates: {
      button: "重复传票（{count}件）",
      title: "重复传票",
      subtitle: "同一文件，或交易对方、交易日、含税金额一致的票据。",
      candidate: "候补",
      duplicate: "重复",
      missingCandidate: "候补票据已删除",
      emptyTitle: "没有重复传票",
      emptyDescription: "同一文件，或交易对方、交易日、含税金额一致的票据会显示在这里。",
      edit: "编辑",
      counterparty: "交易对方",
      documentDate: "交易日",
      amount: "含税金额",
      loadFailed: "无法加载重复传票",
      kind: {
        exact: "同一文件",
        probable: "可能重复",
      },
    },
    source: {
      WEB_UPLOAD: "Web 上传",
      LINE_OFFICIAL_ACCOUNT: "LINE",
    },
    actions: {
      review: "确认",
      approve: "批准",
      correctAndApprove: "修正后批准",
      reparse: "再解析",
      reject: "拒绝",
    },
    delete: {
      action: "删除",
      deleting: "删除中...",
      confirmTitle: "删除此书类？",
      confirmDescription: "将删除「{name}」及其原件文件，此操作不可撤销。",
      confirmAction: "删除",
      failed: "删除失败。",
      linkedToAccounting: "此书类已完成入账，无法删除。",
      notAllowed: "当前状态下不允许删除此书类。",
      retentionLocked: "此书类处于保留锁定状态，无法删除。",
      legalHold: "此书类处于法定保全状态，无法删除。",
    },
    loading: "加载中...",
    reviewMeta: {
      source: "来源渠道",
      receivedAt: "接收时间",
      uploader: "上传人",
      documentStatus: "处理状态",
    },
    readonlyPreview: {
      title: "伝票预览",
      description: "查看照合中的伝票。",
      loadFailed: "无法加载伝票。",
    },
  },
  documentAccountingDemoPage: {
    title: "票据会计 Demo",
    subtitle: "已批准书类 → 会计草稿 → 人工确认过账 → 分录与报表",
    banner:
      "Demo — 批准书类不会自动过账。请先在复核页创建草稿，再于草稿页显式确认过账。",
    actions: {
      openInbox: "打开书类受信箱",
      openUpload: "上传书类",
      continueInInbox: "前往受信箱继续",
    },
    scenarios: {
      title: "推荐演示场景",
      hint: "运行 seed 脚本后可在受信箱找到对应书类。",
      travel: {
        title: "差旅收据",
        description: "标准已批准收据，可创建草稿并完成过账。",
      },
      office: {
        title: "办公用品",
        description: "办公用品类科目映射演示。",
      },
      mixed: {
        title: "混合分摊 (70/30)",
        description: "事业/私用分摊草稿行演示。",
      },
      closedYear: {
        title: "关账年度（禁止过账）",
        description: "交易日期落在关闭期间，校验/过账应被阻止。",
      },
    },
    seed: {
      title: "本地种子数据",
      description: "在已启动 MySQL/MinIO 与 backend build 的环境中执行：",
    },
  },
  documentAccountingDemo: {
    title: "票据会计草稿",
    subtitle: "已批准书类的会计草稿编辑与过账确认",
    banner: "Demo — 批准书类不会自动过账，需在此页显式确认。",
    sections: {
      source: "来源书类",
      editor: "草稿分录",
      status: "状态",
      actions: "操作",
    },
    fields: {
      transactionDate: "交易日期",
      description: "摘要",
      counterparty: "交易对手",
      totalAmount: "合计金额",
      businessAllocation: "事业使用比例",
      account: "科目",
      debit: "借方",
      credit: "贷方",
      amount: "金额",
    },
    status: {
      DRAFT: "草稿",
      READY_TO_POST: "可过账",
      POSTING: "过账中",
      POSTED: "已过账",
      VOIDED: "已作废",
      POSTING_FAILED: "过账失败",
    },
    actions: {
      save: "保存草稿",
      validate: "校验平衡",
      approve: "批准",
      confirmPosting: "过账",
      void: "作废草稿",
      openJournal: "查看分录",
      openLedger: "查看总账",
      openReport: "查看报表",
      backToReview: "返回复核",
    },
    confirmApproval: {
      title: "批准会计草稿",
      description: "将批准此会计草稿。批准后仍不会自动过账。",
      confirm: "批准",
      cancel: "取消",
    },
    confirmPosting: {
      title: "确认过账",
      description: "过账后分录不可原地修改。确认将此草稿正式过账？",
      confirm: "过账",
      cancel: "取消",
    },
    messages: {
      saved: "草稿已保存",
      validated: "草稿已通过校验",
      validationOk: "借贷平衡校验通过",
      validationFailed: "借贷平衡校验失败",
      approved: "草稿已批准",
      voided: "草稿已作废",
      postingConfirmed: "过账请求已提交",
      postingSucceeded: "过账已完成",
      postingFailed: "过账失败",
      loadFailed: "加载草稿失败",
      periodClosed: "会计期间已关闭，无法过账。",
    },
  },
  lineDocumentIntakeDemo: {
    title: "LINE 票据接入 Demo",
    subtitle: "模拟 LINE 消息投递（非真实 LINE 流量）",
    banner:
      "Demo / 模拟 LINE 事件 — 无真实 LINE 流量。后续真实 LINE adapter 将复用同一 normalized event 与 Document Intake 服务。",
    controls: {
      identity: "Demo LINE 用户",
      fixture: "附件样例",
      workspaceHint: "可选目标 Workspace",
      autoWorkspace: "按绑定自动解析",
      send: "发送模拟事件",
    },
    identities: {
      single: "单 Workspace 用户",
      multiple: "多 Workspace 用户",
      unbound: "未绑定用户",
    },
    fixtures: {
      receiptPng: "日元收据 (PNG)",
      receiptJpg: "日元收据 (JPEG)",
      invoicePdf: "日元发票 (PDF)",
      invalidTxt: "无效文件 (TXT)",
    },
    messages: {
      title: "Inbound 消息",
      empty: "尚无模拟消息。选择用户与附件后发送。",
      workspaceSelectionRequired: "需要选择 Workspace 后才能继续提交票据。",
    },
    notifications: {
      title: "最近通知（日志）",
    },
    actions: {
      openInbox: "打开 Document Inbox",
      openReview: "打开 Review",
      selectWorkspace: "确认 Workspace",
      retry: "重试",
    },
    status: {
      RECEIVED: "已接收",
      RESOLVING_OWNER: "解析归属",
      FETCHING_ATTACHMENT: "获取附件",
      SUBMITTING_DOCUMENT: "提交票据",
      ACCEPTED: "已接受",
      NEEDS_WORKSPACE_SELECTION: "待选择 Workspace",
      DUPLICATE: "重复",
      FAILED: "失败",
    },
  },
  errors: {
    unauthorized: "未授权，请重新登录",
    requestFailed: "请求失败，请稍后重试",
    routeLoadFailed: "页面加载失败",
    routeLoadFailedDescription:
      "当前页面资源可能已更新或网络短暂中断，请刷新后继续。",
    badRequest: "请求参数有误",
    forbidden: "没有权限执行此操作",
    notFound: "请求的资源不存在",
    unavailable: "服务暂时不可用，请稍后重试",
    quotaLimitExceeded: "当前套餐额度已达到上限，请升级套餐或联系管理员。",
    quotaUsage: "（已用 {used} / {limit}）",
    quotaReset: "，将在 {resetAt} 重置",
    quotaAction: "。请前往套餐账单升级或联系管理员。",
    subscriptionReadOnly:
      "试用或订阅已到期，当前仅可查看已有数据，请续费后继续处理文档。",
  },
} as const;
